import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ArrowDownToLine, ArrowRight, ChevronRight, Download, Ellipsis, File as FileSvg, FolderOpenIcon, FolderPlus, Link, Move, Pencil, Trash, X, Eye, Star, FolderIcon, Loader2, FileText, Merge, ArrowUpDown, ArrowUp, ArrowDown, Stamp, ArrowLeft, Edit, Grab, EllipsisVertical, Folder, GripVertical, RefreshCcw, CloudCheck, CloudBackup, ScanLine, SlidersHorizontal, ChevronDown, Info, Upload } from "lucide-react";
import simpleUploadService from '../services/simpleUploadService';
import archiveImportService, { ARCHIVE_IMPORT_ENABLED } from '../services/archiveImportService';
import { Checkbox, Dropdown, FolderUpload, MultiFile, SearchSelect, UploadModal, DocumentSearch } from "./FieldComponents";
import EmailPreviewContent from "./EmailPreviewContent";
import Modal from "./Modal";
import PdfConversionOptionsModal from "./PdfConversionOptionsModal";
import FileEditor from "./FileEditor";
import MoveFolderPicker from "./MoveFolderPicker";
import '../styles/DocumentView.css';
import '../styles/Documents.css';
import '../styles/DocumentSection.css';
import { buildFileViewerUrl } from './fileViewerSource';
import '../styles/BatesStamp.css';
import { SearchBar } from "./Nav";
import ShareModal from "./ShareModal";
import CaseShareLinksModal from "./CaseShareLinksModal";
import { DriveFileMove, DriveFileMoveRounded, FolderOutlined } from "@mui/icons-material";
import FileNotes from "../components/FileNotes";
import Alert from "./Alert";
import heic2any from 'heic2any';
import { useUploads } from './UploadManagerContext';
import { createPdfConversionProgressTracker } from '../utils/pdfConversionProgress';
import { ensureCaseDbCsrfToken, withCsrfHeaders } from '../utils/csrf';
import {
    decodeFileName,
    encodeFileName,
    cleanFileName,
    hasEncodedCharacters,
    getFolderLeafNameValidationError,
    normalizeFolderLeafName
} from '../utils/fileNameUtils';
import { beginDownloadNotification, markDownloadNotificationStarted, dismissDownloadNotification } from '../utils/downloadNotifications';
import { Ar } from "@mynaui/icons-react";
import { EntityHoverPreview } from './EntityHoverPreview';
import SplitArchiveChoiceModal from './SplitArchiveChoiceModal';
import {
    archiveImportDestinationIsEligible,
    constrainArchiveDetectionToDestination,
    detectSplitZipArchives
} from '../utils/splitArchiveFiles';
import {
    buildDocumentFolderChildPath,
    buildDocumentUploadFolderRenameRows,
    collectDroppedDocumentFiles,
    findDocumentFolderRenameConflict,
    filterDocumentUploadFiles,
    getActiveDocumentFolderRenames,
    getDocumentFolderRenameCompletionNotice,
    joinDocumentUploadPath,
    normalizeDocumentCaseFolderPath,
    resolveRenamedDocumentUploadFolderPath
} from '../utils/documentUploadFiles';
import { buildDocumentUploadRetrySelection } from '../utils/documentUploadRetry';
import {
    applyExhibitSequence,
    buildBatesClientOperationKey,
    buildBatesFolderOrder,
    buildBatesCompletionNotice,
    buildExhibitPageLabelPlan,
    buildExhibitFileResolution,
    exhibitStampStyleLabel,
    getBatesCaseRelativeFolderPath,
    getBatesFileKey,
    hasValidBatesSelectionSnapshot,
    isBatesSelectionChanged,
    isValidBatesTaskId,
    moveBatesOrderItem,
    normalizeBatesCompletionResult,
    normalizeBatesAnalyzeFiles,
    resolveExhibitStampStyle,
    validateBatesOrderedAnalyzeResult
} from './batesExhibitStamp';
import FolderInfoModal from './FolderInfoModal';
import { buildSelectedFolderInfoTargets } from './folderInfo';
import DocumentSelectionSummary from './DocumentSelectionSummary';
import {
    buildDocumentSelectionDisplayRows,
    buildSelectedDocumentItems,
    toggleVisibleDocumentSelection,
    uniqueDocumentSelectionKeys,
} from '../utils/documentSelection';
import {
    buildDocumentDragPayload,
    resolveDocumentSelectionGesture,
} from '../utils/documentDragSelection';
import {
    ARCHIVE_IMPORT_TERMINAL_EVENT,
    resolveArchivePasswordRetry,
} from '../utils/archivePasswordRetry';
import { getDocumentLiveItemId } from '../utils/documentLiveUpdates';
import {
    isDocumentCaseLogicalPathAllowed,
    normalizeDocumentSyncFolderPath,
    normalizeDocumentSyncMatchPath
} from '../utils/documentCaseBoundary';
import { DOCUMENT_SELECTION_MOVE_EVENT, removeMovedDocumentSelectionKeys } from '../utils/documentSelectionPreferences';
import {
    getDocumentUndoUnavailableMessage,
    normalizeDocumentMutationSourceKey,
    publishDocumentUndoReceipt,
    settleDocumentMutationRefreshes
} from '../utils/documentOperationUndo';
import { getDocumentItemSizeBytes } from '../utils/documentFolderSort';

// Silence debug logs in this module
const console = window?.console ? { ...window.console, log: () => {} } : { log: () => {} };

const DOCUMENT_SEARCH_TYPE_OPTIONS = [
    ['All', 'All fields'],
    ['Title', 'File name'],
    ['Folder', 'Folder'],
    ['Author', 'Author'],
    ['Doc Type', 'File type'],
    ['Last Modified', 'Modified date']
];

const DOCUMENT_SEARCH_TYPE_LABELS = Object.fromEntries(DOCUMENT_SEARCH_TYPE_OPTIONS);

const DOCUMENT_SEARCH_MATCH_OPTIONS = [
    ['all_words', 'All words'],
    ['exact', 'Exact phrase'],
    ['any_word', 'Any word']
];

const DOCUMENT_FILE_TYPE_OPTIONS = [
    ['', 'Any type'],
    ['pdf', 'PDF'],
    ['docx', 'Word'],
    ['xlsx', 'Excel'],
    ['csv', 'CSV'],
    ['msg', 'Email'],
    ['eml', 'EML'],
    ['jpg', 'Images'],
    ['png', 'PNG'],
    ['txt', 'Text']
];

const DEFAULT_DOCUMENT_SEARCH_FILTERS = {
    fileType: '',
    dateFrom: '',
    dateTo: '',
    folder: '',
    semantic: false
};

const BATES_FIXED_POSITION_OPTIONS = [
    ['bottom-right', 'Bottom Right'],
    ['bottom-left', 'Bottom Left'],
    ['bottom-center', 'Bottom Center'],
    ['top-right', 'Top Right'],
    ['top-left', 'Top Left'],
    ['top-center', 'Top Center']
];

const BATES_FOLDER_DRAG_MIME = 'application/x-casedb-bates-folder-index';

const displayDocumentPath = (path, caseName) => decodeFileName(String(path || ''))
    .replace(/\{\{Name\}\}/g, () => caseName || 'Case documents');

// Preview and upload must describe the same final names and renamed folders.
// Checking a selection never needs to read or copy the file bodies.
const buildDocumentUploadBatch = (fileItems, includeFileBodies = true) => {
    const folderList = fileItems.filter(item => !item.file);
    return fileItems.filter(item => item.file).map(item => {
        const ext = (item.newExtension || item.extension || '').trim().replace(/^\./, '');
        const finalFileName = encodeFileName(ext ? `${item.newName}.${ext}` : item.newName);
        const pathParts = String(item.relativePath || '').replace(/\\/g, '/').split('/').filter(Boolean);
        pathParts.pop();
        const relativeFolderPath = resolveRenamedDocumentUploadFolderPath(pathParts.join('/'), folderList);
        return {
            file: includeFileBodies ? new File([item.file], finalFileName, {
                type: item.file.type, lastModified: item.file.lastModified,
            }) : { name: finalFileName },
            fileName: finalFileName,
            relativePath: [relativeFolderPath, finalFileName].filter(Boolean).join('/'),
        };
    });
};

const getBatesFolderLeafName = (folderKey) => {
    const withoutTrailingSlash = String(folderKey || '').replace(/\/+$/, '');
    return withoutTrailingSlash.split('/').pop() || withoutTrailingSlash || 'Folder';
};

const getBatesFolderDisplayPath = (folderKey, caseId, caseDisplayName) => {
    const key = String(folderKey || '');
    const canonicalRelativePath = getBatesCaseRelativeFolderPath(key);
    const caseRoot = `cases/${caseId}/{{Name}}/`;
    const relativePath = canonicalRelativePath !== null
        ? canonicalRelativePath
        : (key.startsWith(caseRoot) ? key.slice(caseRoot.length) : key);
    const displayRoot = caseDisplayName || 'Case documents';
    return relativePath ? `${displayRoot}/${relativePath}` : displayRoot;
};

const uploadSelectionFileKey = (file) => [
    file?.name || '',
    Number(file?.size || 0),
    Number(file?.lastModified || 0),
    file?.webkitRelativePath || ''
].join(':');

const createFolderRenameReservationId = () => {
    if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') {
        return `folder-rename-${window.crypto.randomUUID()}`;
    }
    return `folder-rename-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const normalizeDocumentSearchText = (value) => String(value || '').toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();

const getDocumentSearchTokens = (query) => normalizeDocumentSearchText(query).split(' ').filter(Boolean);

const matchesDocumentSearch = (query, target, matchMode = 'all_words') => {
    const normalizedQuery = normalizeDocumentSearchText(query);
    const normalizedTarget = normalizeDocumentSearchText(target);
    if (!normalizedQuery) return true;
    if (!normalizedTarget) return false;

    if (matchMode === 'exact') {
        return normalizedTarget.includes(normalizedQuery);
    }

    const tokens = getDocumentSearchTokens(normalizedQuery);
    if (tokens.length === 0) return true;
    if (matchMode === 'any_word') {
        return tokens.some(token => normalizedTarget.includes(token));
    }

    return tokens.every(token => normalizedTarget.includes(token));
};

const getDocumentFileExtension = (item) => {
    const explicit = item?.extension || item?.file?.extension;
    if (explicit) return String(explicit).toLowerCase().replace(/^\./, '');
    const name = item?.name || item?.file?.name || item?.key || '';
    const match = String(name).match(/\.([^.\/]+)$/);
    return match ? match[1].toLowerCase() : '';
};

const getDocumentItemPath = (item) => String(item?.file_path || item?.key || item?.file?.key || item?.folder_path || '');

const getDocumentSelectionKey = (item) => String(
    item?.key || item?.file?.key || item?.file_path || item?.file?.file_path || item?.path || ''
);

const getDocumentDragPreviewName = (key, visibleItemsByKey) => {
    const visibleItem = visibleItemsByKey.get(key);
    const item = visibleItem?.file || visibleItem;
    const itemName = item?.name || item?.file_name;
    if (itemName) return decodeFileName(itemName);

    const normalizedKey = String(key || '').replace(/\/+$/, '');
    return decodeFileName(normalizedKey.split('/').pop() || normalizedKey || 'Document');
};

export const getDocumentDragPreviewLayout = ({ sourceKeys, draggedKey, orderedVisibleKeys }) => {
    const normalizedDraggedKey = String(draggedKey || '');
    const normalizedSourceKeys = Array.from(new Set(
        (Array.isArray(sourceKeys) ? sourceKeys : [])
            .map(key => String(key || ''))
            .filter(Boolean)
    ));
    const allKeys = normalizedDraggedKey && !normalizedSourceKeys.includes(normalizedDraggedKey)
        ? [normalizedDraggedKey, ...normalizedSourceKeys]
        : normalizedSourceKeys;
    if (allKeys.length === 0) {
        return { previewKeys: [], draggedPreviewIndex: -1, remainingCount: 0, totalCount: 0 };
    }

    const selectedKeySet = new Set(allKeys);
    const visibleSelection = Array.from(new Set(
        (Array.isArray(orderedVisibleKeys) ? orderedVisibleKeys : [])
            .map(key => String(key || ''))
            .filter(key => selectedKeySet.has(key))
    ));
    const orderedKeys = visibleSelection.includes(normalizedDraggedKey)
        ? visibleSelection
        : [normalizedDraggedKey || allKeys[0]];
    const draggedIndex = Math.max(0, orderedKeys.indexOf(normalizedDraggedKey));
    const visibleAbove = orderedKeys.slice(0, draggedIndex).slice(-3);
    const visibleBelow = orderedKeys.slice(draggedIndex + 1).slice(0, Math.max(0, 5 - visibleAbove.length));
    const previewKeys = [
        ...visibleAbove,
        orderedKeys[draggedIndex],
        ...visibleBelow,
    ].filter(Boolean);

    return {
        previewKeys,
        draggedPreviewIndex: visibleAbove.length,
        remainingCount: Math.max(0, allKeys.length - previewKeys.length),
        totalCount: allKeys.length,
    };
};

const createDocumentDragPreview = ({ sourceKeys, draggedKey, visibleItemsByKey }) => {
    if (typeof document === 'undefined' || !document.body) return null;

    const layout = getDocumentDragPreviewLayout({
        sourceKeys,
        draggedKey,
        orderedVisibleKeys: Array.from(visibleItemsByKey.keys()),
    });
    if (layout.previewKeys.length === 0) return null;

    const preview = document.createElement('div');
    preview.className = 'document-multi-drag-preview';
    preview.setAttribute('aria-hidden', 'true');
    preview.dataset.draggedPreviewIndex = String(layout.draggedPreviewIndex);
    preview.title = `Dragging ${layout.totalCount} ${layout.totalCount === 1 ? 'item' : 'items'}`;

    layout.previewKeys.forEach((key, index) => {
        const card = document.createElement('div');
        const isDragged = index === layout.draggedPreviewIndex;
        card.className = `document-multi-drag-card${isDragged ? ' dragged' : ''}`;
        if (isDragged) card.dataset.dragged = 'true';

        const name = document.createElement('span');
        name.className = 'document-multi-drag-name';
        name.textContent = getDocumentDragPreviewName(key, visibleItemsByKey);
        card.appendChild(name);
        preview.appendChild(card);
    });

    if (layout.remainingCount > 0) {
        const remainder = document.createElement('div');
        remainder.className = 'document-multi-drag-more';
        remainder.textContent = `+${layout.remainingCount} more`;
        preview.appendChild(remainder);
    }

    document.body.appendChild(preview);
    return preview;
};

export const getDocumentDragImageOffsets = (preview, event) => {
    const draggedCard = preview?.querySelector?.('[data-dragged="true"]');
    const draggedPreviewIndex = Number(preview?.dataset?.draggedPreviewIndex || 0);
    const previewRect = preview?.getBoundingClientRect?.();
    const draggedRect = draggedCard?.getBoundingClientRect?.();
    const sourceRect = event?.currentTarget?.getBoundingClientRect?.();
    const previewWidth = Number(previewRect?.width) > 0 ? Number(previewRect.width) : 300;
    const draggedHeight = Number(draggedRect?.height) > 0 ? Number(draggedRect.height) : 32;
    const draggedTop = Number(draggedRect?.height) > 0 && Number(previewRect?.height) > 0
        ? Number(draggedRect.top) - Number(previewRect.top)
        : draggedPreviewIndex * 35;
    const pointerIsInsideSource = Number(sourceRect?.width) > 0
        && Number(sourceRect?.height) > 0
        && Number.isFinite(event?.clientX)
        && Number.isFinite(event?.clientY)
        && event.clientX >= sourceRect.left
        && event.clientX <= sourceRect.right
        && event.clientY >= sourceRect.top
        && event.clientY <= sourceRect.bottom;
    const sourceXRatio = pointerIsInsideSource
        ? (event.clientX - sourceRect.left) / sourceRect.width
        : 0.08;
    const sourceYRatio = pointerIsInsideSource
        ? (event.clientY - sourceRect.top) / sourceRect.height
        : 0.5;

    return {
        x: Math.round(Math.max(8, Math.min(previewWidth - 8, previewWidth * sourceXRatio))),
        y: Math.round(draggedTop + Math.max(4, Math.min(draggedHeight - 4, draggedHeight * sourceYRatio))),
    };
};

const normalizeDocumentFolderFilter = (value) => String(value || '').trim().replace(/^{{Name}}\/?/, '').replace(/^\/+|\/+$/g, '').toLowerCase();

export const isDocumentSearchResultInCaseBoundary = (item, scopePrefix = null) => {
    if (!item || typeof item !== 'object') return false;
    let key = String(item.key || item.file_path || '');
    for (let index = 0; index < 2 && key.includes('%'); index += 1) {
        try {
            const decoded = decodeURIComponent(key.replace(/%(?![0-9a-f]{2})/gi, '%25'));
            if (decoded === key) break;
            key = decoded;
        } catch (error) {
            return false;
        }
    }
    if (key.includes('%') && (() => {
        try {
            return decodeURIComponent(key.replace(/%(?![0-9a-f]{2})/gi, '%25')) !== key;
        } catch (error) {
            return true;
        }
    })()) return false;
    if (/[\\\u0000-\u001f\u007f]/.test(key)) return false;
    const isFolder = item.type === 'folder';
    if (!isFolder && key.endsWith('/')) return false;
    const keyForMatch = isFolder && key.endsWith('/') ? key.slice(0, -1) : key;
    const match = keyForMatch.match(/^cases\/[1-9][0-9]*\/\{\{Name\}\}(?:\/(.*))?$/);
    if (!match) return false;
    const expectedPrefix = String(scopePrefix || '').trim();
    if (!/^cases\/[1-9][0-9]*\/\{\{Name\}\}\/$/.test(expectedPrefix)
        || !keyForMatch.startsWith(expectedPrefix)) {
        return false;
    }
    const relativePath = String(match[1] || '');
    const relativeSegments = relativePath === '' ? [] : relativePath.split('/');
    if (relativeSegments.some(segment => (
        segment === '' || segment === '.' || segment === '..' || segment === '{{Name}}'
    ))) return false;
    const firstSegment = String(relativeSegments[0] || '').trim().toLowerCase();
    const hasReservedAlias = relativeSegments.some(segment => (
        ['pre-case lead documents', 'lead documents']
            .includes(String(segment).trim().toLowerCase())
    ));
    return firstSegment !== 'leads' && !hasReservedAlias;
};

const CreateFolder = ({ folderName, setFolderName, error = '', onCreate=(() => {}), onCancel=(() => {}) }) => {
    return (
        <Modal
            title="Create Folder"
            instructions="Enter a name for the new folder."
            onClose={onCancel}
            footer={
                <div className='modal-footer-actions'>
                    <button onClick={onCreate} className="action">Create</button>
                    <button onClick={onCancel} className="action alt">Cancel</button>
                </div>
            }
        >
            <div className='modal-content-wrapper'>
                <div className='form-group mid'>
                    <label className="subtext">Folder Name</label>
                    <input
                        type="text"
                        value={folderName}
                        onChange={(e) => setFolderName(e.target.value)}
                        placeholder="Enter folder name"
                    />
                    {error && <div className="small-text error" role="alert">{error}</div>}
                </div>
            </div>
        </Modal>
    );
};

const FileConflictModal = ({ conflicts, onResolve, onCancel, onSkipExisting }) => {
    const [resolutions, setResolutions] = useState({});
    
    useEffect(() => {
        // Initialize all conflicts to 'overwrite' by default
        const initial = {};
        conflicts.forEach(conflict => {
            initial[conflict.fileName] = 'overwrite';
        });
        setResolutions(initial);
    }, [conflicts]);
    
    const handleResolutionChange = (fileName, resolution) => {
        setResolutions(prev => ({
            ...prev,
            [fileName]: resolution
        }));
    };
    
    const handleApply = () => {
        onResolve(resolutions);
    };
    
    return (
        <Modal
            title="File Conflict"
            instructions={`${conflicts.length} file${conflicts.length > 1 ? 's' : ''} already exist${conflicts.length === 1 ? 's' : ''} in the destination folder. Choose how to handle each conflict.`}
            onClose={onCancel}
            zIndex={1005}
            footer={
                <div className='modal-footer-actions'>
                    <button onClick={handleApply} className="action">Continue</button>
                    {onSkipExisting && <button type="button" onClick={onSkipExisting} className="action quaternary">Skip existing files</button>}
                    <button onClick={onCancel} className="action alt">Cancel</button>
                </div>
            }
        >
            <div className='modal-content-wrapper' style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {conflicts.map((conflict, index) => (
                    <div key={index} style={{ marginBottom: '20px', padding: '15px', border: '1px solid #e0e0e0', borderRadius: '6px', width: '100%' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '10px', wordBreak: 'break-word' }}>
                            {conflict.fileName}
                        </div>
                        <div className='subtext' style={{ marginBottom: '10px' }}>
                            ./{displayDocumentPath(conflict.folderPath, conflict.caseName)
                                .replace(/^cases\/[^\/]+\//, '')}/
                        </div>
                        <div className='multi-checkbox vertical-group'>
                            <Checkbox
                                value={resolutions[conflict.fileName] === 'overwrite'}
                                onChange={() => handleResolutionChange(conflict.fileName, 'overwrite')}
                                label="Overwrite existing file"
                            />
                            <Checkbox
                                value={resolutions[conflict.fileName] === 'keep-both'}
                                onChange={() => handleResolutionChange(conflict.fileName, 'keep-both')}
                                label="Keep both (rename new file)"
                            />
                        </div>
                    </div>
                ))}
            </div>
        </Modal>
    );
};

// Helper function to check if a file is an audio file
const isAudioFile = (fileName) => {
    if (!fileName) return false;
    const audioExtensions = ['.mp3', '.mp4', '.wav', '.m4a', '.flac', '.ogg', '.amr', '.webm'];
    const lowerFileName = fileName.toLowerCase();
    return audioExtensions.some(ext => lowerFileName.endsWith(ext));
};

const canConvertToPdfFile = (fileName) => {
    if (!fileName) return false;
    const convertibleExtensions = [
        '.doc', '.docx', '.docm', '.dot', '.dotx', '.odt', '.ott', '.rtf',
        '.xls', '.xlsx', '.xlsm', '.xlsb', '.ods', '.ots', '.csv',
        '.ppt', '.pptx', '.pptm', '.pps', '.ppsx', '.odp', '.otp',
        '.eml', '.msg',
        '.heic', '.heif', '.tif', '.tiff', '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp',
        '.txt', '.text', '.md', '.markdown', '.log', '.json', '.xml', '.html', '.htm'
    ];
    const lowerFileName = fileName.toLowerCase();
    return convertibleExtensions.some(ext => lowerFileName.endsWith(ext));
};

const canRedactPdfFile = (fileName) => {
    if (!fileName) return false;
    return fileName.toLowerCase().endsWith('.pdf');
};

const FilePreviewPanel = ({
    previewState,
    file,
    onDownload,
    onFullView,
    onEmailPdfPreview,
    onOpenFolder,
    onFolderInfo,
    onZipFolder,
    onEditFile,
    onConvertToPdf,
    formatSize,
    formatLastModified,
    folderName,
    caseName,
    showFolderSyncOption,
    isFolderSyncPoint,
    onToggleFolderSync,
    syncBusy
}) => {
    if (!previewState || !file) return null;

    const renderActions = ({ showFullView = true, showDownload = true, showEdit = false, showConvert = false, forceOpen = false } = {}) => {
        const actionCount = [showFullView, showEdit, showConvert, showDownload].filter(Boolean).length;
        const gapPx = 5;
        const buttonWidth = actionCount > 0
            ? `calc((100% - ${(actionCount - 1) * gapPx}px) / ${actionCount})`
            : '100%';

        return (
            <div className="file-info-actions">
                {showFullView && (
                    <button className="action alt" style={{ width: buttonWidth }} onClick={() => onFullView(file)} title={forceOpen ? 'Force Open' : 'Full View'}>
                        <Eye size={16} />
                    </button>
                )}
                {showEdit && (
                    <button className="action alt" style={{ width: buttonWidth }} onClick={() => onEditFile(file)} title="Edit File">
                        <Edit size={16} />
                    </button>
                )}
                {showConvert && (
                    <button className="action alt" style={{ width: buttonWidth }} onClick={() => onConvertToPdf(file)} title="Convert to PDF">
                        <FileText size={16} />
                    </button>
                )}
                {showDownload && (
                    <button className="action" style={{ width: buttonWidth }} onClick={() => onDownload(file)} title="Download">
                        <Download size={16} />
                    </button>
                )}
            </div>
        );
    };

    switch (previewState.kind) {
        case 'large':
            return (
                <div className="large-file-gate">
                    <p style={{ marginTop: '8px' }}>
                        This file is large ({formatSize(parseInt(file.size, 10))}). Inline preview disabled to avoid browser instability.
                    </p>
                    <div className="file-info">
                        {renderActions({ showFullView: true, showDownload: true, showConvert: canConvertToPdfFile(file?.name), forceOpen: true })}
                    </div>
                </div>
            );
        case 'folder': {
            const folderActionWidth = showFolderSyncOption
                ? 'calc(25% - 3.75px)'
                : 'calc(33.333% - 3.3px)';
            return (
                <div className="folder-preview">
                    <div className="folder-info">
                        <p><strong>Type:</strong> Folder</p>
                        <p><strong>Items:</strong> {file.size || 0}</p>
                        {file.last_modified && (
                            <p><strong>Modified:</strong> {`${file.last_modified.length > 1 ? `${formatLastModified(file.last_modified)[0]} ${formatLastModified(file.last_modified)[2]}` : '-'}`}</p>
                        )}
                        <p><strong>Path:</strong> {displayDocumentPath(folderName, caseName)}/{file.name}</p>
                        <div className="file-info-actions">
                            <button className="action-micro" style={{ backgroundColor: 'var(--text-color)', color: 'var(--secondary-color)', width: folderActionWidth }} onClick={() => onOpenFolder(file)} title="Open Folder">
                                <FolderIcon size={16} />
                            </button>
                            <button className="action-micro secondary" style={{ width: folderActionWidth }} onClick={() => onFolderInfo(file)} title="Folder Information">
                                <Info size={16} />
                            </button>
                            {showFolderSyncOption && (
                                <button
                                    className="action-micro secondary"
                                    style={{ backgroundColor: isFolderSyncPoint ? 'var(--success-color, #2e7d32)' : '', width: folderActionWidth }}
                                    onClick={() => onToggleFolderSync(file)}
                                    title={isFolderSyncPoint ? 'Remove sync point' : 'Mark as sync point'}
                                    disabled={syncBusy}
                                >
                                    <RefreshCcw size={16} />
                                </button>
                            )}
                            <button className="action-micro" style={{ backgroundColor: 'var(--fill)', color: 'var(--secondary-color)', width: folderActionWidth }} onClick={() => onZipFolder(file)} title="Download ZIP">
                                <Download size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            );
        }
        case 'loading':
            return (
                <div className="loading-preview">
                    <Loader2 className="animate-spin" />
                    <span>Loading preview...</span>
                </div>
            );
        case 'pdf':
            return (
                <div className="pdf-preview-container">
                    {previewState.sourceType === 'base64' ? (
                        <object
                            data={`data:application/pdf;base64,${previewState.data}`}
                            type="application/pdf"
                            width="100%"
                            height="300px"
                            className="pdf-preview"
                            style={{ border: "1px solid var(--border-color)", borderRadius: "3px" }}
                        />
                    ) : (
                        <iframe
                            src={`${previewState.url}#toolbar=0&navpanes=0&scrollbar=0`}
                            width="100%"
                            height="300px"
                            className="pdf-preview"
                            style={{ border: "1px solid var(--border-color)", borderRadius: '6px' }}
                        />
                    )}
                    <div className="file-info small-text">
                        <p><strong>Size:</strong> {formatSize(file.size)}</p>
                        {previewState.pageCount && <p><strong>Page Count:</strong> {previewState.pageCount}</p>}
                        {renderActions()}
                    </div>
                </div>
            );
        case 'email':
            if (previewState.preview) {
                return (
                    <div className="email-preview-container">
                        <EmailPreviewContent
                            preview={previewState.preview}
                            fallbackTitle={file.name}
                            containerStyle={{ maxHeight: '300px', overflowY: 'auto' }}
                        />
                        <div className="file-info">
                            {renderActions({ showConvert: canConvertToPdfFile(file?.name) })}
                            <button
                                className="action alt"
                                style={{ width: '100%' }}
                                onClick={() => onEmailPdfPreview(file)}
                                title="Render PDF preview in panel"
                            >
                                Load Full Preview
                            </button>
                        </div>
                    </div>
                );
            }

            return (
                <div className="email-preview-container">
                    <div className="email-preview" style={{
                        padding: "15px",
                        whiteSpace: "pre-wrap",
                        fontSize: "12px",
                        lineHeight: "1.4",
                        backgroundColor: "var(--secondary-color)",
                        border: "1px solid var(--border-color)",
                        borderRadius: '6px',
                        maxHeight: "300px",
                        overflowY: "auto"
                    }}>
                        {previewState.text}
                    </div>
                    {previewState.truncated && (
                        <p className="truncation-notice">Email content truncated. Full content available on full view.</p>
                    )}
                    <div className="file-info">
                        {renderActions({ showConvert: canConvertToPdfFile(file?.name) })}
                        <button
                            className="action alt"
                            style={{ width: '100%', marginTop: '8px' }}
                            onClick={() => onEmailPdfPreview(file)}
                            title="Render PDF preview in panel"
                        >
                            Load Full Preview
                        </button>
                    </div>
                </div>
            );
        case 'image':
            return (
                <div className="image-preview-container">
                    {previewState.conversionError ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <FileSvg size={48} style={{ color: 'var(--priority-color)' }} />
                            <p style={{ color: 'var(--priority-color)', marginTop: '10px' }}>
                                ⚠ Failed to convert HEIC image
                            </p>
                            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '5px' }}>
                                {previewState.conversionError}
                            </p>
                        </div>
                    ) : (
                        <img
                            src={previewState.url}
                            alt={file.name}
                            style={{ width: "100%", height: "auto", maxHeight: "300px", objectFit: "contain" }}
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                    )}
                    <div className="image-fallback" style={{ display: 'none' }}>
                        <FileSvg size={48} />
                        <span className="file-type">IMAGE</span>
                    </div>
                    {previewState.heicConverted && (
                        <div style={{ textAlign: 'center', fontSize: '11px', color: 'var(--text-muted)', marginTop: '5px' }}>
                            Converted from HEIC format
                        </div>
                    )}
                    <div className="file-info">
                        {renderActions({ showFullView: true, showDownload: true, showConvert: canConvertToPdfFile(file?.name) })}
                    </div>
                </div>
            );
        case 'audio':
            return (
                <div className="audio-preview-container">
                    <audio controls src={previewState.url} style={{ width: "100%", marginBottom: "15px" }} preload="metadata" />
                    <div className="file-info small-text">
                        <p><strong>Size:</strong> {formatSize(file.size)}</p>
                        {file.last_modified && <p><strong>Modified:</strong> {formatLastModified(file?.last_modified)[0]} {formatLastModified(file?.last_modified)[2]}</p>}
                        {renderActions()}
                    </div>
                </div>
            );
        case 'video':
            return (
                <div className="video-preview-container">
                    <video controls src={previewState.url} style={{ width: "100%", maxHeight: "250px" }} preload="metadata" />
                    <div className="file-info small-text">
                        <p><strong>Size:</strong> {formatSize(file.size)}</p>
                        {file.last_modified && <p><strong>Modified:</strong> {formatLastModified(file?.last_modified)[0]} {formatLastModified(file?.last_modified)[2]}</p>}
                        {renderActions()}
                    </div>
                </div>
            );
        case 'calendar':
            return (
                <div className="calendar-preview-container">
                    <pre className="text-preview">{previewState.text}</pre>
                    {previewState.truncated && (
                        <p className="truncation-notice">Calendar content truncated. Full content available on full view.</p>
                    )}
                    <div className="file-info small-text">
                        <p><strong>Size:</strong> {formatSize(file.size)}</p>
                        {file.last_modified && <p><strong>Modified:</strong> {formatLastModified(file?.last_modified)[0]} {formatLastModified(file?.last_modified)[2]}</p>}
                        {renderActions()}
                    </div>
                </div>
            );
        case 'text':
            return (
                <div className="text-preview-container">
                    <pre className="text-preview">{previewState.text}</pre>
                    {previewState.truncated && (
                        <p className="truncation-notice">Preview truncated. Full content available on download.</p>
                    )}
                    <div className="file-info small-text">
                        <p><strong>Size:</strong> {formatSize(file.size)}</p>
                        {file.last_modified && <p><strong>Modified:</strong> {formatLastModified(file?.last_modified)[0]} {formatLastModified(file?.last_modified)[2]}</p>}
                        {renderActions()}
                    </div>
                </div>
            );
        case 'office':
            const officePreviewUrl = previewState.officePreviewUrl || previewState.office_preview_url || previewState.url;
            return (
                <div className="office-preview-container">
                    <iframe
                        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(officePreviewUrl)}&wdAspect=0&wdEmbedCode=0`}
                        style={{ width: "100%", height: "250px", border: "1px solid var(--border-color)", borderRadius: '6px' }}
                        onError={(e) => {
                            const container = e.target.parentElement;
                            container.innerHTML = `
                                <div style="text-align: center; padding: 50px; border: 1px solid var(--border-color); border-radius: 4px; margin-bottom: 15px;">
                                    <p style="margin-bottom: 15px;">📄 Preview not available for this document</p>
                                    <p style="color: var(--text-muted); margin-bottom: 20px; font-size: 14px;">This ${previewState.fileType.toUpperCase()} file cannot be previewed online.</p>
                                    <button onclick="window.open('${previewState.url}', '_blank')" class="action no-size" style="background: var(--primary-color); color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">
                                        Download to View
                                    </button>
                                </div>
                            `;
                        }}
                    />
                    <div className="file-info small-text">
                        <p><strong>Size:</strong> {formatSize(file.size)}</p>
                        {file.last_modified && <p><strong>Modified:</strong> {formatLastModified(file?.last_modified)[0]} {formatLastModified(file?.last_modified)[2]}</p>}
                        {renderActions({ showEdit: true, showConvert: canConvertToPdfFile(file?.name) })}
                    </div>
                </div>
            );
        case 'generic':
            return (
                <div className="generic-preview-container">
                    <div className="file-info">
                        <p><strong>Type:</strong> {previewState.fileType.toUpperCase()}</p>
                        <p><strong>Size:</strong> {formatSize(file.size)}</p>
                        {file.last_modified && <p><strong>Modified:</strong> {formatLastModified(file?.last_modified)[0]} {formatLastModified(file?.last_modified)[2]}</p>}
                        {renderActions({ showFullView: false, showDownload: true, showConvert: canConvertToPdfFile(file?.name) })}
                    </div>
                </div>
            );
        case 'error':
            return (
                <div className="error-preview-container">
                    <div className="preview-icon" aria-hidden="true">
                        <FileSvg size={24} />
                    </div>
                    <div className="error-message">
                        <p>Unable to preview this file</p>
                        <p className="error-details">{previewState.message}</p>
                    </div>
                    <div className="file-info">
                        {renderActions({ showFullView: false, showDownload: true, showConvert: canConvertToPdfFile(file?.name) })}
                    </div>
                </div>
            );
        default:
            return null;
    }
};

export const DocumentSection = ({ fetchDocuments, fetchFolderContents, case_id, user_id, folders, folderName, files, items, metaData, subfolders, caseName, caseTypeId, syncInfo, onFolderClick, onBreadcrumbClick, setDocNav, docNav, refreshCurrentFolder, refreshFolderTree, onPageChange, pagination, pageSize = 100, setPageSize, sortColumn: externalSortColumn = null, sortDirection: externalSortDirection = null, onSortChange, liveNewItemIds = new Set(), onLiveItemSeen = () => {}, onFileUpload, initialFileToOpen, isLoading = false }) => {
    const navigate = useNavigate();
    const { dispatch, folderRenames = {} } = useUploads();  // Get dispatch from upload context
    const hasSubfolders = Object.keys(subfolders).length > 0;
    const [searchQuery, setSearchQuery] = useState("");
    const [searchType, setSearchType] = useState("");
    const [multiFiles, setMultiFiles] = useState(null);
    const [folderFiles, setFolderFiles] = useState(null);
    const [uploadWaiting, setUploadWaiting] = useState(false);
    
    // Debug the props
    console.log('DocumentSection props:', {
        folderName,
        files: files?.length || 0,
        subfolders: Object.keys(subfolders).length,
        subfoldersKeys: Object.keys(subfolders),
        subfoldersValues: Object.values(subfolders),
        folders: folders ? Object.keys(folders).length : 0
    });
    const [activeFile, setActiveFile] = useState([]);
    const [more, setMore] = useState(null);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [filesToRename, setFilesToRename] = useState([]);
    const [skipExistingUploads, setSkipExistingUploads] = useState(false);
    const canCheckExistingUploads = Boolean(onFileUpload);
    const [uploadDuplicateCheck, setUploadDuplicateCheck] = useState(null);
    const [uploadDuplicateCheckRetry, setUploadDuplicateCheckRetry] = useState(0);
    const [uploadDestinationPath, setUploadDestinationPath] = useState(folderName || '{{Name}}');
    const [uploadSelectionCaseId, setUploadSelectionCaseId] = useState(case_id);
    const [unresolvedUploadGroups, setUnresolvedUploadGroups] = useState([]);
    const unresolvedUploadIdRef = useRef(0);
    const uploadSelectionGenerationRef = useRef(0);
    const activeUploadSelectionRef = useRef(null);
    const currentUploadCaseRef = useRef(String(case_id));
    currentUploadCaseRef.current = String(case_id);
    const isCurrentUploadSelection = selection => Boolean(selection
        && selection.generation === uploadSelectionGenerationRef.current
        && String(selection.caseId) === currentUploadCaseRef.current);
    const duplicateCheckIsCurrent = Boolean(uploadDuplicateCheck
        && uploadDuplicateCheck.items === filesToRename
        && uploadDuplicateCheck.destination === uploadDestinationPath
        && uploadDuplicateCheck.caseId === String(case_id)
        && uploadDuplicateCheck.retry === uploadDuplicateCheckRetry);
    const duplicateCheckReady = duplicateCheckIsCurrent && uploadDuplicateCheck.status === 'ready';
    const hiddenUploadItems = useMemo(() => {
        if (!skipExistingUploads || !duplicateCheckReady) return new Set();
        return new Set(filesToRename.filter(item => item.file).filter((item, index) => (
            uploadDuplicateCheck.existing.has(String(index))
        )));
    }, [skipExistingUploads, duplicateCheckReady, filesToRename, uploadDuplicateCheck]);

    useEffect(() => {
        if (!canCheckExistingUploads || !skipExistingUploads || !showRenameModal
            || String(uploadSelectionCaseId) !== String(case_id)) return undefined;
        const controller = new AbortController();
        const snapshot = {
            items: filesToRename, destination: uploadDestinationPath, caseId: String(case_id),
            retry: uploadDuplicateCheckRetry,
        };
        const total = filesToRename.filter(item => item.file).length;
        setUploadDuplicateCheck({ ...snapshot, status: 'checking', completed: 0, total });
        // Debounce edits; cancellation also stops the remaining bounded chunks.
        const timer = window.setTimeout(async () => {
            try {
                const invalidFolder = filesToRename.find(item => !item.file && getFolderLeafNameValidationError(item.newName));
                if (invalidFolder) throw new Error('Correct the folder name before checking for existing files.');
                const existing = await simpleUploadService.checkExistingFiles(
                    buildDocumentUploadBatch(filesToRename, false), {
                        caseId: case_id, folderName: uploadDestinationPath, signal: controller.signal,
                        onCheckProgress: ({ completed }) => {
                            if (!controller.signal.aborted) setUploadDuplicateCheck({ ...snapshot, status: 'checking', completed, total });
                        },
                    },
                );
                if (!controller.signal.aborted) setUploadDuplicateCheck({ ...snapshot, status: 'ready', existing, total });
            } catch (error) {
                if (!controller.signal.aborted) setUploadDuplicateCheck({
                    ...snapshot, status: 'error', total,
                    error: 'Unable to check existing files. Nothing has been removed or uploaded. Retry the check, or turn off Skip existing files.',
                });
            }
        }, 250);
        return () => { window.clearTimeout(timer); controller.abort(); };
    }, [case_id, filesToRename, canCheckExistingUploads, showRenameModal, skipExistingUploads,
        uploadDestinationPath, uploadSelectionCaseId, uploadDuplicateCheckRetry]);
    const [splitArchiveSelection, setSplitArchiveSelection] = useState(null);
    const [splitArchiveStarting, setSplitArchiveStarting] = useState(false);
    const [splitArchiveError, setSplitArchiveError] = useState('');
    const splitArchivePromptRef = useRef(false);
    const splitArchiveStartRef = useRef(false);
    const archivePollActiveRef = useRef(true);
    const documentDragPreviewRef = useRef(null);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const selectedItemCacheRef = useRef(new Map());
    const selectionAnchorKeyRef = useRef(null);
    const [folderInfoTargets, setFolderInfoTargets] = useState(null);
    const [createFolder, setCreateFolder] = useState(false);
    const [message, setMessage] = useState(null);
    const [newFolderName, setNewFolderName] = useState("");
    const [createFolderError, setCreateFolderError] = useState('');
    const [userMap, setUserMap] = useState({});
    const [renameTarget, setRenameTarget] = useState(null);
    const [newNameInput, setNewNameInput] = useState("");
    const [newExtensionInput, setNewExtensionInput] = useState("");
    const [renameSubmitting, setRenameSubmitting] = useState(false);
    const [renameStatus, setRenameStatus] = useState(null);
    const renameDialogTokenRef = useRef(null);
    const [newFileTag, setNewFileTag] = useState({});
    const [view] = useState(0);
    const [deleteModal, setDeleteModal] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const [deletedPrefixes, setDeletedPrefixes] = useState([]); // Local cache of deleted folder prefixes to hide immediately
    const [deleteInProgress, setDeleteInProgress] = useState(false); // Prevent navigation while delete is running
    const [overallProgress, setOverallProgress] = useState(0);
    const [shareModal, setShareModal] = useState(false);
    const [showCaseShareLinks, setShowCaseShareLinks] = useState(false);
    const [shareTarget, setShareTarget] = useState(null);
    const [shareLink, setShareLink] = useState("");
    const shareScopeRef = useRef({ caseId: String(case_id), userId: String(user_id) });
    if (shareScopeRef.current.caseId !== String(case_id)
        || shareScopeRef.current.userId !== String(user_id)) {
        // Replace the scope object so even an away-and-back navigation cannot
        // display an earlier case/user's pending share response.
        shareScopeRef.current = { caseId: String(case_id), userId: String(user_id) };
    }
    const [zipTaskId, setZipTaskId] = useState(null);
    const [zipProgress, setZipProgress] = useState(0);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [moveTargetFolder, setMoveTargetFolder] = useState('');
    const [showBatesFolderPicker, setShowBatesFolderPicker] = useState(false);
    const [showMergeFolderPicker, setShowMergeFolderPicker] = useState(false);
    const [moveProcessing, setMoveProcessing] = useState(false); // disable move/copy button while request in-flight
    const [copyFiles, setCopyFiles] = useState(false);
    const [availableFolders, setAvailableFolders] = useState([]);
    const [showMoveFolderPicker, setShowMoveFolderPicker] = useState(false);
    const [movePickerInitialPath, setMovePickerInitialPath] = useState('');
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    
    // File conflict detection states
    const [showConflictModal, setShowConflictModal] = useState(false);
    const [conflictFiles, setConflictFiles] = useState([]);
    const [conflictResolutions, setConflictResolutions] = useState({});
    const [pendingOperation, setPendingOperation] = useState(null); // Stores the operation to execute after conflict resolution
    
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [downloadType, setDownloadType] = useState('zip'); // 'zip' or 'individual'
    const [keysToDownload, setKeysToDownload] = useState([]); // Store keys when modal opens
    
    const [showFileEditor, setShowFileEditor] = useState(false);
    const [fileToEdit, setFileToEdit] = useState(null);
    const [pdfConversionRequest, setPdfConversionRequest] = useState(null);
    const pdfConversionInFlightRef = useRef(new Set());
    
    const [sidePreview, setSidePreview] = useState(null);
    const [sidePreviewFile, setSidePreviewFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null); // Full file display modal
    
    const [heicConversionCache, setHeicConversionCache] = useState(new Map()); // Cache converted HEIC files
    const [heicConversionState, setHeicConversionState] = useState({
        isConverting: false,
        currentFile: null,
        conversionError: null
    });
    const [isDragOver, setIsDragOver] = useState(false);
    const [dragCounter, setDragCounter] = useState(0); // Track drag enter/leave events
    const [syncRules, setSyncRules] = useState(null);
    const [syncBusy, setSyncBusy] = useState(false);
    const [syncStatus, setSyncStatus] = useState({ pending: false, lastChecked: null, error: null });
    const [syncFileStatus, setSyncFileStatus] = useState({});

    const isParentCase = !!syncInfo?.is_parent;

    const normalizeSyncPath = normalizeDocumentSyncFolderPath;
    const normalizeSyncMatchPath = normalizeDocumentSyncMatchPath;

    const getFolderKeyFromItem = useCallback((item) => {
        if (item?.key) return normalizeSyncPath(item.key);

        if (!item?.name) return null;

        if (item.name === '{{Name}}') {
            return normalizeSyncPath(`cases/${case_id}/{{Name}}/`);
        }

        const currentPath = folderName && folderName !== 'root' && folderName !== '' && folderName !== '{{Name}}'
            ? folderName.replace(/^{{Name}}\/?/, '')
            : '';
        const basePath = currentPath ? `${currentPath.replace(/\/$/, '')}/` : '';
        return normalizeSyncPath(`cases/${case_id}/{{Name}}/${basePath}${item.name}/`);
    }, [case_id, folderName, normalizeSyncPath]);

    const normalizeFileLookupPath = useCallback((path) => {
        if (!path || typeof path !== 'string') return null;
        return path;
    }, []);

    const metadataByPath = useMemo(() => {
        const entries = new Map();

        const getMetadataRank = (meta) => {
            const numericId = Number(meta?.id);
            if (Number.isFinite(numericId)) {
                return numericId;
            }

            const timestamp = Date.parse(meta?.creation_date || meta?.last_modified || meta?.updated_at || '');
            return Number.isFinite(timestamp) ? timestamp : 0;
        };

        const setPreferredEntry = (key, meta) => {
            if (!key) return;
            const existing = entries.get(key);
            if (!existing || getMetadataRank(meta) >= getMetadataRank(existing)) {
                entries.set(key, meta);
            }
        };

        (metaData || []).forEach((meta) => {
            const rawPath = meta?.file_path;
            const normalizedPath = normalizeFileLookupPath(rawPath);

            setPreferredEntry(rawPath, meta);
            setPreferredEntry(normalizedPath, meta);
        });

        return entries;
    }, [metaData, normalizeFileLookupPath]);

    const getFileLookupCandidates = useCallback((file) => {
        const candidates = [];
        const seen = new Set();

        [file?.key, file?.file_path, file?.path].forEach((value) => {
            if (typeof value === 'string' && value.length > 0 && !seen.has(value)) {
                seen.add(value);
                candidates.push(value);
            }

            const normalizedValue = normalizeFileLookupPath(value);
            if (normalizedValue && !seen.has(normalizedValue)) {
                seen.add(normalizedValue);
                candidates.push(normalizedValue);
            }
        });

        return candidates;
    }, [normalizeFileLookupPath]);

    const resolveFileMetadata = useCallback((file) => {
        const candidates = getFileLookupCandidates(file);

        for (const candidate of candidates) {
            const match = metadataByPath.get(candidate);
            if (match) {
                return match;
            }
        }

        if (file?.id) {
            const numericId = Number(file.id);
            if (Number.isFinite(numericId)) {
                return (metaData || []).find((meta) => Number(meta?.id) === numericId) || null;
            }
        }

        return null;
    }, [getFileLookupCandidates, metaData, metadataByPath]);

    const currentFolderLookupPath = useMemo(() => {
        if (!folderName || folderName === 'root' || folderName === '') {
            return '{{Name}}';
        }
        return folderName;
    }, [folderName]);

    const currentFolderFiles = useMemo(() => {
        const propFiles = Object.values(files || {}).filter(file => file?.name && file.name !== '(Folder)');
        const folderMapFiles = Array.isArray(folders?.[currentFolderLookupPath])
            ? folders[currentFolderLookupPath].filter(file => file?.name && file.name !== '(Folder)')
            : [];

        const merged = new Map();
        [...propFiles, ...folderMapFiles].forEach((file) => {
            const key = file?.key || file?.file_path || file?.path || file?.name;
            if (key) {
                merged.set(key, file);
            }
        });

        return Array.from(merged.values());
    }, [files, folders, currentFolderLookupPath]);

    const currentFolderSubfolders = useMemo(() => {
        const merged = new Map();

        Object.entries(subfolders || {}).forEach(([name, folderData]) => {
            if (name) {
                merged.set(name, folderData || {});
            }
        });

        const folderPrefix = currentFolderLookupPath === '{{Name}}'
            ? '{{Name}}/'
            : `${currentFolderLookupPath.replace(/\/+$/, '')}/`;

        Object.keys(folders || {}).forEach((folderPath) => {
            if (!folderPath || folderPath === currentFolderLookupPath || !folderPath.startsWith(folderPrefix)) {
                return;
            }

            const relativePath = folderPath.slice(folderPrefix.length);
            if (!relativePath || relativePath.includes('/')) {
                return;
            }

            if (!merged.has(relativePath)) {
                const folderEntries = Array.isArray(folders?.[folderPath]) ? folders[folderPath] : [];
                const folderMeta = folderEntries.find(entry => entry?.name === '(Folder)') || {};
                merged.set(relativePath, {
                    ...folderMeta,
                    name: relativePath,
                    key: `cases/${case_id}/${folderPath.replace(/^\/+/, '').replace(/\/+$/, '')}/`,
                    size: folderMeta?.size ?? folderEntries.filter(entry => entry?.name && entry.name !== '(Folder)').length,
                    last_modified: folderMeta?.last_modified || null
                });
            }
        });

        return Object.fromEntries(merged.entries());
    }, [subfolders, folders, currentFolderLookupPath, case_id]);

    const getCurrentFolderSyncKey = useCallback(() => {
        if (!case_id) return null;
        if (!folderName || folderName === 'root' || folderName === '' || folderName === '{{Name}}') {
            return normalizeSyncPath(`cases/${case_id}/{{Name}}/`);
        }

        let cleanPath = folderName.replace(/^{{Name}}\/?/, '').replace(/^\/+/, '');
        if (!cleanPath) {
            return normalizeSyncPath(`cases/${case_id}/{{Name}}/`);
        }

        if (!cleanPath.endsWith('/')) cleanPath += '/';
        return normalizeSyncPath(`cases/${case_id}/{{Name}}/${cleanPath}`);
    }, [case_id, folderName, normalizeSyncPath]);

    const effectiveSyncRules = syncRules || syncInfo?.rules || {};
    const syncFolderPaths = (effectiveSyncRules.folder_paths || []).map(normalizeSyncPath).filter(Boolean);

    const isFolderSyncPoint = useCallback((folderKey) => {
        const normalized = normalizeSyncMatchPath(folderKey);
        if (!normalized) return false;
        const relative = normalized.replace(/^cases\/\d+\//, '');
        return syncFolderPaths.some(folder => {
            const folderRelative = normalizeSyncMatchPath(folder)?.replace(/^cases\/\d+\//, '');
            return folderRelative === relative;
        });
    }, [normalizeSyncMatchPath, syncFolderPaths]);

    const isPathInSyncPoints = useCallback((path) => {
        const normalized = normalizeSyncMatchPath(path);
        if (!normalized) return false;
        const relative = normalized.replace(/^cases\/\d+\//, '');
        return syncFolderPaths.some(folder => {
            const folderRelative = normalizeSyncMatchPath(folder)?.replace(/^cases\/\d+\//, '');
            return folderRelative ? relative.startsWith(folderRelative) : false;
        });
    }, [normalizeSyncMatchPath, syncFolderPaths]);

    const fetchSyncStatus = useCallback(async () => {
        if (!isParentCase || !case_id || syncFolderPaths.length === 0) {
            setSyncStatus({ pending: false, lastChecked: Date.now(), error: null });
            return;
        }

        try {
            const response = await fetch(`https://api.casedb.co/case_sync_queue.php?parent_case_id=${case_id}&status=pending`, {
                credentials: 'include'
            });
            const data = await response.json();
            const pending = Array.isArray(data?.items) ? data.items.length > 0 : false;
            setSyncStatus({ pending, lastChecked: Date.now(), error: null });
        } catch (error) {
            setSyncStatus({ pending: false, lastChecked: Date.now(), error: 'fetch_failed' });
        }
    }, [case_id, isParentCase, syncFolderPaths.length]);

    const fetchSyncFileStatuses = useCallback(async (paths) => {
        if (!case_id || !Array.isArray(paths) || paths.length === 0) {
            setSyncFileStatus({});
            return;
        }

        try {
            const response = await fetch('https://api.casedb.co/case_sync_status.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    case_id,
                    paths
                })
            });
            const data = await response.json();
            if (data?.success && Array.isArray(data?.statuses)) {
                const next = {};
                data.statuses.forEach((row) => {
                    if (row?.file_path) {
                        next[row.file_path] = row;
                    }
                });
                setSyncFileStatus(next);
            } else {
                setSyncFileStatus({});
            }
        } catch (error) {
            setSyncFileStatus({});
        }
    }, [case_id]);

    useEffect(() => {
        archivePollActiveRef.current = true;
        return () => {
            archivePollActiveRef.current = false;
        };
    }, []);

    useEffect(() => {
        const refreshAfterArchiveRetry = async (event) => {
            if (String(event?.detail?.caseId || '') !== String(case_id || '')) return;
            try {
                if (refreshFolderTree) await refreshFolderTree();
                if (refreshCurrentFolder) await refreshCurrentFolder(1, folderName);
                if (fetchDocuments) await fetchDocuments();
            } catch (error) {
                console.error('Unable to refresh resumed document package:', error);
            }
        };
        window.addEventListener(ARCHIVE_IMPORT_TERMINAL_EVENT, refreshAfterArchiveRetry);
        return () => window.removeEventListener(ARCHIVE_IMPORT_TERMINAL_EVENT, refreshAfterArchiveRetry);
    }, [case_id, fetchDocuments, folderName, refreshCurrentFolder, refreshFolderTree]);

    useEffect(() => {
        fetchSyncStatus();
        if (!isParentCase || syncFolderPaths.length === 0) return;
        const interval = setInterval(fetchSyncStatus, 15000);
        return () => clearInterval(interval);
    }, [fetchSyncStatus, isParentCase, syncFolderPaths.length]);

    useEffect(() => {
        const fileArray = Array.isArray(files) ? files : Object.values(files || {});
        const paths = fileArray
            .map((file) => file?.key || file?.file_path)
            .filter(Boolean);
        fetchSyncFileStatuses(paths);
    }, [files, fetchSyncFileStatuses]);

    useEffect(() => {
        if (syncInfo?.rules) {
            setSyncRules(syncInfo.rules);
        }
    }, [syncInfo]);

    const toggleFolderSyncPoint = useCallback(async (folderKey) => {
        if (!isParentCase || !folderKey || syncBusy) return;
        const normalized = normalizeSyncPath(folderKey);
        if (!normalized) return;

        const current = syncFolderPaths;
        const already = current.includes(normalized);
        const nextPaths = already
            ? current.filter(p => p !== normalized)
            : [...current, normalized];

        const nextRules = {
            ...effectiveSyncRules,
            share_docs: nextPaths.length > 0 ? 1 : 0,
            folder_paths: nextPaths,
            field_groups: effectiveSyncRules.field_groups || [],
            feed_update_ids: effectiveSyncRules.feed_update_ids || [],
        };

        setSyncBusy(true);
        try {
            const response = await fetch('https://api.casedb.co/case_sync_rules.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    parent_case_id: case_id,
                    share_docs: nextRules.share_docs,
                    share_field_groups: nextRules.share_field_groups || 0,
                    share_feed_updates: nextRules.share_feed_updates || 0,
                    folder_paths: nextRules.folder_paths || [],
                    field_groups: nextRules.field_groups || [],
                    feed_update_ids: nextRules.feed_update_ids || []
                })
            });

            const data = await response.json();
            if (data?.success) {
                setSyncRules(nextRules);
                fetchSyncStatus();
                setMessage({ text: already ? 'Sync point removed.' : 'Sync point added.', type: 'success' });
                setTimeout(() => setMessage(null), 3000);
            } else {
                setMessage({ text: data?.message || 'Failed to update sync settings.', type: 'error' });
                setTimeout(() => setMessage(null), 5000);
            }
        } catch (error) {
            setMessage({ text: 'Failed to update sync settings.', type: 'error' });
            setTimeout(() => setMessage(null), 5000);
        } finally {
            setSyncBusy(false);
        }
    }, [case_id, effectiveSyncRules, fetchSyncStatus, isParentCase, normalizeSyncPath, syncBusy, syncFolderPaths]);

    const folderSizeCache = useRef(new Map()); // Cache folder sizes to persist during navigation
    const deleteWarningShownRef = useRef(false); // Track if we've already shown a refresh warning alert during delete
    const [showMergeDownloadModal, setShowMergeDownloadModal] = useState(false);
    const [mergeResult, setMergeResult] = useState(null);
    const [isMerging, setIsMerging] = useState(false);
    
    const [showAdvancedMergeModal, setShowAdvancedMergeModal] = useState(false);
    const [mergeSettings, setMergeSettings] = useState({
        fileName: 'merged_documents',
        targetFolder: '',
        selectedFiles: [],
        fileOrder: [],
        ocrPdf: false
    });

    // Clear optimistic delete filters when navigating to a different folder; keep them while in the same folder
    useEffect(() => {
        setDeletedPrefixes([]);
    }, [folderName]);

    // Warn the user if they try to close/refresh while a delete is running
    useEffect(() => {
        if (!deleteInProgress) {
            deleteWarningShownRef.current = false;
            return;
        }
        const handler = (event) => {
            const message = 'A delete is still in progress. Leaving will cancel it.';
            if (!deleteWarningShownRef.current) {
                deleteWarningShownRef.current = true;
                try {
                    window.alert(message);
                } catch (err) {
                    // Some browsers block alerts during beforeunload; ignore silently
                }
            }
            event.preventDefault();
            event.returnValue = message;
            return message;
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [deleteInProgress]);
    
    // Enhanced search states
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isSearchQueued, setIsSearchQueued] = useState(false);
    const [searchAllFiles, setSearchAllFiles] = useState(false);
    const isLiveNewItem = useCallback((type, item) => (
        !searchAllFiles && liveNewItemIds?.has?.(getDocumentLiveItemId(type, item))
    ), [liveNewItemIds, searchAllFiles]);
    const markLiveItemSeen = useCallback((type, item) => {
        onLiveItemSeen(type, item);
    }, [onLiveItemSeen]);
    const [searchScope, setSearchScope] = useState('case');
    const [searchMatchMode, setSearchMatchMode] = useState('all_words');
    const [searchFilters, setSearchFilters] = useState(DEFAULT_DOCUMENT_SEARCH_FILTERS);
    const [showDocumentSearchFilters, setShowDocumentSearchFilters] = useState(false);
    const documentSearchFilterRef = useRef(null);
    const searchRequestSeqRef = useRef(0);
    const searchAbortControllerRef = useRef(null);
    const searchDebounceTimerRef = useRef(null);

    const clearQueuedDocumentSearch = useCallback(() => {
        if (searchDebounceTimerRef.current) {
            clearTimeout(searchDebounceTimerRef.current);
            searchDebounceTimerRef.current = null;
        }
        setIsSearchQueued(false);
    }, []);

    const cancelActiveDocumentSearch = useCallback(() => {
        clearQueuedDocumentSearch();
        searchRequestSeqRef.current += 1;
        if (searchAbortControllerRef.current) {
            try {
                searchAbortControllerRef.current.abort();
            } catch (_) {}
            searchAbortControllerRef.current = null;
        }
        setIsSearching(false);
    }, [clearQueuedDocumentSearch]);

    useEffect(() => {
        return () => {
            if (searchDebounceTimerRef.current) {
                clearTimeout(searchDebounceTimerRef.current);
            }
            if (searchAbortControllerRef.current) {
                try {
                    searchAbortControllerRef.current.abort();
                } catch (_) {}
            }
        };
    }, []);

    useEffect(() => {
        if (!showDocumentSearchFilters) return;

        const handleClickOutside = (event) => {
            if (documentSearchFilterRef.current && !documentSearchFilterRef.current.contains(event.target)) {
                setShowDocumentSearchFilters(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showDocumentSearchFilters]);

    // Hot docs functionality states
    const [hotDocsOnly, setHotDocsOnly] = useState(false);
    const [hotDocsList, setHotDocsList] = useState(new Set());
    
    // Bates stamping functionality states
    const [showBatesModal, setShowBatesModal] = useState(false);
    const [batesStep, setBatesStep] = useState(1);
    const [batesFiles, setBatesFiles] = useState([]);
    const [batesAnalyzedFiles, setBatesAnalyzedFiles] = useState([]);
    const [batesSettings, setBatesSettings] = useState({
        selectedFiles: [],
        fileOrder: [],
        destination: { type: 'current', path: '' },
        prefix: '',
        suffix: '',
        startNumber: 1,
        padding: 5,
        position: 'bottom-right',
        replaceOriginals: false,
        stampRedText: false,
        shrinkToMargin: false,
        pageLabelMode: 'bates',
        docLabelSource: 'filename',
        exhibitStampStyle: 'auto',
        exhibitDisplayIdentifierMode: 'identifier',
        exhibitOutputNameMode: 'identifier',
        exhibitAutoPlacementPolicy: 'strict',
        preserveStructure: true,
        conflictPolicy: 'dedupe'
    });
    const [batesSelection, setBatesSelection] = useState({
        mode: 'files',
        files: [],
        folders: [],
        tree: [],
        orderedFolderParent: ''
    });
    const [batesStampMode, setBatesStampMode] = useState('continuous');
    const [batesMergeMode, setBatesMergeMode] = useState('none'); // 'none' | 'all' | 'per-folder'
    const batesCurrentFolderPath = !folderName || folderName === 'root' || folderName === ''
        ? '{{Name}}'
        : folderName;
    const [batesFolderSettings, setBatesFolderSettings] = useState({});
    const [batesMergedName, setBatesMergedName] = useState('Bates_Merged');
    const [batesAnalyzeLoading, setBatesAnalyzeLoading] = useState(false);
    const [batesSelectionSnapshot, setBatesSelectionSnapshot] = useState(null);
    const [batesExhibitOverrides, setBatesExhibitOverrides] = useState({});
    const [batesExhibitSequence, setBatesExhibitSequence] = useState({
        format: 'PTX',
        customFormat: '',
        startNumber: 1,
        padding: 4,
        stampStyle: 'auto',
        scope: 'all'
    });
    const [batesExhibitPageLabels, setBatesExhibitPageLabels] = useState({
        enabled: false,
        padding: 3,
        separator: '-',
        position: 'bottom-left'
    });
    const [batesSubfoldersFollowRules, setBatesSubfoldersFollowRules] = useState(true);
    const [batesContinuousSubfolders, setBatesContinuousSubfolders] = useState(false);
    const [batesProcessing, setBatesProcessing] = useState(false);
    const [batesProgress, setBatesProgress] = useState({});
    const [batesResult, setBatesResult] = useState(null);
    const batesAnalyzeControllerRef = useRef(null);
    const batesAnalyzeGenerationRef = useRef(0);
    const batesStartGuardRef = useRef(false);
    const batesPollControllerRef = useRef(null);
    const batesPollGenerationRef = useRef(0);
    const batesPollTimerRef = useRef(null);
    const batesActiveTasksRef = useRef(new Map());
    const batesResumePollRef = useRef(null);
    const batesCaseIdRef = useRef(String(case_id));
    batesCaseIdRef.current = String(case_id);
    const menuRef = useRef(null);
    const [menuPositionAbove, setMenuPositionAbove] = useState({});
    const [menuPosition, setMenuPosition] = useState({});
    const clickTimer = useRef(null); // For handling single/double click timing
    const lastMobileTapRef = useRef({ key: null, time: 0 });

    useEffect(() => {
        const effectCaseId = String(case_id);
        batesStartGuardRef.current = false;
        setBatesProcessing(false);
        setShowBatesModal(false);
        const resumableTask = batesActiveTasksRef.current.get(effectCaseId);
        if (resumableTask) {
            setBatesProcessing(true);
            dispatch({
                type: 'UPDATE_UPLOAD',
                payload: {
                    id: resumableTask.uploadId,
                    status: 'uploading',
                    error: null
                }
            });
            Promise.resolve().then(() => {
                if (effectCaseId === batesCaseIdRef.current && batesResumePollRef.current) {
                    batesResumePollRef.current(
                        resumableTask.taskId,
                        resumableTask.uploadId,
                        resumableTask.analyzeRequest
                    );
                }
            });
        }
        return () => {
            const pausedTask = batesActiveTasksRef.current.get(effectCaseId);
            if (pausedTask) {
                dispatch({
                    type: 'FAIL_UPLOAD',
                    payload: {
                        id: pausedTask.uploadId,
                        error: 'Status tracking paused after navigation. The server task continues; return to this case to resume tracking.'
                    }
                });
            }
            batesAnalyzeGenerationRef.current += 1;
            batesAnalyzeControllerRef.current?.abort();
            batesPollGenerationRef.current += 1;
            batesPollControllerRef.current?.abort();
            if (batesPollTimerRef.current) clearTimeout(batesPollTimerRef.current);
            batesPollTimerRef.current = null;
        };
    }, [case_id]);

    const calculateMenuPosition = (index, subfolder = false, menuElement = null) => {
        const menuId = subfolder ? `${index}sub` : index;
        
        // Find the dots element that was clicked
        const dotsElement = document.querySelector(`[data-menu-index="${menuId}"]`);
        if (!dotsElement) return { showAbove: false, left: 0, top: 0 };
        
        const dotsRect = dotsElement.getBoundingClientRect();
        // Use actual menu height if available, otherwise estimate
        const menuHeight = menuElement ? menuElement.offsetHeight : 270;
        const menuWidth = menuElement ? menuElement.offsetWidth : 150;
        
        // Check if menu would go below viewport bottom - add some margin for safety
        const spaceBelow = window.innerHeight - dotsRect.bottom - 20; // 20px margin
        const showAbove = spaceBelow < menuHeight;
        
        // Calculate position
        let left = dotsRect.right - menuWidth;
        let top = showAbove ? dotsRect.top - menuHeight - 5 : dotsRect.bottom + 5; // 5px gap from dots
        
        // Ensure menu stays within viewport
        if (left < 10) left = 10;
        if (left + menuWidth > window.innerWidth - 10) left = window.innerWidth - menuWidth - 10;
        if (top < 10) top = 10;
        if (top + menuHeight > window.innerHeight - 10) top = window.innerHeight - menuHeight - 10;
        
        return { showAbove, left, top };
    };
    const safeSearchQuery = searchQuery ? searchQuery.trim().toLowerCase() : '';
    const safeSearchType = searchType || 'All';
    const searchTypeLabel = DOCUMENT_SEARCH_TYPE_LABELS[safeSearchType] || safeSearchType;
    const [showUploadModal, setShowUploadModal] = useState(false);
    
    // Safety: Fix selectedKeys if it's not an array
    React.useEffect(() => {
        if (!Array.isArray(selectedKeys)) {
            console.error('selectedKeys is not an array, resetting to []', selectedKeys);
            setSelectedKeys([]);
        }
    }, [selectedKeys]);

    useEffect(() => {
        if (Array.isArray(selectedKeys) && selectedKeys.length === 0) {
            selectionAnchorKeyRef.current = null;
        }
    }, [selectedKeys]);

    useEffect(() => () => {
        documentDragPreviewRef.current?.remove?.();
        documentDragPreviewRef.current = null;
    }, []);
    useEffect(() => {
        const removeMovedSelection = (event) => {
            if (String(event?.detail?.caseId ?? '') !== String(case_id ?? '')) return;

            const movedKeys = Array.isArray(event?.detail?.sourceKeys) ? event.detail.sourceKeys : [];
            if (movedKeys.length === 0) return;

            setSelectedKeys(current => removeMovedDocumentSelectionKeys(current, movedKeys));
            setActiveFile(current => removeMovedDocumentSelectionKeys(current, movedKeys));
        };

        window.addEventListener(DOCUMENT_SELECTION_MOVE_EVENT, removeMovedSelection);
        return () => window.removeEventListener(DOCUMENT_SELECTION_MOVE_EVENT, removeMovedSelection);
    }, [case_id]);

    
    // Sorting functionality states
    const [sortColumn, setSortColumn] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');
    const activeSortColumn = externalSortColumn || sortColumn;
    const activeSortDirection = externalSortDirection || sortDirection;

    // --- Robust upload management additions ---
    const uploadingRef = useRef(false); // true while a batch upload is actively streaming
    const cancelRequestedRef = useRef(false); // flag for cooperative cancellation
    const activeUploadControllerRef = useRef(null); // AbortController for current chunk request
    const [uploadStateMap, setUploadStateMap] = useState({}); // { fileName: { status: 'queued'|'uploading'|'done'|'error'|'canceled', progress } }

    const resetUploadSession = () => {
        uploadingRef.current = false;
        cancelRequestedRef.current = false;
        if (activeUploadControllerRef.current) {
            try { activeUploadControllerRef.current.abort(); } catch (_) {}
        }
        activeUploadControllerRef.current = null;
        setUploadStateMap({});
    };

    // Check for file conflicts before upload/move/copy
    const checkFileConflicts = async (fileNames, targetFolder) => {
        let asyncStarted = false;
        try {
            const response = await fetch(`https://api.casedb.co/check-file-conflicts.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    case_id: case_id,
                    folder_path: targetFolder,
                    file_names: fileNames
                })
            });

            const data = await response.json();
            
            console.log('[CONFLICT CHECK] Response:', data);
            
            if (!data.success) {
                console.error('Error checking conflicts:', data.message);
                return { hasConflicts: false, conflicts: [], suggestions: {} };
            }

            return {
                hasConflicts: data.has_conflicts,
                conflicts: data.conflicts.map(conflict => ({
                    ...conflict,
                    caseName: conflict.caseName || caseName // Use backend caseName or fallback to local
                })),
                suggestions: data.suggestions
            };
        } catch (error) {
            console.error('Error checking file conflicts:', error);
            return { hasConflicts: false, conflicts: [], suggestions: {} };
        }
    };

    // Generate unique filename with (1), (2), etc.
    const generateUniqueFileName = (fileName, existingFiles) => {
        const nameWithoutExt = fileName.includes('.') ? fileName.substring(0, fileName.lastIndexOf('.')) : fileName;
        const extension = fileName.includes('.') ? fileName.substring(fileName.lastIndexOf('.')) : '';
        
        let counter = 1;
        let newName = fileName;
        
        while (existingFiles.includes(newName)) {
            newName = `${nameWithoutExt} (${counter})${extension}`;
            counter++;
        }
        
        return newName;
    };

    // Apply conflict resolutions to file list
    const applyConflictResolutions = (files, resolutions, suggestions) => {
        const existingFiles = [];
        
        return files.map(fileItem => {
            const ext = (fileItem.newExtension || fileItem.extension || '').trim().replace(/^\./, '');
            const fileName = fileItem.file ? (ext ? `${fileItem.newName}.${ext}` : fileItem.newName) : fileItem.originalName;
            
            if (resolutions[fileName] === 'keep-both') {
                // Use suggested unique name from backend
                const uniqueName = suggestions[fileName] || generateUniqueFileName(fileName, existingFiles);
                existingFiles.push(uniqueName);
                
                if (fileItem.file) {
                    // For files with extensions
                    const nameWithoutExt = uniqueName.includes('.') ? 
                        uniqueName.substring(0, uniqueName.lastIndexOf('.')) : uniqueName;
                    const extension = uniqueName.includes('.') ? 
                        uniqueName.substring(uniqueName.lastIndexOf('.') + 1) : '';
                    
                    return {
                        ...fileItem,
                        newName: nameWithoutExt,
                        extension: extension || fileItem.extension,
                        newExtension: extension || fileItem.newExtension || fileItem.extension
                    };
                } else {
                    // For folders
                    return {
                        ...fileItem,
                        newName: uniqueName
                    };
                }
            } else {
                // Overwrite - keep original name
                existingFiles.push(fileName);
                return fileItem;
            }
        });
    };

    // Apply skipping to the entire captured upload tree, not a basename-only
    // conflict list. The upload service checks each final destination and the
    // server enforces create-only writes for anything missing at that check.
    const handleSkipExistingConflicts = async () => {
        const operation = pendingOperation;
        if (operation?.type !== 'upload' || uploadingRef.current
            || !isCurrentUploadSelection(operation.selection)) return;
        setShowConflictModal(false);
        setPendingOperation(null);
        setConflictFiles([]);
        setSkipExistingUploads(true);
        await proceedWithUpload(
            [...operation.folderList, ...operation.fileList],
            operation.destinationPath,
            { ...operation.selection, skipExisting: true }
        );
    };

    // Handle conflict resolution
    const handleConflictResolution = async (resolutions) => {
        setShowConflictModal(false);
        
        if (!pendingOperation) return;
        
        if (pendingOperation.type === 'upload') {
            if (!isCurrentUploadSelection(pendingOperation.selection)) {
                setPendingOperation(null);
                return;
            }
            // Apply resolutions to file list
            const updatedFiles = applyConflictResolutions(
                pendingOperation.fileList,
                resolutions,
                pendingOperation.suggestions
            );
            
            const updatedFolders = applyConflictResolutions(
                pendingOperation.folderList,
                resolutions,
                pendingOperation.suggestions
            );
            
            // Update the filesToRename state with resolved names and trigger upload
            setFilesToRename([...updatedFolders, ...updatedFiles]);
            
            // Small delay to ensure state updates, then programmatically trigger upload
            setTimeout(() => {
                // Directly call the upload logic without showing rename modal again
                proceedWithUpload(
                    [...updatedFolders, ...updatedFiles],
                    pendingOperation.destinationPath,
                    pendingOperation.selection
                );
            }, 100);
            
        } else if (pendingOperation.type === 'move' || pendingOperation.type === 'copy') {
            // For move/copy, we need to rename files that should keep both
            const keysToRename = new Map();
            
            Object.keys(resolutions).forEach(fileName => {
                if (resolutions[fileName] === 'keep-both') {
                    const suggestion = pendingOperation.suggestions[fileName];
                    if (suggestion) {
                        keysToRename.set(fileName, suggestion);
                    }
                }
            });
            
            // If we have files to rename, we need to handle this specially
            if (keysToRename.size > 0) {
                // For now, show an alert that we need to rename files on the backend
                // In a full implementation, you'd update the backend to accept renamed files
                alert('Files with conflicts will be automatically renamed to avoid overwriting.');
            }
            
            // Execute the move/copy operation
            await executeMoveOrCopy(
                pendingOperation.selectedKeys,
                pendingOperation.targetFolder,
                pendingOperation.type === 'copy',
                resolutions,
                pendingOperation.suggestions
            );
        }
        
        setPendingOperation(null);
    };


    // Pagination handlers
    const handlePageChange = (newPage) => {
        if (onPageChange) {
            onPageChange(newPage);
        } else if (fetchFolderContents) {
            fetchFolderContents(folderName, newPage, pageSize);
        } else if (fetchDocuments) {
            fetchDocuments(newPage);
        }
    };

    const handlePreviousPage = () => {
        if (pagination?.has_prev) {
            handlePageChange(pagination.current_page - 1);
        }
    };

    const handleNextPage = () => {
        if (pagination?.has_next) {
            handlePageChange(pagination.current_page + 1);
        }
    };

    const handleUploadFiles = (files) => {
        // Always show the rename modal first for file organization
        handleFileSet(files);
    };

    // Sorting function
    const handleSort = (column) => {
        const nextDirection = activeSortColumn === column
            ? (activeSortDirection === 'asc' ? 'desc' : 'asc')
            : 'asc';
        if (onSortChange) {
            onSortChange(column, nextDirection);
        } else if (activeSortColumn === column) {
            setSortDirection(nextDirection);
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    // Sort items based on current sort column and direction
    const sortItems = (items) => {
        return [...items].sort((a, b) => {
            // Always sort folders first, then files
            if (a.type === 'folder' && b.type !== 'folder') {
                return -1;
            }
            if (b.type === 'folder' && a.type !== 'folder') {
                return 1;
            }
            
            // Both are same type (folder/folder or file/file), sort by selected column
            let aValue, bValue;
            
            switch (activeSortColumn) {
                case 'name':
                    // Natural sort: tokenize numbers so "file 2" comes before "file 14"
                    const natTokenize = (str) => {
                        return str.toLowerCase().match(/(\d+|\D+)/g)?.map(tok => {
                            if (/^\d+$/.test(tok)) { return { type: 'num', v: parseInt(tok,10) }; }
                            return { type: 'str', v: tok };
                        }) || [];
                    };
                    const aa = natTokenize(a.name || '');
                    const bb = natTokenize(b.name || '');
                    const len = Math.max(aa.length, bb.length);
                    for (let i=0;i<len;i++) {
                        const ta = aa[i];
                        const tb = bb[i];
                        if (!ta) { aValue = -1; bValue = 0; break; }
                        if (!tb) { aValue = 1; bValue = 0; break; }
                        if (ta.type === 'num' && tb.type === 'num') {
                            if (ta.v !== tb.v) { aValue = ta.v; bValue = tb.v; break; }
                        } else if (ta.type === 'num') { aValue = -1; bValue = 0; break; }
                        else if (tb.type === 'num') { aValue = 1; bValue = 0; break; }
                        else if (ta.v !== tb.v) { aValue = ta.v; bValue = tb.v; break; }
                        if (i === len-1) { aValue = 0; bValue = 0; }
                    }
                    if (aValue === undefined) { aValue = 0; bValue = 0; }
                    // We'll rely on numeric comparison below; if tie, fall back to full string compare
                    if (aValue === 0 && bValue === 0) {
                        aValue = (a.name||'').toLowerCase();
                        bValue = (b.name||'').toLowerCase();
                    }
                    break;
                case 'date':
                    aValue = new Date(a.last_modified || 0);
                    bValue = new Date(b.last_modified || 0);
                    break;
                case 'size':
                    // For folders, use item count; for files, use file size
                    if (a.type === 'folder' && b.type === 'folder') {
                        aValue = Number(a.size) || 0;
                        bValue = Number(b.size) || 0;
                    } else {
                        aValue = getDocumentItemSizeBytes(a);
                        bValue = getDocumentItemSizeBytes(b);
                    }
                    break;
                case 'author':
                    const aAuthor = a.author_name || a.meta?.uploaded_by_name || userMap[a.meta?.author_id] || '';
                    const bAuthor = b.author_name || b.meta?.uploaded_by_name || userMap[b.meta?.author_id] || '';
                    aValue = aAuthor.toLowerCase();
                    bValue = bAuthor.toLowerCase();
                    break;
                default:
                    aValue = a.name.toLowerCase();
                    bValue = b.name.toLowerCase();
            }
            
            const cmp = (x, y) => {
                if (typeof x === 'number' && typeof y === 'number') return x - y;
                // Mixed types: numbers come first
                const xn = typeof x === 'number';
                const yn = typeof y === 'number';
                if (xn && !yn) return -1;
                if (!xn && yn) return 1;
                if (x < y) return -1;
                if (x > y) return 1;
                return 0;
            };
            const base = cmp(aValue, bValue);
            if (base !== 0) return activeSortDirection === 'asc' ? base : -base;

            // Server pages use the same deterministic key tie-breaker. This
            // prevents equal values from jumping as metadata/user names load.
            return String(a.key || a.name || '').localeCompare(
                String(b.key || b.name || ''),
                undefined,
                { numeric: true, sensitivity: 'base' }
            );
        });
    };

    // Monitor window width changes with debouncing for better performance
    useEffect(() => {
        let resizeTimeout;
        const breakpoints = [600, 767, 900];
        
        const handleResize = () => {
            const newWidth = window.innerWidth;
            const currentWidth = windowWidth;
            
            // Check if we've crossed any breakpoint
            const hasBreakpointChange = breakpoints.some(bp => 
                (currentWidth <= bp && newWidth > bp) || (currentWidth > bp && newWidth <= bp)
            );
            
            // Clear any existing timeout
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
            
            // Set immediate update for responsive behavior
            setWindowWidth(newWidth);
            
            // Additional update after debounce if we crossed breakpoints
            if (hasBreakpointChange) {
                resizeTimeout = setTimeout(() => {
                    setWindowWidth(window.innerWidth);
                    console.log('Exhibits table: Window resized, width:', window.innerWidth, 'Breakpoint crossed');
                }, 50);
            } else {
                // Standard debounce for other resize events
                resizeTimeout = setTimeout(() => {
                    setWindowWidth(window.innerWidth);
                }, 150);
            }
        };

        // Set initial window width
        setWindowWidth(window.innerWidth);
        console.log('Exhibits table: Initial window width:', window.innerWidth);
        
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
        };
    }, [windowWidth]);

    // Force component update when window width crosses responsive breakpoints
    useEffect(() => {
        // This effect ensures the component re-renders when windowWidth changes
        // triggering updates to conditional rendering based on width thresholds
    }, [windowWidth]);

    // Cleanup HEIC conversion cache URLs to prevent memory leaks
    useEffect(() => {
        return () => {
            // Cleanup all cached object URLs when component unmounts
            heicConversionCache.forEach((url) => {
                try {
                    URL.revokeObjectURL(url);
                } catch (error) {
                    console.warn('Failed to revoke object URL:', error);
                }
            });
        };
    }, []);

    // Cache folder sizes to persist across navigation
    useEffect(() => {
        // Cache sizes from current subfolders
        Object.entries(currentFolderSubfolders).forEach(([folderName, folderData]) => {
            if (folderData.size !== undefined) {
                const folderKey = `${case_id}:${folderName}`;
                folderSizeCache.current.set(folderKey, folderData.size);
            }
        });
        
        // Cache sizes from folders prop as well
        if (folders) {
            Object.entries(folders).forEach(([folderPath, folderFiles]) => {
                const folderMeta = folderFiles?.find(f => f.name === "(Folder)");
                if (folderMeta?.size !== undefined) {
                    const folderKey = `${case_id}:${folderPath}`;
                    folderSizeCache.current.set(folderKey, folderMeta.size);
                }
            });
        }
    }, [currentFolderSubfolders, folders, case_id]);

    // Listen for upload/delete completion to auto-close modals and refresh
    useEffect(() => {
        const unsubscribe = simpleUploadService.addListener((uploads) => {
            const stats = simpleUploadService.getUploadStats();
            
            // Check if there are any delete operations
            const deleteOps = uploads.filter(upload => upload.isDelete);
            const uploadOps = uploads.filter(upload => !upload.isDelete);
            
            // Auto-close upload modal when all uploads are finished (successful or failed)
            if (uploadingRef.current && isCurrentUploadSelection(activeUploadSelectionRef.current)
                && stats.allFinished && stats.total > 0 && (showUploadModal || showRenameModal)) {
                console.log('[OPERATIONS] All operations finished, auto-closing modal', stats);
                setShowUploadModal(false);
                setShowRenameModal(false);
                
                // Refresh document list after operations complete
                if (refreshCurrentFolder) {
                    refreshCurrentFolder();
                } else if (fetchDocuments) {
                    fetchDocuments();
                }
                
                // Note: Keep upload service visible so users can see results and close manually
            }
            
            // Auto-refresh when delete operations complete (even if no modal is open)
            else if (stats.allFinished && deleteOps.length > 0 && uploadOps.length === 0) {
                console.log('🗑️ [DELETE] All delete operations finished, refreshing', {
                    completed: deleteOps.filter(d => d.status === 'completed').length,
                    failed: deleteOps.filter(d => d.status === 'error').length,
                    deleteOps: deleteOps.map(d => ({ id: d.id, status: d.status, fileName: d.fileName })),
                    hasRefreshFunction: !!refreshCurrentFolder,
                    hasFetchFunction: !!fetchDocuments
                });
                
                // Refresh document list after delete operations complete
                if (refreshCurrentFolder) {
                    console.log('🗑️ [DELETE] Calling refreshCurrentFolder()');
                    refreshCurrentFolder();
                } else if (fetchDocuments) {
                    console.log('🗑️ [DELETE] Calling fetchDocuments()');
                    fetchDocuments();
                } else {
                    console.warn('🗑️ [DELETE] No refresh function available!');
                }
                
                // Note: Keep upload service visible so users can see delete results
            }
        });

        return unsubscribe;
    }, [showUploadModal, showRenameModal, refreshCurrentFolder, fetchDocuments]);

    const formatSize = (size) => {
        if (size === null || size === undefined) return "-";
        if (!size || size === 0) return "0 KB";
        return size < 1024
            ? `${size} B`
            : size < 1024 * 1024
            ? `${(size / 1024).toFixed(1)} KB`
            : size < 1024 * 1024 * 1024
            ? `${(size / 1024 / 1024).toFixed(1)} MB`
            : size < 1024 * 1024 * 1024 * 1024
            ? `${(size / 1024 / 1024 / 1024).toFixed(1)} GB`
            : `${(size / 1024 / 1024 / 1024 / 1024).toFixed(1)} TB`;
    };

    const formatLastModified = (dateString) => {
        if (!dateString || typeof dateString !== 'string') return ["", "", "", "", "", ""];

        const months = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];

        try {
            const hasTimeZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(dateString);
            const isDateTimeWithSpace = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString);
            const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
            const isIsoWithoutZone = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(dateString);

            let normalized = dateString;
            if (!hasTimeZone) {
                if (isDateTimeWithSpace) {
                    normalized = `${dateString.replace(' ', 'T')}Z`;
                } else if (isDateOnly) {
                    normalized = `${dateString}T00:00:00Z`;
                } else if (isIsoWithoutZone) {
                    normalized = `${dateString}Z`;
                }
            }

            let parsedDate = new Date(normalized);

            if (isNaN(parsedDate.getTime())) {
                parsedDate = new Date(`${dateString}T00:00:00Z`);
            }

            if (isNaN(parsedDate.getTime())) {
                return ["", "", "", "", "", ""];
            }

            const fallbackTimeZone = "America/Chicago";
            const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || fallbackTimeZone;
            let zonedTime;
            try {
                zonedTime = new Date(parsedDate.toLocaleString("en-US", { timeZone: userTimeZone }));
            } catch (e) {
                zonedTime = new Date(parsedDate.toLocaleString("en-US", { timeZone: fallbackTimeZone }));
            }

            const iso = parsedDate.toISOString();
            const longDate = `${months[zonedTime.getMonth()]} ${zonedTime.getDate()}, ${zonedTime.getFullYear()}`;
            const shortDate = `${zonedTime.getMonth() + 1}/${zonedTime.getDate()}/${zonedTime.getFullYear()}`;
            const shortFormatted = `${zonedTime.getDate()} ${months[zonedTime.getMonth()].slice(0, 3)} ${zonedTime.getFullYear()}`;
            const hours = zonedTime.getHours();
            const minutes = zonedTime.getMinutes();
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const hour12 = hours % 12 || 12;
            const minutePadded = String(minutes).padStart(2, '0');
            const timeFormatted = `${hour12}:${minutePadded} ${ampm}`;
            const dateTimeFormatted = `${zonedTime.getMonth() + 1}/${zonedTime.getDate()}/${zonedTime.getFullYear()} ${timeFormatted}`;

            return [longDate, iso, timeFormatted, shortDate, shortFormatted, dateTimeFormatted];
        } catch (e) {
            console.error("Invalid date:", dateString, e);
            return ["", "", "", "", "", ""];
        }
    };

    const formatLastModifiedSearch = (dateString) => {
        if (!dateString) return '';
        const [longDate, , timeFormatted, shortDate, shortFormatted, dateTimeFormatted] = formatLastModified(dateString);
        return [longDate, shortDate, shortFormatted, dateTimeFormatted, timeFormatted]
            .filter(Boolean)
            .join(' ');
    };

    // Large file protection: bypass inline preview beyond threshold
    const LARGE_FILE_PREVIEW_LIMIT_BYTES = 500 * 1024 * 1024; // 500 MB
    const isTooLargeForInline = (file) => {
        if (!file) return false;
        const raw = typeof file.size === 'string' ? parseInt(file.size, 10) : file.size;
        return !!raw && raw > LARGE_FILE_PREVIEW_LIMIT_BYTES;
    };

    // Convert HEIC files for preview display
    const convertHeicFileForPreview = async (signedUrl, fileKey) => {
        // Check cache first
        if (heicConversionCache.has(fileKey)) {
            return heicConversionCache.get(fileKey);
        }

        setHeicConversionState({ 
            isConverting: true, 
            currentFile: fileKey, 
            conversionError: null 
        });

        try {
            // Fetch the HEIC file as a blob
            const response = await fetch(signedUrl);
            if (!response.ok) {
                throw new Error('Failed to fetch HEIC file');
            }

            const heicBlob = await response.blob();

            // Convert HEIC to JPEG
            const jpegBlob = await heic2any({
                blob: heicBlob,
                toType: "image/jpeg",
                quality: 0.8 // Slightly lower quality for preview to save memory
            });

            // Create object URL for the converted image
            const convertedUrl = URL.createObjectURL(jpegBlob instanceof Array ? jpegBlob[0] : jpegBlob);
            
            // Cache the converted URL
            setHeicConversionCache(prev => new Map(prev.set(fileKey, convertedUrl)));

            setHeicConversionState({ 
                isConverting: false, 
                currentFile: null, 
                conversionError: null 
            });

            return convertedUrl;

        } catch (error) {
            console.error('HEIC conversion error:', error);
            setHeicConversionState({ 
                isConverting: false, 
                currentFile: null, 
                conversionError: error.message 
            });
            throw error;
        }
    };

    // Drag and drop handlers
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        setDragCounter(prev => prev + 1);
        
        // Only show overlay if we have files/folders being dragged from outside
        // Check if the drag is coming from outside the browser (external files)
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            const hasFiles = Array.from(e.dataTransfer.items).some(item => item.kind === 'file');
            if (hasFiles) {
                setIsDragOver(true);
            }
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        setDragCounter(prev => {
            const newCount = prev - 1;
            if (newCount === 0) {
                setIsDragOver(false);
            }
            return newCount;
        });
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Ensure we show the drop effect
        if (e.dataTransfer) {
            e.dataTransfer.dropEffect = 'copy';
        }
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        setIsDragOver(false);
        setDragCounter(0);

        const dataTransfer = e.dataTransfer;
        try {
            const allFiles = await collectDroppedDocumentFiles(dataTransfer);
            if (allFiles.length === 0) return;

            setMessage({
                text: `Processing ${allFiles.length} file(s) for upload...`,
                type: 'info'
            });
            await handleFileSet(allFiles);
        } catch (error) {
            console.error('Error handling dropped files:', error);
            setMessage({
                text: 'Error processing dropped files',
                type: 'error'
            });
        }
    };

    // File preview functionality
    const handleFilePreview = async (file) => {
        setSidePreviewFile(file);

        if (isTooLargeForInline(file)) {
            setSidePreview({ kind: 'large' });
            return;
        }

        if (file.type === 'folder' || file.isFolder) {
            setSidePreview({ kind: 'folder' });
            return;
        }

        const fileType = file.name.split('.').pop().toLowerCase();
        setSidePreview({ kind: 'loading' });

        try {
            const fileIdentifier = file.key || file.file_path || file.path || file.name;

            console.log('🎬 handleFilePreview debugging:', {
                fileName: file.name,
                fileType: fileType,
                fileIdentifier: fileIdentifier,
                fileObject: file,
                availableIdentifiers: {
                    id: file.id,
                    key: file.key,
                    file_path: file.file_path,
                    path: file.path,
                    name: file.name
                }
            });

            if (fileType === "pdf") {
                const response = await fetch(`https://api.casedb.co/pdf-proxy.php?fileKey=${encodeURIComponent(fileIdentifier)}&cases=true`);
                if (!response.ok) {
                    throw new Error(`Failed to load PDF: ${response.status} ${response.statusText}`);
                }

                let pageCount = null;
                try {
                    const pageCountResponse = await fetch(`https://api.casedb.co/get-signed-url.php?key=${encodeURIComponent(fileIdentifier)}&cases=true&include_page_count=true`);
                    const pageCountData = await pageCountResponse.json();
                    if (pageCountData.success && pageCountData.page_count !== undefined) {
                        pageCount = pageCountData.page_count;
                    }
                } catch (error) {
                    console.warn('Failed to fetch page count:', error);
                }

                const contentType = response.headers.get('Content-Type');
                if (contentType && contentType.includes('application/pdf')) {
                    const blob = await response.blob();
                    const url = URL.createObjectURL(blob);
                    setSidePreview({ kind: 'pdf', sourceType: 'url', url, pageCount });
                } else {
                    const result = await response.json();
                    if (result.error) throw new Error(result.error);
                    if (result.type === 'base64') {
                        setSidePreview({ kind: 'pdf', sourceType: 'base64', data: result.data, pageCount });
                    }
                }
                return;
            }

            if (fileType === 'msg' || fileType === 'eml') {
                const response = await fetch(`https://api.casedb.co/email-preview.php?fileKey=${encodeURIComponent(fileIdentifier)}&cases=true`);
                const result = await response.json();
                if (!response.ok || !result.success || !result.preview) {
                    throw new Error(result.error || 'Failed to load email preview');
                }
                setSidePreview({ kind: 'email', preview: result.preview });
                return;
            }

            if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg", "heic"].includes(fileType)) {
                const response = await fetch(`https://api.casedb.co/get-signed-url.php?key=${encodeURIComponent(fileIdentifier)}&cases=true`);
                const result = await response.json();
                if (!result.success) throw new Error('Failed to get signed URL');

                if (fileType === "heic") {
                    try {
                        const convertedUrl = await convertHeicFileForPreview(result.url, fileIdentifier);
                        setSidePreview({ kind: 'image', url: convertedUrl, heicConverted: true });
                    } catch (error) {
                        setSidePreview({ kind: 'image', conversionError: error.message || 'Conversion error' });
                    }
                } else {
                    setSidePreview({ kind: 'image', url: result.url });
                }
                return;
            }

            if (["mp3", "wav", "ogg", "aac", "flac"].includes(fileType)) {
                const response = await fetch(`https://api.casedb.co/get-signed-url.php?key=${encodeURIComponent(fileIdentifier)}&cases=true`);
                const result = await response.json();
                if (!result.success) throw new Error('Failed to get signed URL');
                setSidePreview({ kind: 'audio', url: result.url });
                return;
            }

            if (["mp4", "webm", "mov"].includes(fileType)) {
                const response = await fetch(`https://api.casedb.co/get-signed-url.php?key=${encodeURIComponent(fileIdentifier)}&cases=true`);
                const result = await response.json();
                if (!result.success) throw new Error('Failed to get signed URL');
                setSidePreview({ kind: 'video', url: result.url });
                return;
            }

            if (fileType === "ics") {
                const response = await fetch(`https://api.casedb.co/get-signed-url.php?key=${encodeURIComponent(fileIdentifier)}&cases=true`);
                const result = await response.json();
                if (!result.success) throw new Error('Failed to get signed URL');
                const textResponse = await fetch(result.url);
                const text = await textResponse.text();
                const truncatedText = text.length > 1000 ? text.substring(0, 1000) + '...' : text;
                setSidePreview({ kind: 'calendar', text: truncatedText, truncated: text.length > 1000 });
                return;
            }

            if (["txt", "log", "md", "json", "xml", "csv", "html", "htm"].includes(fileType)) {
                const response = await fetch(`https://api.casedb.co/get-signed-url.php?key=${encodeURIComponent(fileIdentifier)}&cases=true&include_page_count=true`);
                const result = await response.json();
                if (!result.success) throw new Error('Failed to get signed URL');
                const textResponse = await fetch(result.url);
                const text = await textResponse.text();
                const truncatedText = text.length > 1000 ? text.substring(0, 1000) + '...' : text;
                setSidePreview({ kind: 'text', text: truncatedText, truncated: text.length > 1000 });
                return;
            }

            if (["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(fileType)) {
                const response = await fetch(`https://api.casedb.co/get-signed-url.php?key=${encodeURIComponent(fileIdentifier)}&cases=true&include_page_count=true`);
                const result = await response.json();
                if (!result.success) throw new Error('Failed to get signed URL');
                setSidePreview({ kind: 'office', url: result.office_preview_url || result.url, officePreviewUrl: result.office_preview_url, fileType });
                return;
            }

            setSidePreview({ kind: 'generic', fileType });
        } catch (error) {
            console.error('Preview error:', error);
            setSidePreview({ kind: 'error', message: error.message || 'Unknown error' });
        }
    };

    // Unified click handler for file rows/cards
    const handleFileClick = (file) => {
        markLiveItemSeen('file', file);
        if (windowWidth < 768) {
            const fileKey = file?.key || file?.file_path || file?.path || file?.id || file?.name;
            const now = Date.now();
            if (lastMobileTapRef.current.key === fileKey && now - lastMobileTapRef.current.time < 350) {
                lastMobileTapRef.current = { key: null, time: 0 };
                handleDoubleClick(file);
                return;
            }
            lastMobileTapRef.current = { key: fileKey, time: now };
            return;
        }

        if (clickTimer.current) {
            clearTimeout(clickTimer.current);
            clickTimer.current = null;
        }
        clickTimer.current = setTimeout(() => {
            handleFilePreview(file);
            clickTimer.current = null;
        }, 200); // 200ms delay for double click detection
    };

    const handleFileDoubleClick = (file) => {
        if (clickTimer.current) {
            clearTimeout(clickTimer.current);
            clickTimer.current = null;
        }
        handleDoubleClick(file);
    };

    // Folder click handler
    const handleFolderClick = (folder) => {
        markLiveItemSeen('folder', folder);
        if (windowWidth < 768) {
            const folderKey = folder?.key || folder?.name;
            const now = Date.now();
            if (lastMobileTapRef.current.key === folderKey && now - lastMobileTapRef.current.time < 350) {
                lastMobileTapRef.current = { key: null, time: 0 };
                onFolderClick && onFolderClick(folder.name);
                return;
            }
            lastMobileTapRef.current = { key: folderKey, time: now };
            return;
        }

        if (clickTimer.current) {
            clearTimeout(clickTimer.current);
            clickTimer.current = null;
        }
        if (searchQuery && searchQuery.trim() !== '') {
            setSearchQuery('');
            setSearchResults([]);
            setIsSearching(false);
            setSearchAllFiles(false);
            setSearchPagination(prev => ({
                ...prev,
                current_page: 1,
                total_pages: 0,
                total_results: 0,
                has_next: false,
                has_prev: false
            }));
            onFolderClick && onFolderClick(folder.name);
            return;
        }
        clickTimer.current = setTimeout(() => {
            handleFilePreview({ ...folder, type: 'folder', isFolder: true });
            clickTimer.current = null;
        }, 200); // 200ms delay for double click detection
    };

    const handleFolderDoubleClick = (folder) => {
        markLiveItemSeen('folder', folder);
        if (clickTimer.current) {
            clearTimeout(clickTimer.current);
            clickTimer.current = null;
        }
        onFolderClick && onFolderClick(folder.name);
    };

    const getFolderPreviewPath = (folder) => {
        const key = String(folder?.key || '');
        return key || folder?.name || '';
    };

    // Function to update file note count
    const updateFileNoteCount = useCallback((s3ObjectId, newCount) => {
        if (sidePreviewFile && sidePreviewFile.id === s3ObjectId) {
            setSidePreviewFile(prev => ({ ...prev, noteCount: newCount }));
        }
    }, [sidePreviewFile]);

    // Full file display functionality (double-click)
    const handleDoubleClick = async (file) => {
        markLiveItemSeen(file?.type === 'folder' ? 'folder' : 'file', file);
        if (file.type === 'folder') {
            onFolderClick && onFolderClick(file.name);
            return;
        }

        // Find the file ID from metadata - try multiple identifier properties
        const fileIdentifier = file.id || file.key || file.file_path || file.path || file.name;
        const fileMeta = resolveFileMetadata(file);
        
        // Debug logging for file opening issues
        console.log('handleDoubleClick debugging:', {
            file: file,
            fileIdentifier: fileIdentifier,
            metaDataCount: metaData ? metaData.length : 0,
            metaDataSample: metaData ? metaData.slice(0, 2) : null
        });
        
        // Try to find metadata by different possible matching strategies
        if (metaData && fileIdentifier) {
            console.log('Metadata match result:', {
                found: !!fileMeta,
                fileMeta,
                candidates: getFileLookupCandidates(file)
            });
        }
        
        const viewerUrl = buildFileViewerUrl({
            file,
            fileRecordId: fileMeta?.id || file.file_id || file.id,
            caseId: case_id,
        });

        if (viewerUrl) {
            console.log('Opening file viewer:', viewerUrl);
            window.open(viewerUrl, '_blank');
        } else {
            console.log('No file ID found for opening:', { file, fileMeta });
            // Fallback: still show download option for large files or files without metadata
            if (isTooLargeForInline(file)) {
                setFilePreview(
                    <div className="large-file-fullview">
                        <h3>{file.name}</h3>
                        <p>This file is {formatSize(parseInt(file.size,10))} and exceeds the inline viewing limit (500 MB). Download instead?</p>
                        <div style={{display:'flex', gap:'8px', marginTop:'12px'}}>
                            <button className="action no-size" onClick={() => handleDownload(file)}><Download size={16}/> Download</button>
                            <button className="action alt no-size" onClick={() => { setFilePreview(null); }}>&times; Close</button>
                        </div>
                    </div>
                );
            } else {
                alert('File viewer not available for this file');
            }
        }
    };

    const fetchUser = async (id) => {
        if (!id || userMap[id]) return;
    
        try {
            const response = await fetch(`https://api.casedb.co/user.php?user_id=${id}`, { credentials: 'include' });
            const data = await response.json();
    
            if (data.success && data.user) {
                setUserMap(prev => ({ ...prev, [id]: data.user.name }));
            }
        } catch (error) {
            console.error("Error fetching user:", error);
        }
    };

    const folderParts = folderName.split('/');
    const isRootFolder = !folderName || folderName === 'root' || folderName === '{{Name}}';
    const isTemplateOnlyRoot = Number(caseTypeId) === 1;
    // Folder generation is available for every case type. The API resolves the
    // case's template from case_id, including the distinct type 2 and 3 trees.
    const showGenerateFolders = isRootFolder && Number(case_id) > 0;
    const showCreateFolder = !isRootFolder || !isTemplateOnlyRoot;

    const getCurrentFolderPathForPicker = () => {
        let path = folderName || '{{Name}}';
        if (path === 'root') {
            return '{{Name}}';
        }
        if (path.startsWith('cases/')) {
            const parts = path.split('/');
            const nameIndex = parts.indexOf('{{Name}}');
            if (nameIndex >= 0) {
                path = parts.slice(nameIndex).join('/');
            } else {
                const relative = parts.slice(2).filter(Boolean).join('/');
                path = relative ? `{{Name}}/${relative}` : '{{Name}}';
            }
        }
        if (!path.startsWith('{{Name}}')) {
            path = `{{Name}}/${path.replace(/^\/+/, '')}`;
        }
        return path;
    };

    const openMoveModal = useCallback(() => {
        setMovePickerInitialPath(getCurrentFolderPathForPicker());
        setShowMoveModal(true);
    }, [getCurrentFolderPathForPicker]);

    const getItemCount = (folder) => {
        let count = 0;
    
        if (!folder || typeof folder !== 'object') return 0;
    
        for (const [key, value] of Object.entries(folder)) {
            if (key === '__files') {
                if (Array.isArray(value)) {
                    count += value.filter(file => file.name !== "(Folder)").length;
                }
            } else if (typeof value === 'object' && value !== null) {
                count += 1;
                count += getItemCount(value);
            }
        }
    
        return count;
    };

    const activeFolderRenames = useMemo(
        () => getActiveDocumentFolderRenames(folderRenames, case_id),
        [folderRenames, case_id]
    );

    const folderRenameOverlapsPath = (destinationPath, options = {}) => Boolean(
        findDocumentFolderRenameConflict(
            [destinationPath],
            folderRenames,
            case_id,
            options
        )
    );

    const folderRenameBlocksAddition = (destinationPath, operation = 'upload') => (
        folderRenameOverlapsPath(destinationPath, {
            includeAncestors: true,
            operation,
            allowSourceAdditions: true
        })
    );

    const folderRenameBlocksUploadPicker = (destinationFolderPath) => (
        folderRenameBlocksAddition(
            joinDocumentUploadPath(destinationFolderPath, '__new_upload__'),
            'upload'
        )
    );

    const preventUploadDuringFolderRename = (destinationPath) => {
        if (!folderRenameBlocksAddition(destinationPath, 'upload')) return false;

        setMessage({
            type: 'info',
            text: 'This folder is still being renamed. Wait for it to finish before selecting or uploading files here. Other folders remain available.'
        });
        return true;
    };

    const findPendingRenameUploadDestination = (destinationPath, fileItems = []) => {
        const folderRows = fileItems.filter(item => !item.file);
        if (folderRows.some(folder => getFolderLeafNameValidationError(folder.newName))) {
            return null;
        }
        const candidatePaths = folderRows.map(folder => joinDocumentUploadPath(
            destinationPath,
            resolveRenamedDocumentUploadFolderPath(
                folder.originalPath || folder.relativePath || folder.originalName,
                folderRows
            )
        ));

        fileItems.filter(item => item.file).forEach(item => {
            const pathParts = String(item.relativePath || item.originalName || '')
                .replace(/\\/g, '/')
                .split('/')
                .filter(Boolean);
            pathParts.pop();
            const relativeFolderPath = resolveRenamedDocumentUploadFolderPath(
                pathParts.join('/'),
                folderRows
            );
            const fileLeaf = String(item.newName || item.originalName || item.file?.name || 'file')
                .replace(/\\/g, '/')
                .split('/')
                .pop();
            candidatePaths.push(joinDocumentUploadPath(
                joinDocumentUploadPath(destinationPath, relativeFolderPath),
                fileLeaf
            ));
        });

        return candidatePaths.find(path => folderRenameBlocksAddition(path, 'upload')) || null;
    };

    const handleFileSet = async (files, options = {}) => {
        if (!files || files.length === 0) return;
        // Folder pickers and every drag/drop target converge here. Apply the
        // metadata-file guard again so macOS/Windows artifacts can never enter
        // the rename queue or be uploaded even if a browser bypasses traversal.
        files = filterDocumentUploadFiles(files);
        if (files.length === 0) return;
        // Re-entrancy / duplicate guard: if rename modal already showing with pending files, ignore.
        if (showRenameModal && String(uploadSelectionCaseId) === String(case_id) && !options.skipArchiveDetection) {
            console.log('⏭️ [FILESET] Ignored duplicate file set while rename modal active');
            return;
        }

        // Include the browser-relative path so same-named files selected from
        // different folders are never silently collapsed into one upload.
        const seen = new Set();
        const deduped = [];
        Array.from(files).forEach(f => {
            const key = uploadSelectionFileKey(f);
            if (!seen.has(key)) { seen.add(key); deduped.push(f); }
        });
        if (deduped.length !== files.length) {
            console.log(`🔁 [FILESET] Deduped dropped files: ${files.length} -> ${deduped.length}`);
        }
        files = deduped;

        // Capture the folder at selection time. Upload/rename dialogs and
        // archive preparation can outlive navigation, but the selected files
        // must remain bound to the destination the user dropped/picked them in.
        const selectedDestinationPath = String(
            options.destinationPath || getCurrentFolderPathForPicker()
        );
        const overlappingDestination = files
            .map(file => joinDocumentUploadPath(
                selectedDestinationPath,
                file?.webkitRelativePath || file?.relativePath || file?.name || 'file'
            ))
            .find(destinationPath => folderRenameBlocksAddition(destinationPath, 'upload'));
        if (overlappingDestination && preventUploadDuringFolderRename(overlappingDestination)) return;
        const archiveDestinationPath = selectedDestinationPath;
        // A directory upload owns its relative tree. Keep it on the established
        // upload path; intercepting its numbered parts here would flatten them
        // into the current folder and violate the user's selected destination.
        const containsDirectoryFiles = files.some(file => Boolean(file?.webkitRelativePath));
        if (ARCHIVE_IMPORT_ENABLED
            && !options.skipArchiveDetection
            && !containsDirectoryFiles
            && archiveImportDestinationIsEligible(archiveDestinationPath)) {
            const splitArchiveDetection = constrainArchiveDetectionToDestination(
                detectSplitZipArchives(files),
                archiveDestinationPath
            );
            if (splitArchiveDetection.hasSplitArchives) {
                // State updates are asynchronous, so use a ref to prevent a
                // same-tick duplicate chooser/drop event from opening the
                // archive decision twice.
                if (splitArchivePromptRef.current) {
                    console.log('⏭️ [FILESET] Ignored duplicate split archive prompt');
                    return;
                }
                if (splitArchiveStartRef.current) {
                    setMessage({
                        type: 'info',
                        text: 'Finish uploading the current document package before selecting another one.'
                    });
                    return;
                }
                splitArchivePromptRef.current = true;
                setShowUploadModal(false);
                setSplitArchiveError('');
                setSplitArchiveSelection(splitArchiveDetection);
                return;
            }
        }

        console.log('[FILESET] Received files for processing:', files.length);
        console.log('[FILESET] File names:', Array.from(files).map(f => f.name));
    
        // Close upload modal when showing rename modal
        uploadSelectionGenerationRef.current += 1;
        setShowUploadModal(false);
        setUploadDestinationPath(selectedDestinationPath);
        setUploadSelectionCaseId(case_id);
        setSkipExistingUploads(false);
        
        const fileList = [];
        const folderPaths = new Set();
    
        Array.from(files).forEach(file => {
            const originalName = file.name;
            const relativePath = file.webkitRelativePath || originalName;
            
            console.log('[FILESET] Processing file:', originalName, 'Path:', relativePath);
            
            const parts = relativePath.split('/').filter(p => p);
            const fileName = parts.pop();
            
            let currentPath = '';
            for (let part of parts) {
                currentPath += (currentPath ? '/' : '') + part;
                folderPaths.add(currentPath);
            }
    
            fileList.push({
                file,
                originalName: fileName,
                nameWithoutExt: fileName.includes('.') ? fileName.substring(0, fileName.lastIndexOf('.')) : fileName,
                extension: fileName.includes('.') ? fileName.split('.').pop() : '',
                newExtension: fileName.includes('.') ? fileName.split('.').pop() : '',
                newName: fileName.includes('.') ? fileName.substring(0, fileName.lastIndexOf('.')) : fileName,
                relativePath: relativePath
            });
        });
    
        console.log('[FILESET] Total files processed:', fileList.length);
        console.log('[FILESET] Folder paths found:', Array.from(folderPaths));
    
        const folderList = buildDocumentUploadFolderRenameRows(folderPaths);
    
        // Don't check for conflicts yet - let users rename first in the modal
        // Conflicts will be checked when they click Upload
        setFilesToRename([...folderList, ...fileList]);
        setShowRenameModal(true);
        
        // Prime upload state map
        const initialMap = {};
        fileList.forEach(f => { 
            // Only add extension if the file actually has one
            const ext = (f.newExtension || f.extension || '').trim().replace(/^\./, '');
            const uploadName = ext ? `${f.newName}.${ext}` : f.newName;
            initialMap[uploadName] = { status: 'queued', progress: 0 };
        });
        setUploadStateMap(initialMap);
    };

    const closeSplitArchiveChoice = () => {
        splitArchivePromptRef.current = false;
        setSplitArchiveStarting(false);
        setSplitArchiveError('');
        setSplitArchiveSelection(null);
    };

    const pollArchiveImport = async (jobId, trayId, importMode = 'prepare') => {
        const terminalStatuses = new Set(['completed', 'failed', 'cancelled']);
        let statusRetryCount = 0;
        let pollCount = 0;
        // The durable server job can continue after the user leaves this page.
        // Poll while the page remains open so its plain-language phase is also
        // reflected in the existing global upload tray.
        while (jobId && archivePollActiveRef.current) {
            let response;
            try {
                response = await archiveImportService.status(jobId);
                statusRetryCount = 0;
            } catch (error) {
                const retryable = !error?.status || error.status >= 500;
                if (!retryable) throw error;
                statusRetryCount += 1;
                dispatch({
                    type: 'UPDATE_UPLOAD',
                    payload: {
                        id: trayId,
                        status: 'uploading',
                        statusMessage: 'Still working in the background'
                    }
                });
                const retryDelayMs = Math.min(30000, 2000 * statusRetryCount);
                await new Promise(resolve => setTimeout(resolve, retryDelayMs));
                continue;
            }
            const job = response?.job || response?.archive_import || response || {};
            const status = String(job.status || '').toLowerCase();
            const progress = Math.max(70, Math.min(100, Number(job.progress_percent ?? job.progress ?? 70)));
            const statusMessage = job.status_message || (
                status === 'validating' ? 'Checking the document package' :
                status === 'extracting' ? 'Preparing documents' :
                status === 'publishing' ? 'Adding documents to the case' :
                status === 'completed' ? (importMode === 'archive_only' ? 'Original files uploaded' : 'Documents are ready') :
                'Waiting to prepare documents'
            );

            dispatch({
                type: 'UPDATE_UPLOAD',
                payload: {
                    id: trayId,
                    archiveJobId: String(job.job_id || job.public_id || jobId || ''),
                    archivePasswordRetry: resolveArchivePasswordRetry(job),
                    progress,
                    status: status === 'completed' ? 'complete' : status === 'failed' ? 'failed' : status === 'cancelled' ? 'cancelled' : 'uploading',
                    statusMessage,
                    error: status === 'failed' ? (job.error || job.message || job.public_error_message || 'The document package could not be prepared.') : null
                }
            });

            if (terminalStatuses.has(status)) return job;
            pollCount += 1;
            const pollDelayMs = pollCount < 20 ? 3000 : pollCount < 74 ? 10000 : 30000;
            await new Promise(resolve => setTimeout(resolve, pollDelayMs));
        }
        if (!archivePollActiveRef.current) return null;
        throw new Error('CaseDB did not return an archive job ID.');
    };

    const startSplitArchiveJobs = async ({ archiveGroups = [], destinationPath = '', unrelatedFiles = [], importMode = 'prepare', password = '' } = {}) => {
        if (splitArchiveStartRef.current || archiveGroups.length === 0) return;
        splitArchiveStartRef.current = true;
        setSplitArchiveStarting(true);
        setSplitArchiveError('');
        // Use the folder captured when the browser detected the archive. A
        // multi-part upload can take hours, and navigation after the prompt
        // must never redirect either the originals or extracted documents.
        const archiveDestinationPath = String(destinationPath || '');
        if (!archiveDestinationPath || !archiveImportDestinationIsEligible(archiveDestinationPath)) {
            splitArchiveStartRef.current = false;
            setSplitArchiveStarting(false);
            setMessage({
                type: 'error',
                text: 'The selected upload folder is no longer available. Choose the files again.'
            });
            return;
        }

        // The decision is complete. Move long-running uploads into the global
        // tray instead of keeping the modal over the case for several hours.
        splitArchivePromptRef.current = false;
        setSplitArchiveSelection(null);
        setSplitArchiveStarting(false);

        const backgroundPolls = [];
        try {
            if (unrelatedFiles.length > 0) {
                await handleFileSet(unrelatedFiles, {
                    skipArchiveDetection: true,
                    destinationPath: archiveDestinationPath
                });
            }

            for (const group of archiveGroups) {
                const trayId = `archive-import-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
                const totalBytes = group.files.reduce((sum, file) => sum + Number(file?.size || 0), 0);
                const loadedByFile = new Map();
                const extractionFolderName = group.extractionFolderName || group.exactFileNames?.[0] || group.basename;
                dispatch({
                    type: 'ADD_UPLOAD',
                    payload: {
                        id: trayId,
                        name: extractionFolderName,
                        fileName: extractionFolderName,
                        caseName,
                        caseId: case_id,
                        archiveDestinationPath,
                        archiveImportMode: importMode,
                        operation: 'archive_import',
                        progress: 0,
                        status: 'uploading',
                        statusMessage: importMode === 'archive_only' ? 'Preparing original files' : 'Preparing the secure upload',
                        totalBytes
                    }
                });

                try {
                    const queued = await archiveImportService.start({
                        caseId: case_id,
                        destinationPath: archiveDestinationPath,
                        // The extraction folder is a sibling of the untouched
                        // archive parts and uses the first part's exact name.
                        baseName: extractionFolderName,
                        files: group.files,
                        importMode,
                        password: importMode === 'prepare' ? password : '',
                        onProgress: ({ file, loaded }) => {
                            loadedByFile.set(uploadSelectionFileKey(file), Number(loaded || 0));
                            const uploadedBytes = Array.from(loadedByFile.values()).reduce((sum, value) => sum + value, 0);
                            const uploadProgress = totalBytes > 0
                                ? Math.min(69, Math.round((uploadedBytes / totalBytes) * 70))
                                : 0;
                            dispatch({
                                type: 'UPDATE_UPLOAD',
                                payload: {
                                    id: trayId,
                                    progress: uploadProgress,
                                    loadedBytes: uploadedBytes,
                                    totalBytes,
                                    statusMessage: `Uploading ${group.files.length} archive part${group.files.length === 1 ? '' : 's'}`
                                }
                            });
                        }
                    });

                    const job = queued?.job || queued?.archive_import || queued || {};
                    const jobId = job.job_id || job.public_id || queued?.job_id;
                    dispatch({
                        type: 'UPDATE_UPLOAD',
                        payload: {
                            id: trayId,
                            archiveJobId: String(jobId || ''),
                            archivePasswordRetry: null,
                            progress: 70,
                            statusMessage: importMode === 'archive_only' ? 'Saving original files' : 'Checking the document package'
                        }
                    });
                    backgroundPolls.push(
                        pollArchiveImport(jobId, trayId, importMode).catch(error => {
                            console.error('Archive import status failed:', error);
                            dispatch({
                                type: 'UPDATE_UPLOAD',
                                payload: {
                                    id: trayId,
                                    status: 'failed',
                                    error: error?.message || 'CaseDB could not check this document package.',
                                    statusMessage: 'Document package status unavailable'
                                }
                            });
                            return null;
                        })
                    );
                } catch (error) {
                    console.error('Archive import failed:', error);
                    dispatch({
                        type: 'UPDATE_UPLOAD',
                        payload: {
                            id: trayId,
                            status: 'failed',
                            error: error?.message || 'The document package could not be uploaded.',
                            statusMessage: 'Document package failed'
                        }
                    });
                    setMessage({
                        type: 'error',
                        text: error?.message || 'The document package could not be uploaded.'
                    });
                }
            }
        } finally {
            // At this point every selected package has either reached the
            // durable server queue or received a visible upload error. The
            // browser is free to accept another package while polling carries
            // on independently.
            splitArchiveStartRef.current = false;
        }

        if (backgroundPolls.length > 0) {
            Promise.allSettled(backgroundPolls).then(async results => {
                // A failed preparation can still have saved its original files
                // in the selected folder. Refresh on every fulfilled terminal
                // result so they appear without requiring a manual reload.
                const anyTerminal = results.some(result => (
                    result.status === 'fulfilled'
                    && ['completed', 'failed', 'cancelled'].includes(
                        String(result.value?.status || '').toLowerCase()
                    )
                ));
                if (!anyTerminal) return;
                if (refreshFolderTree) await refreshFolderTree();
                if (refreshCurrentFolder) await refreshCurrentFolder(1, folderName);
                if (fetchDocuments) await fetchDocuments();
            }).catch(error => {
                console.error('Unable to refresh prepared document package:', error);
            });
        }
    };

    const keepSplitArchiveOnly = (payload = {}) => startSplitArchiveJobs({
        ...payload,
        importMode: 'archive_only'
    });

    const prepareSplitArchives = (payload = {}) => startSplitArchiveJobs({
        ...payload,
        importMode: 'prepare'
    });

    // Proceed with upload without conflict checking (used after conflict resolution)
    const proceedWithUpload = async (fileItems, destinationPath = uploadDestinationPath, selection) => {
        if (!onFileUpload || !isCurrentUploadSelection(selection)) return;

        const selectedDestinationPath = String(destinationPath || '{{Name}}');
        const skipExisting = selection.skipExisting === true;
        const validFiles = fileItems.filter(item => item.file);
        const folderList = fileItems.filter(item => !item.file);
        const invalidFolder = folderList.find(folder => getFolderLeafNameValidationError(folder.newName));
        if (invalidFolder) {
            setMessage({
                type: 'error',
                text: `${invalidFolder.displayPath || invalidFolder.originalName}: ${getFolderLeafNameValidationError(invalidFolder.newName)}`
            });
            return;
        }
        const overlappingDestination = findPendingRenameUploadDestination(
            selectedDestinationPath,
            fileItems
        );
        if (overlappingDestination && preventUploadDuringFolderRename(overlappingDestination)) return;

        uploadingRef.current = true;
        activeUploadSelectionRef.current = selection;
        cancelRequestedRef.current = false;

        console.log('[UPLOAD] Proceeding with upload after conflict resolution');

        const uploadProgressByFile = new Map();
        const totalUploadBytes = validFiles.reduce(
            (total, item) => total + Number(item.file?.size || 0),
            0
        );
        
        // Close modals
        setShowRenameModal(false);
        setShowUploadModal(false);
        if (skipExisting) setMessage({ type: 'info', text: 'Checking for existing files…' });

        const resolveRenamedFolderPath = (relativeFolderPath) => (
            resolveRenamedDocumentUploadFolderPath(relativeFolderPath, folderList)
        );

        // Missing file keys create their own folder prefixes. In skip mode,
        // avoid rewriting existing folder markers or touching case timestamps
        // before the service has confirmed whether any files need uploading.
        for (let folder of (skipExisting ? [] : folderList)) {
            try {
                const folderDestination = joinDocumentUploadPath(
                    selectedDestinationPath,
                    resolveRenamedFolderPath(folder.originalPath || folder.relativePath)
                );
                console.log(`Creating folder: ${folderDestination}`);
                const result = await simpleUploadService.createFolder(
                    case_id,
                    folderDestination,
                    user_id
                );
                if (!result?.success) {
                    throw new Error(result?.message || result?.error || 'Folder creation failed');
                }
            } catch (error) {
                console.error(`Error creating folder ${folder.newName}:`, error);
            }
        }

        // One service call preserves the uploader's concurrency instead of
        // reducing every selection to a series of one-file batches.
        const uploadBatch = buildDocumentUploadBatch(fileItems);

        if (isCurrentUploadSelection(selection)) setUploadStateMap(prev => {
            const updated = { ...prev };
            uploadBatch.forEach(({ file }) => {
                updated[file.name] = { ...(updated[file.name] || {}), status: 'uploading' };
            });
            return updated;
        });

        let uploadResults = [];
        if (!cancelRequestedRef.current && uploadBatch.length > 0) {
            try {
                uploadResults = await simpleUploadService.uploadFiles(uploadBatch, {
                    caseId: case_id,
                    folderName: selectedDestinationPath,
                    userId: user_id,
                    caseName,
                    ...(skipExisting ? { skipExisting: true, confirmedExistingIndexes: selection.confirmedExistingIndexes } : {}),
                    uploadContext: { dispatch },
                    onProgress: (uploadedFile, progress) => {
                        if (!isCurrentUploadSelection(selection)) return;
                        uploadProgressByFile.set(uploadedFile, progress);
                        setUploadProgress(prev => ({ ...prev, [uploadedFile.name]: progress }));
                        if (totalUploadBytes > 0) {
                            const uploadedBytes = uploadBatch.reduce((total, { file }) => (
                                total + ((uploadProgressByFile.get(file) || 0) / 100) * Number(file.size || 0)
                            ), 0);
                            setOverallProgress(Math.min(99, Math.round((uploadedBytes / totalUploadBytes) * 100)));
                        }
                    }
                });
            } catch (error) {
                // A batch-level rejection provides no authoritative per-file
                // result. Retain those files for review, not blind re-upload.
                console.error('Unable to confirm batch upload results', error);
            }
        }

        const retrySelection = buildDocumentUploadRetrySelection(fileItems, uploadResults);
        const skippedCount = Array.isArray(uploadResults)
            ? uploadResults.slice(0, validFiles.length).filter(result => result?.status === 'fulfilled' && result.value?.skipped === true).length
            : 0;
        const uploadedCount = retrySelection.completedCount - skippedCount;
        const skipSummary = `${uploadedCount} file(s) uploaded; ${skippedCount} existing file(s) skipped.`;
        activeUploadSelectionRef.current = null;
        uploadBatch.forEach(({ file }, index) => {
            if (!isCurrentUploadSelection(selection)) return;
            const result = Array.isArray(uploadResults) ? uploadResults[index] : undefined;
            if (result?.status === 'fulfilled') {
                setUploadStateMap(prev => ({
                    ...prev,
                    [file.name]: { ...(prev[file.name] || {}), status: 'done', progress: 100 }
                }));
                return;
            }

            const error = result?.reason || new Error(
                cancelRequestedRef.current ? 'Upload canceled' : 'Upload failed'
            );
            console.error('Batch upload failed', file.name, error);
            setUploadStateMap(prev => ({
                ...prev,
                [file.name]: {
                    ...(prev[file.name] || {}),
                    status: cancelRequestedRef.current ? 'canceled' : 'error',
                    error: error.message
                }
            }));
        });
        
        if (retrySelection.unresolvedIndexes.length > 0) {
            const group = {
                id: ++unresolvedUploadIdRef.current,
                caseId: case_id,
                destinationPath: selectedDestinationPath,
                skipExisting,
                files: retrySelection.unresolvedIndexes.map(index => ({
                    ...validFiles[index],
                    destinationPath: joinDocumentUploadPath(selectedDestinationPath, uploadBatch[index].relativePath),
                })),
            };
            setUnresolvedUploadGroups(previous => [...previous, group]);
        }
        if (retrySelection.retryIndexes.length > 0 && !isCurrentUploadSelection(selection)) {
            // A late batch must not replace a new case's selection. Keep its
            // known failures available for explicit review in the source case.
            const group = {
                id: ++unresolvedUploadIdRef.current,
                caseId: case_id,
                destinationPath: selectedDestinationPath,
                skipExisting,
                retryItems: retrySelection.retryItems,
                files: retrySelection.retryIndexes.map(index => ({
                    ...validFiles[index],
                    destinationPath: joinDocumentUploadPath(selectedDestinationPath, uploadBatch[index].relativePath),
                })),
            };
            setUnresolvedUploadGroups(previous => [...previous, group]);
        } else if (retrySelection.retryIndexes.length > 0) {
            // Only failed files and their own ancestor folders belong in the
            // retry dialog. Preserve the selection's case, root and edits.
            setFilesToRename(retrySelection.retryItems);
            setUploadDestinationPath(selectedDestinationPath);
            setUploadSelectionCaseId(case_id);
            setSkipExistingUploads(skipExisting);
            setShowRenameModal(true);
        } else if (isCurrentUploadSelection(selection)) {
            setFilesToRename([]);
        }
        if (isCurrentUploadSelection(selection)
            && (retrySelection.retryIndexes.length || retrySelection.unresolvedIndexes.length)) {
            setMessage({
                type: 'error',
                text: [
                    skipExisting ? skipSummary : '',
                    retrySelection.retryIndexes.length ? `${retrySelection.retryIndexes.length} file(s) failed and remain ready to retry.` : '',
                    retrySelection.unresolvedIndexes.length ? `${retrySelection.unresolvedIndexes.length} file(s) need confirmation before retrying. Check the destination listed below; they may already have uploaded.` : '',
                ].filter(Boolean).join(' '),
            });
        } else if (skipExisting && isCurrentUploadSelection(selection)) {
            setMessage({ type: 'success', text: skipSummary });
        }
        
        try {
            if (isCurrentUploadSelection(selection)) {
                if (refreshFolderTree) {
                    await refreshFolderTree();
                } else if (refreshCurrentFolder) {
                    await refreshCurrentFolder(1, folderName);
                }
                if (isCurrentUploadSelection(selection)) fetchDocuments();
            }
        } catch (e) {
            console.error('Refresh after upload failed', e);
        } finally {
            uploadingRef.current = false;
            cancelRequestedRef.current = false;
            activeUploadSelectionRef.current = null;
            if (isCurrentUploadSelection(selection)) {
                setUploadProgress({});
                setShowUploadModal(false);
                setOverallProgress(0);
            }
        }
    };

    const uploadRenamedFiles = async () => {
        if (String(uploadSelectionCaseId) !== String(case_id)) return;
        const selection = {
            generation: uploadSelectionGenerationRef.current,
            caseId: case_id,
            skipExisting: skipExistingUploads,
            confirmedExistingIndexes: skipExistingUploads && duplicateCheckReady
                ? new Set(uploadDuplicateCheck.existing) : undefined,
        };
        if (onFileUpload && skipExistingUploads && !duplicateCheckReady) return;
        if (onFileUpload) {
            if (uploadingRef.current) {
                console.log('⏭️ [UPLOAD] Upload already in progress; ignoring new trigger');
                return;
            }

            // Use the new upload service with renamed files
            const validFiles = filesToRename.filter(item => item.file);
            const folderList = filesToRename.filter(item => !item.file);
            const invalidFolder = folderList.find(folder => getFolderLeafNameValidationError(folder.newName));
            if (invalidFolder) {
                setMessage({
                    type: 'error',
                    text: `${invalidFolder.displayPath || invalidFolder.originalName}: ${getFolderLeafNameValidationError(invalidFolder.newName)}`
                });
                return;
            }
            const overlappingDestination = findPendingRenameUploadDestination(
                uploadDestinationPath,
                filesToRename
            );
            if (overlappingDestination && preventUploadDuringFolderRename(overlappingDestination)) return;

            uploadingRef.current = true;
            cancelRequestedRef.current = false;

            console.log('[UPLOAD] Starting file upload process');
            console.log('[UPLOAD] Total files to rename:', filesToRename.length);

            console.log('[UPLOAD] Valid files for upload:', validFiles.length);
            console.log('[UPLOAD] Folders to create:', folderList.length);

            if (selection.skipExisting) {
                // The service performs a complete, fail-closed check using
                // resolved nested paths. Do not use the legacy basename-only
                // overwrite/keep-both check for this opt-in mode.
                await proceedWithUpload([...folderList, ...validFiles], uploadDestinationPath, selection);
                return;
            }

            // Check for conflicts with the RENAMED filenames before uploading
            const fileNames = validFiles.map(f => {
                const ext = (f.newExtension || f.extension || '').trim().replace(/^\./, '');
                return ext ? `${f.newName}.${ext}` : f.newName;
            });
            console.log('[UPLOAD] Checking for conflicts with files:', fileNames);
            
            const conflictCheck = await checkFileConflicts(fileNames, uploadDestinationPath);
            if (!isCurrentUploadSelection(selection)) {
                uploadingRef.current = false;
                return;
            }
            
            if (conflictCheck.hasConflicts) {
                // Stop the upload and show conflict resolution modal
                uploadingRef.current = false;
                setConflictFiles(conflictCheck.conflicts);
                setPendingOperation({
                    type: 'upload',
                    fileList: validFiles,
                    folderList: folderList,
                    destinationPath: uploadDestinationPath,
                    selection,
                    suggestions: conflictCheck.suggestions
                });
                setShowConflictModal(true);
                return;
            }
            
            // No conflicts, proceed with upload
            console.log('[UPLOAD] No conflicts detected, proceeding with upload');
            await proceedWithUpload([...folderList, ...validFiles], uploadDestinationPath, selection);
            return;
        }

        // Fallback to old upload method if new service not available
        const validFiles = filesToRename.filter(item => item.file);
        const folderList = filesToRename.filter(item => !item.file);
        const invalidFolder = folderList.find(folder => getFolderLeafNameValidationError(folder.newName));
        if (invalidFolder) {
            setMessage({
                type: 'error',
                text: `${invalidFolder.displayPath || invalidFolder.originalName}: ${getFolderLeafNameValidationError(invalidFolder.newName)}`
            });
            return;
        }
        const overlappingDestination = findPendingRenameUploadDestination(
            uploadDestinationPath,
            filesToRename
        );
        if (overlappingDestination && preventUploadDuringFolderRename(overlappingDestination)) return;
        setUploadWaiting(true);

        for (let folder of folderList) {
            try {
                const folderDestination = joinDocumentUploadPath(
                    uploadDestinationPath,
                    resolveRenamedDocumentUploadFolderPath(
                        folder.originalPath || folder.relativePath,
                        folderList
                    )
                );
                const result = await simpleUploadService.createFolder(case_id, folderDestination, user_id);
                if (!result?.success) {
                    throw new Error(result?.message || result?.error || 'Folder creation failed');
                }
            } catch (error) {
                console.error(`Error creating folder ${folder.newName}:`, error);
            }
        }

        let totalBytes = validFiles.reduce((acc, item) => acc + item.file.size, 0);

        for (let item of validFiles) {
            try {
                const ext = (item.newExtension || item.extension || '').trim().replace(/^\./, '');
                const finalFileName = ext ? `${item.newName}.${ext}` : item.newName;
                const fileWithNewName = new File(
                    [item.file],
                    finalFileName,
                    { type: item.file.type }
                );

                let relativePath = item.relativePath;
                if (relativePath.includes('/')) {
                    const pathParts = relativePath.split('/');
                    pathParts[pathParts.length - 1] = finalFileName;
                    const relativeFolderPath = resolveRenamedDocumentUploadFolderPath(
                        pathParts.slice(0, -1).join('/'),
                        folderList
                    );
                    relativePath = [relativeFolderPath, finalFileName].filter(Boolean).join('/');
                } else {
                    relativePath = finalFileName;
                }

                await uploadFileInChunks(
                    fileWithNewName,
                    {
                        caseId: case_id,
                        folderName: uploadDestinationPath,
                        userId: user_id,
                        relativePath
                    },
                    (progressForThisFile) => {
                        setUploadProgress(prev => {
                            const newProgress = { ...prev, [fileWithNewName.name]: progressForThisFile };
                            const totalUploaded = Object.keys(newProgress).reduce((acc, fileName) => {
                                const prog = newProgress[fileName] || 0;
                                const size = validFiles.find(f => {
                                    const ext = (f.newExtension || f.extension || '').trim().replace(/^\./, '');
                                    const name = ext ? `${f.newName}.${ext}` : f.newName;
                                    return name === fileName;
                                })?.file?.size || 0;
                                return acc + (prog / 100) * size;
                            }, 0);
                            setOverallProgress(Math.round((totalUploaded / totalBytes) * 100));
                            return newProgress;
                        });
                    }
                );
            } catch (error) {
                const ext = (item.newExtension || item.extension || '').trim().replace(/^\./, '');
                const fileLabel = ext ? `${item.newName}.${ext}` : item.newName;
                console.error(`Error uploading file ${fileLabel}:`, error);
            }
        }

        try {
            // sequential loop already awaited each upload; nothing to do here
        } finally {
            // Refresh current folder contents using the new approach
            if (refreshCurrentFolder) {
                await refreshCurrentFolder();
            } else {
                // Fallback to old approach
                fetchDocuments();
            }
            setUploadWaiting(false);
            setShowRenameModal(false);
            setFilesToRename([]);
            setUploadProgress({});
            setOverallProgress(0);
            setShowUploadModal(false);
            uploadingRef.current = false;
            if (cancelRequestedRef.current) {
                console.log('🛑 [UPLOAD] Upload session canceled by user');
            }
        }
    };

    // Poll backend assembly/status endpoint
    const uploadFileInChunks = async (file, { caseId, folderName, userId, relativePath }, onProgress) => {
        if (cancelRequestedRef.current) throw new Error('Upload canceled');
        
        try {
            // Prepare file data for the service
            const fileData = { file, fileName: file.name, relativePath };
            
            const results = await simpleUploadService.uploadFiles([fileData], {
                caseId,
                folderName,
                userId,
                onProgress: (file, progress) => onProgress(progress),
                uploadContext: { dispatch }  // Pass upload context
            });
            
            // Check the first (and only) result
            const result = results[0];
            if (result.status === 'fulfilled') {
                return { success: true, status: 'complete' };
            } else {
                throw new Error(result.reason?.message || 'Upload failed');
            }
        } catch (error) {
            if (error.message === 'Upload canceled') {
                throw error;
            }
            throw new Error(`Upload failed: ${error.message}`);
        }
    };

    const cancelAllUploads = () => {
        if (!uploadingRef.current) return;
        console.log('🛑 [UPLOAD] Cancel requested by user');
        cancelRequestedRef.current = true;
        if (activeUploadControllerRef.current) {
            try { activeUploadControllerRef.current.abort(); } catch (_) {}
        }
        setUploadStateMap(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(k => {
                if (updated[k].status === 'uploading' || updated[k].status === 'queued') {
                    updated[k] = { ...updated[k], status: 'canceled' };
                }
            });
            return updated;
        });
    };

    const createFolderOnS3 = async ({ caseId, folderName, currentPath }) => {
        if (!caseId) {
            console.error('Failed to create folder: Missing case ID');
            throw new Error('Missing case ID');
        }

        // Treat the text field as one leaf. A slash typed in a date is a
        // visible character in that folder name, not a structural separator.
        const cleanFolderName = normalizeFolderLeafName(folderName);
        const prospectiveFolderPath = buildDocumentFolderChildPath(
            currentPath || '{{Name}}',
            cleanFolderName
        );
        if (findDocumentFolderRenameConflict(
            [prospectiveFolderPath],
            folderRenames,
            case_id,
            {
                includeAncestors: true,
                operation: 'create_folder',
                allowSourceAdditions: true
            }
        )) {
            throw new Error('That folder location is still being renamed. Wait for it to finish or choose a different folder name.');
        }
        let fullPath;
        
        // If we're in the root folder ({{Name}}), create the folder directly without path prefix
        if (currentPath === '{{Name}}') {
            fullPath = cleanFolderName;
        } else if (currentPath && currentPath.trim() !== '') {
            // Remove {{Name}}/ prefix if it exists, since the API will add it
            const cleanCurrentPath = currentPath.replace(/^{{Name}}\/?/, '').replace(/^\/+|\/+$/g, '');
            fullPath = cleanCurrentPath ? `${cleanCurrentPath}/${cleanFolderName}` : cleanFolderName;
        } else {
            fullPath = cleanFolderName;
        }
        
        // Validate that we have a path after processing
        if (!fullPath || !fullPath.trim()) {
            console.error('Failed to create folder: Missing folder path');
            throw new Error('Missing folder path');
        }
        
        try {
            const result = await simpleUploadService.createFolder(
                caseId,
                fullPath,
                user_id
            );
            
            if (result.success) {
                setMessage({ text: 'Folder created successfully', type: 'success' });
                // Refresh current folder contents using the new approach
                if (refreshCurrentFolder) {
                    await refreshCurrentFolder();
                } else {
                    // Fallback to old approach
                    fetchDocuments();
                }
            } else {
                console.error('Failed to create folder:', result);
                throw new Error(result.message || 'Failed to create folder');
            }
        } catch (error) {
            console.error('Error creating folder:', error);
            throw error;
        }
    };

    const handleGenerateFolders = async () => {
        if (!case_id) {
            setMessage({ text: 'Missing case ID', type: 'error' });
            return;
        }

        try {
            const response = await fetch('https://api.casedb.co/generate-missing-case-folders.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ case_id })
            });
            const data = await response.json();

            if (!response.ok || !data?.success) {
                throw new Error(data?.message || 'Failed to generate folders');
            }

            const createdCount = data?.created || 0;
            setDeletedPrefixes([]);
            setMessage({
                text: createdCount > 0
                    ? `Generated ${createdCount} missing folder${createdCount === 1 ? '' : 's'}`
                    : 'All template folders already exist',
                type: 'success'
            });

            if (refreshCurrentFolder) {
                await refreshCurrentFolder();
            } else {
                fetchDocuments();
            }
        } catch (error) {
            setMessage({ text: error.message || 'Failed to generate folders', type: 'error' });
        }
    };

    const getDownloadUrlForFile = async (file) => {
        const downloadCaseId = String(case_id ?? '').trim();
        if (!/^[1-9]\d*$/.test(downloadCaseId)) {
            throw new Error('A valid case is required to download this file.');
        }
        // Handle different file object structures - prioritize S3 path over database ID
        const fileIdentifier = file.key || file.file_path || file.path || file.name;
        
        // Get a signed URL with attachment disposition for direct download
        // Explicit case scope preserves the exact stored key, including literal percent sequences.
        const downloadName = file.name ? `&download_name=${encodeURIComponent(file.name)}` : '';
        const signedUrlResponse = await fetch(
            `https://api.casedb.co/get-signed-url.php?key=${encodeURIComponent(fileIdentifier)}&cases=true&case_id=${encodeURIComponent(downloadCaseId)}&download=true${downloadName}`,
            { credentials: 'include' }
        );
        const signedUrlData = await signedUrlResponse.json();
        
        if (!signedUrlResponse.ok || !signedUrlData.success || !signedUrlData.url) {
            throw new Error(signedUrlData.message || 'Failed to get download URL');
        }

        return {
            url: signedUrlData.url,
            filename: signedUrlData.filename || file.name || 'download'
        };
    };

    const triggerSignedUrlDownload = ({ url, filename }, useIsolatedFrame = false) => {
        if (useIsolatedFrame) {
            const frame = document.createElement('iframe');
            frame.style.display = 'none';
            frame.src = url;
            frame.title = `download-${filename}`;
            document.body.appendChild(frame);
            setTimeout(() => {
                if (frame.parentNode) {
                    frame.parentNode.removeChild(frame);
                }
            }, 60000);
            return;
        }

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const markDownloadedFileAsRead = (file) => {
        // Try to find metadata using the same strategy as before
        const meta = resolveFileMetadata(file);
        if (meta?.new !== null && meta?.id) {
            markFileAsRead(meta.id);
        }
    };

    const handleDownload = async (file) => {
        const notificationId = beginDownloadNotification(file?.name || file?.file_name);
        try {
            const download = await getDownloadUrlForFile(file);
            triggerSignedUrlDownload(download);
            markDownloadedFileAsRead(file);
            markDownloadNotificationStarted(notificationId);
        } catch (error) {
            dismissDownloadNotification(notificationId);
            console.error("Download error:", error);
            alert(`Failed to download file: ${error.message}`);
        }
    };

    const handleEditDocument = async (file) => {
        try {
            const ext = file.name.toLowerCase().split('.').pop();
            
            // Define text-based file extensions that can be edited in browser
            const textExtensions = [
                'txt', 'md', 'markdown', 'js', 'jsx', 'ts', 'tsx', 'html', 'htm', 
                'css', 'scss', 'sass', 'json', 'xml', 'sql', 'py', 'php', 'java', 
                'c', 'cpp', 'h', 'cs', 'go', 'rb', 'sh', 'bat', 'ps1', 'yaml', 
                'yml', 'csv', 'tsv', 'svg', 'ini', 'conf', 'config', 'env', 
                'gitignore', 'gitattributes', 'readme', 'license', 'changelog', 'rtf'
            ];
            
            // Define Office file extensions
            const officeExtensions = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
            const fileIdentifier = file.key || file.file_path || file.path || file.name;
            
            if (textExtensions.includes(ext)) {
                // Use FileEditor component for text-based files
                setFileToEdit(file);
                setShowFileEditor(true);
            } else if (officeExtensions.includes(ext)) {
                // Use edit-session.php for Office files with OneDrive integration
                console.log('Opening Office file with edit session...');
                console.log('File object:', file);
                console.log('File identifier:', fileIdentifier);
                
                // Find the document ID from metadata
                const fileMeta = resolveFileMetadata(file);
                const documentId = fileMeta?.id;
                
                console.log('File metadata:', fileMeta);
                console.log('Document ID:', documentId);
                
                if (!documentId) {
                    throw new Error('Document ID not found for Office editing');
                }
                
                try {
                    // Create editing session using our edit-session.php endpoint
                    const response = await fetch(`https://api.casedb.co/edit-session.php/${documentId}/editing`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        credentials: 'include'
                    });
                    
                    const result = await response.json();
                    
                    console.log('Edit session response:', result);
                    console.log('Response status:', response.status);
                    
                    if (result.success) {
                        console.log('Edit session created:', result);
                        
                        // Check if we have Microsoft integration active
                        if (result.microsoftIntegration && result.microsoftIntegration.status === 'active') {
                            
                            const isLocalhost = window.location.hostname === 'localhost' || 
                                              window.location.hostname === '127.0.0.1' ||
                                              window.location.hostname.includes('localhost');
                            
                            const webEditUrl = result.webEditUrl || result.directUrl;
                            if (isLocalhost) {
                                // On localhost, open in browser
                                setMessage({
                                    type: 'info',
                                    text: `Opening ${file.name} in Office on the web. Protocol handlers don't work reliably on localhost.`
                                });
                                window.open(webEditUrl, '_blank');
                            } else {
                                // Production: Prefer desktop Office protocol; fallback to web
                                try {
                                    if (result.editUrl) {
                                        console.log('Opening Office desktop with URL:', result.editUrl);
                                        window.location.href = result.editUrl;
                                        setMessage({
                                            type: 'success',
                                            text: `Opening ${file.name} in the desktop Office app. Changes will sync automatically back to your case!`
                                        });
                                    } else {
                                        console.log('Opening Office on the web with URL:', webEditUrl);
                                        window.open(webEditUrl, '_blank', 'noopener');
                                        setMessage({
                                            type: 'success',
                                            text: `Opening ${file.name} in Office on the web. Changes will sync automatically back to your case!`
                                        });
                                    }
                                } catch (error) {
                                    console.error('Office open failed:', error);
                                    window.open(webEditUrl, '_blank');
                                    setMessage({
                                        type: 'info',
                                        text: `Opened ${file.name} in Office on the web. Changes will still sync automatically!`
                                    });
                                }
                            }
                            
                        } else {
                            // Microsoft integration failed, use fallback
                            console.log('Microsoft integration not active, using fallback:', result.fallbackUrl);
                            window.open(result.fallbackUrl || result.directUrl, '_blank');
                            setMessage({
                                type: 'warning',
                                text: `Opened ${file.name} for download. Upload the file back after editing.`
                            });
                        }
                        
                    } else {
                        console.error('Edit session failed:', result);
                        throw new Error(result.error || 'Failed to create editing session');
                    }
                    
                } catch (error) {
                    console.error('Edit session error:', error);
                    console.error('Error details:', error.message);
                    
                    // Fallback to simple file download
                    console.log('Falling back to direct file download...');
                    try {
                        const response = await fetch(`https://api.casedb.co/get-signed-url.php?key=${encodeURIComponent(fileIdentifier)}&cases=true`);
                        const result = await response.json();
                        
                        if (result.success) {
                            // Create a download link
                            const link = document.createElement("a");
                            link.href = result.url;
                            link.setAttribute("download", file.name);
                            link.target = "_blank";
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            
                            setMessage({
                                type: 'info',
                                text: `Downloaded ${file.name}. Open it locally in Word to edit.`
                            });
                        } else {
                            throw new Error('Failed to get file URL for download');
                        }
                        
                    } catch (fallbackError) {
                        console.error('Fallback download failed:', fallbackError);
                        setMessage({
                            type: 'error',
                            text: `Failed to open ${file.name} for editing: ${fallbackError.message}`
                        });
                    }
                }
            } else {
                // For other file types, just open in browser or download
                console.log('Opening file in browser...');
                const response = await fetch(`https://api.casedb.co/get-signed-url.php?key=${encodeURIComponent(fileIdentifier)}&cases=true`);
                const result = await response.json();
                
                if (result.success) {
                    window.open(result.url, '_blank');
                } else {
                    throw new Error('Failed to get file URL');
                }
            }
        } catch (error) {
            console.error("Edit document error:", error);
            setMessage({
                type: 'error',
                text: `Failed to open document for editing: ${error.message}`
            });
        }
    };

    // Handle saving edited file
    const handleFileSave = (file, content) => {
        console.log(`File ${file.name} saved with new content`);
        // Refresh the document list to show updated file
        if (refreshCurrentFolder) {
            refreshCurrentFolder();
        }
        // Show success message
        setMessage({
            type: 'success',
            text: `File "${file.name}" saved successfully!`
        });
    };

    // Open one conversion workflow; OCR is an option instead of a separate action.
    // initialOcr keeps older callers safe while the document menus migrate to one item.
    const handleConvertToPdf = (file, { ocrPdf = false } = {}) => {
        const fileKey = file?.key || file?.file_path || file?.path;
        if (!fileKey) {
            setMessage({
                type: 'error',
                text: 'This document does not have a file path and cannot be converted.'
            });
            return;
        }

        if (pdfConversionInFlightRef.current.has(fileKey)) {
            setMessage({
                type: 'info',
                text: `A PDF conversion is already running for "${file?.name || file?.file_name || 'this document'}".`
            });
            return;
        }

        setPdfConversionRequest({
            file: {
                ...file,
                key: fileKey,
                name: file?.name || file?.file_name || fileKey.split('/').pop()
            },
            initialOcr: Boolean(ocrPdf)
        });
    };

    const executePdfConversion = async ({ ocrPdf = false } = {}) => {
        const file = pdfConversionRequest?.file;
        if (!file?.key) return;

        if (pdfConversionInFlightRef.current.has(file.key)) {
            return;
        }

        pdfConversionInFlightRef.current.add(file.key);

        setPdfConversionRequest(null);
        const progressTracker = createPdfConversionProgressTracker(dispatch, {
            file,
            caseName,
            ocrPdf
        });

        try {
            setMessage({
                type: 'info',
                text: `Conversion started for "${file.name}". Progress will continue in the upload tray.`
            });

            await ensureCaseDbCsrfToken();
            const response = await fetch(`https://api.casedb.co/convert_to_pdf.php`, {
                method: 'POST',
                credentials: 'include',
                headers: withCsrfHeaders({
                    'Content-Type': 'application/json',
                }, 'POST'),
                body: JSON.stringify({
                    file_key: file.key,
                    case_id: case_id,
                    user_id: user_id,
                    ocr_pdf: ocrPdf
                })
            });

            progressTracker.responseReceived();
            const responseText = await response.text();
            let result = null;
            try {
                result = responseText ? JSON.parse(responseText) : null;
            } catch (parseError) {
                throw new Error(response.ok
                    ? 'The conversion service returned an invalid response.'
                    : `Conversion failed with status ${response.status}.`);
            }

            if (response.ok && result?.success) {
                progressTracker.complete(result);
                const originalName = result.original_file?.filename || file.name;
                const convertedName = result.converted_file?.filename || `${file.name}.pdf`;
                setMessage({
                    type: 'success',
                    text: `Successfully converted "${originalName}" to ${ocrPdf ? 'searchable ' : ''}PDF: "${convertedName}"`
                });

                // Refresh the document list to show the new PDF file
                if (refreshCurrentFolder) {
                    try {
                        Promise.resolve(refreshCurrentFolder()).catch((refreshError) => {
                            console.error('Unable to refresh documents after PDF conversion:', refreshError);
                        });
                    } catch (refreshError) {
                        console.error('Unable to refresh documents after PDF conversion:', refreshError);
                    }
                }
            } else {
                throw new Error(result?.error || result?.message || `Conversion failed with status ${response.status}.`);
            }
        } catch (error) {
            console.error('PDF conversion error:', error);
            progressTracker.fail(error);
            setMessage({
                type: 'error',
                text: `Failed to convert document to ${ocrPdf ? 'searchable ' : ''}PDF: ${error.message}`
            });
        } finally {
            pdfConversionInFlightRef.current.delete(file.key);
        }
    };

    const handleViewFile = (file) => {
        // Find the file ID from metadata
        const fileMeta = resolveFileMetadata(file);
        const viewerUrl = buildFileViewerUrl({
            file,
            fileRecordId: fileMeta?.id || file.file_id || file.id,
            caseId: case_id,
        });

        if (viewerUrl) {
            window.open(viewerUrl, '_blank');
        } else {
            alert('File viewer not available for this file');
        }
    };

    const handleRedactFile = (file) => {
        const fileMeta = resolveFileMetadata(file);

        if (!fileMeta && file.id) {
            window.open(`/file/${file.id}?mode=redact`, '_blank');
        } else if (fileMeta && fileMeta.id) {
            window.open(`/file/${fileMeta.id}?mode=redact`, '_blank');
        } else {
            alert('Redaction viewer not available for this file');
        }
    };

    const handleEmailPdfPreview = async (file) => {
        try {
            const fileIdentifier = file.key || file.file_path || file.path || file.name;
            const fileMeta = resolveFileMetadata(file);
            const fileId = fileMeta?.id || file.id || '';

            if (!fileIdentifier) {
                throw new Error('File path not available for email PDF preview');
            }

            setSidePreview({ kind: 'loading' });

            const params = new URLSearchParams();
            params.append('fileKey', fileIdentifier);
            params.append('cases', 'true');
            if (fileId) {
                params.append('fileId', String(fileId));
            }

            const response = await fetch(`https://api.casedb.co/email-preview-pdf.php?${params.toString()}`, {
                credentials: 'include'
            });
            const result = await response.json();

            if (!response.ok || !result?.success || !result?.url) {
                throw new Error(result?.error || 'Failed to load email PDF preview');
            }

            setSidePreview({ kind: 'pdf', sourceType: 'url', url: result.url, pageCount: null });
        } catch (error) {
            console.error('Email PDF preview error:', error);
            setSidePreview({ kind: 'error', message: error.message || 'Failed to load email PDF preview' });
        }
    };

    const handleTranscribeAudio = async (file) => {
        try {
            // Find the file ID from metadata
            const fileIdentifier = file.key || file.file_path || file.path || file.name;
            const fileMeta = resolveFileMetadata(file);

            const fileId = fileMeta?.id || file.id;
            if (!fileId) {
                setMessage({
                    type: 'error',
                    text: 'Unable to find file ID for transcription'
                });
                return;
            }

            const fileName = file.name || 'Audio File';

            // Add to upload tray with transcription operation
            const transcriptionId = `transcribe-${fileId}-${Date.now()}`;
            console.log('Adding transcription to upload tray:', {
                transcriptionId,
                fileName,
                fileId,
                dispatchExists: !!dispatch
            });
            
            dispatch({
                type: 'ADD_UPLOAD',
                payload: {
                    id: transcriptionId,
                    fileName: fileName,
                    name: fileName,
                    operation: 'transcribe',
                    status: 'uploading',
                    progress: 0,
                    fileId: fileId,
                    jobName: null
                }
            });

            console.log('Transcription added to tray, starting job...');

            // Start transcription job
            const startResponse = await fetch('https://api.casedb.co/transcribe.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    action: 'start',
                    file_id: fileId
                })
            });

            const startData = await startResponse.json();

            if (!startData.success) {
                dispatch({
                    type: 'UPDATE_UPLOAD',
                    payload: {
                        id: transcriptionId,
                        status: 'failed',
                        error: startData.error || 'Failed to start transcription'
                    }
                });
                setMessage({
                    type: 'error',
                    text: `Transcription failed: ${startData.error || 'Unknown error'}`
                });
                return;
            }

            const jobName = startData.job_name;

            // Update with job name
            console.log('Transcription job started, updating progress:', jobName);
            dispatch({
                type: 'UPDATE_UPLOAD',
                payload: {
                    id: transcriptionId,
                    jobName: jobName,
                    progress: 10
                }
            });

            // Poll for completion
            const pollInterval = setInterval(async () => {
                try {
                    const statusResponse = await fetch(
                        `https://api.casedb.co/transcribe.php?job_name=${encodeURIComponent(jobName)}&file_id=${fileId}`,
                        { credentials: 'include' }
                    );
                    const statusData = await statusResponse.json();

                    if (statusData.status === 'COMPLETED') {
                        clearInterval(pollInterval);
                        dispatch({
                            type: 'UPDATE_UPLOAD',
                            payload: {
                                id: transcriptionId,
                                status: 'complete',
                                progress: 100
                            }
                        });
                        setMessage({
                            type: 'success',
                            text: `Transcription complete for ${fileName}`
                        });
                        // Refresh to show new transcript file
                        refreshCurrentFolder();
                    } else if (statusData.status === 'FAILED') {
                        clearInterval(pollInterval);
                        dispatch({
                            type: 'UPDATE_UPLOAD',
                            payload: {
                                id: transcriptionId,
                                status: 'failed',
                                error: statusData.error || 'Transcription failed'
                            }
                        });
                        setMessage({
                            type: 'error',
                            text: `Transcription failed: ${statusData.error || 'Unknown error'}`
                        });
                    } else {
                        // Still in progress, update progress indicator
                        dispatch({
                            type: 'UPDATE_UPLOAD',
                            payload: {
                                id: transcriptionId,
                                progress: Math.min(90, (statusData.progress || 50))
                            }
                        });
                    }
                } catch (error) {
                    console.error('Error polling transcription status:', error);
                    clearInterval(pollInterval);
                    dispatch({
                        type: 'UPDATE_UPLOAD',
                        payload: {
                            id: transcriptionId,
                            status: 'failed',
                            error: 'Failed to check transcription status'
                        }
                    });
                }
            }, 5000); // Poll every 5 seconds

            // Set a timeout to stop polling after 10 minutes
            setTimeout(() => {
                clearInterval(pollInterval);
            }, 600000);

        } catch (error) {
            console.error('Transcription error:', error);
            setMessage({
                type: 'error',
                text: `Failed to start transcription: ${error.message}`
            });
        }
    };

    const handleBulkDownload = async (passedKeys = null) => {
        console.log('handleBulkDownload called with:', {
            passedKeys,
            selectedKeys,
            selectedKeysLength: selectedKeys?.length,
            passedKeysLength: passedKeys?.length
        });
        
        const keys = passedKeys || selectedKeys;
        
        console.log('handleBulkDownload - keys after fallback:', {
            keys,
            keysLength: keys?.length
        });
        
        if (keys.length === 0) {
            alert("Please select files or folders to download");
            return;
        }
    
        // If only one item is selected
        if (keys.length === 1) {
            const selectedKey = keys[0];
            
            // Check if it's a folder (ends with /) - always ZIP folders
            if (selectedKey.endsWith('/')) {
                // Folder selected - proceed directly to ZIP download
                setSelectedKeys(keys);
                setDownloadType('zip');
                setShowDownloadModal(false);
                await executeZipDownload(keys);
                return;
            }
            
            if (!selectedKey.endsWith('/')) {
                // Find the file object to get its name
                const file = Object.values(files || {}).find(f => f.key === selectedKey) ||
                             Object.values(folders).flat().find(f => f.key === selectedKey) ||
                             selectedDocumentItems.find(item => item.key === selectedKey);
                
                if (file) {
                    await handleDownload(file);
                    return;
                }
            }
        }
        
        // For multiple files/folders, show download options modal
        if (passedKeys) {
            setSelectedKeys(keys);
        }
        
        // Save the keys before showing modal to prevent them from being lost
        console.log('handleBulkDownload - Saving keys before showing modal:', keys);
        setKeysToDownload(keys);
        
        console.log('handleBulkDownload - About to show modal. Current selectedKeys:', selectedKeys);
        setShowDownloadModal(true);
    };

    const executeZipDownload = async (keysToZip = null) => {
        console.log('executeZipDownload called with:', {
            keysToZip,
            selectedKeys,
            keysToZipType: typeof keysToZip,
            selectedKeysType: typeof selectedKeys,
            selectedKeysIsArray: Array.isArray(selectedKeys)
        });
        
        try {
            // Use provided keys or fall back to selectedKeys from state
            let keys = keysToZip || selectedKeys;
            
            console.log('executeZipDownload - keys after fallback:', {
                keys,
                keysLength: keys?.length,
                keysIsArray: Array.isArray(keys)
            });
            
            if (!keys || keys.length === 0) {
                console.error('No keys for ZIP download:', { keys, keysToZip, selectedKeys });
                alert("No files selected for ZIP download");
                return;
            }
            
            // Ensure {{Name}} is present in keys for S3 compatibility
            keys = keys.map(key => {
                // If key starts with cases/{case_id}/ but doesn't have {{Name}}, add it
                const casePrefix = `cases/${case_id}/`;
                if (key.startsWith(casePrefix) && !key.includes('{{Name}}')) {
                    // Insert {{Name}}/ right after cases/{case_id}/
                    const afterCaseId = key.substring(casePrefix.length);
                    return `${casePrefix}{{Name}}/${afterCaseId}`;
                }
                return key;
            });
            
            console.log('ZIP download - keys with {{Name}} ensured:', keys);
            
            // For ZIP download, use the existing ZIP approach
            setZipProgress(0);
            
            // Track ZIP creation in global upload tray
            const zipId = `zip-${Date.now()}`;
            dispatch({
                type: 'START_UPLOAD',
                payload: {
                    id: zipId,
                    fileName: `Creating ZIP (${keys.length} items)`,
                    fileSize: 0,
                    operation: 'zip'
                }
            });
            
            // Start the ZIP creation process
            const response = await fetch('https://api.casedb.co/documents.php?zip', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    keys: keys,
                    case_id: case_id,
                    current_path: folderName
                }),
                credentials: 'include'
            });

            if (!response.ok) {
                dispatch({
                    type: 'FAIL_UPLOAD',
                    payload: {
                        id: zipId,
                        error: `HTTP error! status: ${response.status}`
                    }
                });
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            
            if (!result.success) {
                dispatch({
                    type: 'FAIL_UPLOAD',
                    payload: {
                        id: zipId,
                        error: result.message || 'Failed to start ZIP creation'
                    }
                });
                throw new Error(result.message || 'Failed to start ZIP creation');
            }

            // Start polling for ZIP status
            const taskId = result.task_id;
            setZipTaskId(taskId);
            dispatch({
                type: 'UPDATE_UPLOAD',
                payload: {
                    id: zipId,
                    cancelUrl: `https://api.casedb.co/documents.php?zip_cancel=${encodeURIComponent(taskId)}`,
                    cancelMethod: 'POST'
                }
            });
            
            const pollInterval = setInterval(async () => {
                try {
                    const statusResponse = await fetch(`https://api.casedb.co/documents.php?zip_status=${taskId}`, { credentials: 'include' });
                    const statusData = await statusResponse.json();
                    
                    if (statusData.success) {
                        const currentProgress = statusData.progress || 0;
                        const statusMessage = statusData.message || '';
                        const isFinalizing = /finalizing\s+zip/i.test(statusMessage);

                        setZipProgress(currentProgress);
                        
                        // Update progress in upload tray
                        dispatch({
                            type: 'UPDATE_PROGRESS',
                            payload: {
                                id: zipId,
                                progress: currentProgress,
                                statusMessage
                            }
                        });

                        if (isFinalizing) {
                            dispatch({
                                type: 'UPDATE_UPLOAD',
                                payload: {
                                    id: zipId,
                                    statusMessage: 'Finalizing ZIP...'
                                }
                            });
                        }

                        if (statusData.status === 'complete') {
                            clearInterval(pollInterval);
                            
                            // Mark as complete in upload tray
                            dispatch({
                                type: 'COMPLETE_UPLOAD',
                                payload: {
                                    id: zipId
                                }
                            });
                            
                            // Download the completed ZIP
                            const downloadUrl = `https://api.casedb.co/documents.php?zip_download=${taskId}`;
                            const link = document.createElement('a');
                            link.href = downloadUrl;
                            link.download = 'documents.zip';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            
                            // Clean up states
                            setZipTaskId(null);
                            setZipProgress(0);
                            
                            // Mark files as read if they're in the metadata
                            keys.forEach(key => {
                                const meta = metaData?.find(metaFile => metaFile.file_path === key);
                                if (meta?.new !== null && meta?.id) {
                                    markFileAsRead(meta.id);
                                }
                            });
                        } else if (statusData.status === 'cancelled') {
                            clearInterval(pollInterval);

                            dispatch({
                                type: 'UPDATE_UPLOAD',
                                payload: {
                                    id: zipId,
                                    status: 'cancelled',
                                    error: null
                                }
                            });

                            setZipTaskId(null);
                            setZipProgress(0);
                        } else if (statusData.status === 'error') {
                            clearInterval(pollInterval);
                            
                            // Mark as failed in upload tray
                            dispatch({
                                type: 'FAIL_UPLOAD',
                                payload: {
                                    id: zipId,
                                    error: statusData.message || 'ZIP creation failed'
                                }
                            });
                            
                            setZipTaskId(null);
                            throw new Error(statusData.message || 'ZIP creation failed');
                        }
                    } else {
                        // Continue polling even if individual status check fails
                        console.warn('Status check failed, continuing to poll...');
                    }
                } catch (error) {
                    console.error('Error checking ZIP status:', error);
                }
            }, 2000); // Poll every 2 seconds
            
            // Poll until the server reports a terminal state. Large archives may
            // spend a long time in ZipArchive::close() without changing progress.
            
        } catch (error) {
            console.error("Download error:", error);
            setZipTaskId(null);
            alert(`Failed to download files: ${error.message}`);
        }
    };

    const executeDownload = async () => {
        console.log('executeDownload called - selectedKeys:', {
            selectedKeys,
            selectedKeysLength: selectedKeys?.length,
            selectedKeysIsArray: Array.isArray(selectedKeys),
            keysToDownload,
            keysToDownloadLength: keysToDownload?.length,
            downloadType
        });
        
        setShowDownloadModal(false);
        
        // Use the saved keys from when modal was opened
        const keys = keysToDownload.length > 0 ? keysToDownload : selectedKeys;
        
        try {
            if (downloadType === 'individual') {
                const fileKeys = keys.filter(key => {
                    // Only download files, not folders
                    return !key.endsWith('/');
                });
                const filesToDownload = buildSelectedDocumentItems(fileKeys, [
                    ...selectedItemCacheRef.current.values(),
                    ...Object.values(folders).flat(), ...Object.values(files || {}),
                ]);

                if (filesToDownload.length === 0) {
                    alert("No files selected for individual download");
                    return;
                }

                const downloads = await Promise.all(filesToDownload.map(async (file) => ({
                    file,
                    download: await getDownloadUrlForFile(file)
                })));
                
                downloads.forEach(({ file, download }, index) => {
                    setTimeout(() => {
                        triggerSignedUrlDownload(download, true);
                        markDownloadedFileAsRead(file);
                    }, index * 250);
                });
                return;
            }
            
            // For ZIP download, call the extracted function with saved keys
            await executeZipDownload(keys);
            
        } catch (error) {
            console.error("Download error:", error);
            alert(`Failed to download files: ${error.message}`);
        }
    };

    const handleBulkDelete = async (del = null) => {
        if (selectedKeys.length === 0) return;

        if (del === 'confirm') {
            setDeleteModal(true);
            return;
        }

        if (del === 'delete') {
            // Close modal immediately and show progress in upload service
            setDeleteModal(false);
            setDeleteInProgress(true);

            try {
                // Pre-process keys to ensure proper format and remove duplicated paths
                const processedKeys = selectedKeys.map(key => {
                    // Remove leading slashes only; keep trailing slash so folders stay marked as folders
                    const normalizedKey = key.replace(/^\/+/, '');
                    
                    // Check if the key already contains the case and path prefix to avoid duplication
                    const casePathPattern = new RegExp(`^cases/${case_id}/{{Name}}/${folderName.replace(/^{{Name}}\//, '')}`);
                    if (casePathPattern.test(normalizedKey)) {
                        return normalizedKey;
                    }
                    
                    const casePattern = new RegExp(`^cases/${case_id}`);
                    if (casePattern.test(normalizedKey)) {
                        return normalizedKey;
                    }
                    
                    return normalizedKey;
                });

                // Use the progress-aware delete method
                console.log(`🗑️ Starting deletion of ${processedKeys.length} files with progress tracking`);
                
                const result = await simpleUploadService.deleteFilesWithProgress(
                    processedKeys, 
                    case_id, 
                    folderName,
                    { dispatch },  // Pass upload context for batch tracking
                    { caseName }
                );
                
                if (result.success) {
                    // Optimistically hide deleted folders immediately; backend refresh will follow
                    const folderPrefixes = processedKeys
                        .filter(k => typeof k === 'string' && k.trim().length > 0)
                        // Treat only folder-looking keys (end with '/' or have no extension) as prefixes
                        .filter(k => k.endsWith('/') || !k.includes('.'))
                        .map(k => k.endsWith('/') ? k : `${k}/`)
                        .filter((v, i, arr) => arr.indexOf(v) === i);
                    if (folderPrefixes.length) {
                        setDeletedPrefixes(prev => {
                            const next = new Set(prev);
                            folderPrefixes.forEach(p => {
                                if (!p) return;
                                const normalized = p.replace(/^\/+/, '');
                                next.add(normalized);

                                if (!normalized.startsWith('cases/')) {
                                    next.add(`cases/${case_id}/${normalized.replace(/^\{\{Name\}\}\/?/, '{{Name}}/')}`);
                                }

                                if (normalized.startsWith(`cases/${case_id}/`)) {
                                    next.add(normalized.replace(new RegExp(`^cases/${case_id}/`), ''));
                                }
                            });
                            return Array.from(next);
                        });
                    }

                    // If the current folder is being deleted (or is inside one), navigate to its parent to avoid landing in a deleted view
                    const currentFolderKey = folderName && folderName !== 'root'
                        ? `cases/${case_id}/${folderName.replace(/^\/+|\/$/g, '')}/`
                        : `cases/${case_id}/{{Name}}/`;
                    const isCurrentFolderDeleted = folderPrefixes.some(prefix => currentFolderKey.startsWith(prefix));

                    console.log(`🗑️ Delete operation completed: ${result.message}`);
                    
                    // Clear selection
                    setSelectedKeys([]);
                    setActiveFile([]);
                    
                    // Navigate up if needed, then refresh
                    if (isCurrentFolderDeleted) {
                        const pathParts = folderName.split('/').filter(Boolean);
                        // Drop last segment to go to parent; default to root name
                        const parentPath = pathParts.length > 1 ? pathParts.slice(0, -1).join('/') : '{{Name}}';
                        if (onBreadcrumbClick) {
                            onBreadcrumbClick(parentPath);
                        }
                    }

                    if (refreshCurrentFolder) {
                        refreshCurrentFolder();
                    } else if (fetchDocuments) {
                        fetchDocuments();
                    }
                } else {
                    console.error("Delete error:", result.message);
                    alert(`Delete error: ${result.message}`);
                }
            } catch (error) {
                console.error("Delete error:", error);
                alert(`Delete error: ${error.message}`);
            } finally {
                setDeleteInProgress(false);
            }
        }
    };

    const downloadMergedFile = (result) => {
        if (result && result.content) {
            // Convert base64 to blob and download
            const binaryString = atob(result.content);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: 'application/pdf' });
            
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = result.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }
        
        // Clear merge states
        setMergeResult(null);
        setShowMergeDownloadModal(false);
        
    // Clear selection after successful merge
        setSelectedKeys([]);
        setActiveFile([]);
    };

    // Enhanced search states for pagination and performance
    const [searchPagination, setSearchPagination] = useState({
        current_page: 1,
        total_pages: 0,
        total_results: 0,
        per_page: 50,
        has_next: false,
        has_prev: false
    });
    const [searchPerformance, setSearchPerformance] = useState(null);

    const getSearchFolderFilter = useCallback(() => {
        const explicitFolder = typeof searchFilters.folder === 'string'
            ? searchFilters.folder
            : '';
        if (explicitFolder.length > 0) {
            return explicitFolder;
        }

        if (searchScope !== 'folder') {
            return '';
        }

        if (!folderName || folderName === 'root' || folderName === '{{Name}}') {
            return '{{Name}}';
        }

        return folderName;
    }, [folderName, searchFilters.folder, searchScope]);

    const clearDocumentSearch = useCallback(() => {
        cancelActiveDocumentSearch();
        setSearchQuery('');
        setSearchResults([]);
        setSearchAllFiles(false);
        setSearchPerformance(null);
        setSearchPagination({
            current_page: 1,
            total_pages: 0,
            total_results: 0,
            per_page: 50,
            has_next: false,
            has_prev: false
        });
    }, [cancelActiveDocumentSearch]);

    const updateSearchFilter = useCallback((key, value) => {
        cancelActiveDocumentSearch();
        setSearchFilters(prev => ({
            ...prev,
            [key]: value
        }));
    }, [cancelActiveDocumentSearch]);

    const runDocumentSearch = (queryOverride = searchQuery, page = 1, resetResults = true) => {
        const trimmed = String(queryOverride || '').trim();
        if (searchScope === 'folder') {
            cancelActiveDocumentSearch();
            setSearchAllFiles(false);
            setSearchResults([]);
            setSearchPerformance(null);
            return;
        }

        if (trimmed.length >= 2) {
            searchAllCaseFiles(trimmed, page, resetResults);
        } else {
            clearDocumentSearch();
        }
    };

    // Enhanced search function with pagination and optimized backend
    const searchAllCaseFiles = async (query, page = 1, resetResults = true) => {
        clearQueuedDocumentSearch();
        if (!query.trim()) {
            cancelActiveDocumentSearch();
            setSearchResults([]);
            setSearchAllFiles(false);
            setSearchPagination({
                current_page: 1,
                total_pages: 0,
                total_results: 0,
                per_page: 50,
                has_next: false,
                has_prev: false
            });
            return;
        }

        if (searchAbortControllerRef.current) {
            try {
                searchAbortControllerRef.current.abort();
            } catch (_) {}
        }

        const requestId = ++searchRequestSeqRef.current;
        const controller = new AbortController();
        searchAbortControllerRef.current = controller;
        setIsSearching(true);
        try {
            // Use GET method for better compatibility with the optimized search API
            const searchParams = new URLSearchParams({
                case_id: case_id,
                search: query.trim(),
                search_type: safeSearchType,
                scope: searchScope,
                match_mode: searchMatchMode,
                file_type: searchFilters.fileType || '',
                date_from: searchFilters.dateFrom || '',
                date_to: searchFilters.dateTo || '',
                folder: getSearchFolderFilter(),
                semantic: searchFilters.semantic ? 'true' : 'false',
                page: page,
                limit: 50
            });
            
            const response = await fetch(`https://api.casedb.co/search-files-optimized.php?${searchParams}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                signal: controller.signal
            });

            const result = await response.json();
            if (requestId !== searchRequestSeqRef.current) return;

            if (result.success) {
                const responseFiles = Array.isArray(result.files) ? result.files : [];
                const newFiles = responseFiles
                    .filter(item => isDocumentSearchResultInCaseBoundary(
                        item,
                        result.scope_prefix
                    ));
                const rejectedBoundaryRows = newFiles.length !== responseFiles.length;
                
                if (resetResults || page === 1) {
                    setSearchResults(newFiles);
                } else {
                    // Append to existing results for "Load More" functionality
                    setSearchResults(prevResults => [...prevResults, ...newFiles]);
                }
                
                setSearchAllFiles(true);
                setSearchPagination(rejectedBoundaryRows ? {
                    current_page: page,
                    total_pages: 1,
                    total_results: newFiles.length,
                    per_page: 50,
                    has_next: false,
                    has_prev: page > 1
                } : (result.pagination || {
                    current_page: page,
                    total_pages: result.pagination?.total_pages || 0,
                    total_results: result.total_results || newFiles.length,
                    per_page: 50,
                    has_next: result.pagination?.has_next || false,
                    has_prev: result.pagination?.has_prev || false
                }));
                setSearchPerformance({
                    execution_time: result.execution_time || '0ms'
                });
                
                console.log('Search completed:', {
                    query,
                    results: newFiles.length,
                    total: result.total_results,
                    performance: result.execution_time
                });
            } else {
                console.error('Search failed:', result.message);
                // Fallback to local search if API fails
                setSearchAllFiles(false);
                showErrorMessage(`Search failed: ${result.message}`);
            }
        } catch (error) {
            if (error?.name === 'AbortError') {
                return;
            }
            if (requestId !== searchRequestSeqRef.current) return;
            console.error('Search error:', error);
            // Fallback to local search if API fails
            setSearchAllFiles(false);
            showErrorMessage('Search request failed. Please try again.');
        } finally {
            if (requestId === searchRequestSeqRef.current) {
                searchAbortControllerRef.current = null;
                setIsSearching(false);
            }
        }
    };

    const queueDocumentSearch = (query) => {
        cancelActiveDocumentSearch();
        const trimmed = String(query || '').trim();

        if (searchScope !== 'case') {
            setSearchAllFiles(false);
            setSearchResults([]);
            setSearchPerformance(null);
            return;
        }

        if (trimmed.length < 2) {
            setSearchAllFiles(false);
            setSearchResults([]);
            setSearchPerformance(null);
            setSearchPagination(prev => ({
                ...prev,
                current_page: 1,
                total_pages: 0,
                total_results: 0,
                has_next: false,
                has_prev: false
            }));
            return;
        }

        setIsSearchQueued(true);
        searchDebounceTimerRef.current = setTimeout(() => {
            searchDebounceTimerRef.current = null;
            setIsSearchQueued(false);
            searchAllCaseFiles(trimmed, 1, true);
        }, 350);
    };

    // Load more search results
    const loadMoreSearchResults = async () => {
        if (!searchAllFiles || !searchPagination.has_next || isSearching) return;
        
        const nextPage = searchPagination.current_page + 1;
        await searchAllCaseFiles(searchQuery, nextPage, false);
    };

    // Enhanced merge function with advanced options
    const handleAdvancedMerge = () => {
        if (selectedKeys.length === 0) {
            alert("Please select files or folders to merge");
            return;
        }

        const mergeSelection = buildMergeSelectionFromKeys(selectedKeys);
        const hasFolderSelection = mergeSelection.some(item => item.type === 'folder');

        if (mergeSelection.length === 0) {
            alert("Please select at least one file or folder to merge");
            return;
        }

        if (mergeSelection.length === 1 && !hasFolderSelection) {
            alert("Please select at least 2 files to merge");
            return;
        }

        setMergeSettings({
            fileName: 'merged_documents',
            targetFolder: folderName || '{{Name}}',
            selectedFiles: mergeSelection,
            fileOrder: [...mergeSelection], // Default to selected order
            ocrPdf: false
        });

        setShowAdvancedMergeModal(true);
    };

    const createMergeProgressTracker = (outputName) => {
        const mergeId = `merge_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
        const uploadId = `merge-${mergeId}`;
        let pollInterval = null;

        dispatch({
            type: 'ADD_UPLOAD',
            payload: {
                id: uploadId,
                name: `Merging ${outputName}`,
                fileName: outputName,
                operation: 'merge',
                status: 'uploading',
                progress: 1,
                statusMessage: 'Queued'
            }
        });

        const updateProgress = async () => {
            try {
                const response = await fetch(`https://api.casedb.co/merge-progress.php?merge_id=${encodeURIComponent(mergeId)}`, {
                    credentials: 'include'
                });
                const status = await response.json();
                if (!status.success) return;

                dispatch({
                    type: 'UPDATE_UPLOAD',
                    payload: {
                        id: uploadId,
                        progress: status.progress || 0,
                        statusMessage: status.message || 'Merging documents...'
                    }
                });

                if (status.status === 'complete') {
                    clearInterval(pollInterval);
                    dispatch({ type: 'COMPLETE_UPLOAD', payload: { id: uploadId } });
                } else if (status.status === 'failed') {
                    clearInterval(pollInterval);
                    dispatch({
                        type: 'FAIL_UPLOAD',
                        payload: { id: uploadId, error: status.error || status.message || 'Merge failed' }
                    });
                }
            } catch (error) {
                console.warn('Merge progress polling failed:', error);
            }
        };

        pollInterval = setInterval(updateProgress, 2000);
        updateProgress();

        return {
            mergeId,
            complete: () => {
                clearInterval(pollInterval);
                dispatch({ type: 'COMPLETE_UPLOAD', payload: { id: uploadId } });
            },
            fail: (error) => {
                clearInterval(pollInterval);
                dispatch({
                    type: 'FAIL_UPLOAD',
                    payload: { id: uploadId, error: error?.message || String(error) || 'Merge failed' }
                });
            }
        };
    };

    const handleMergeDocuments = async () => {
        if (selectedKeys.length === 0) {
            alert("Please select files or folders to merge");
            return;
        }

        const mergeSelection = buildMergeSelectionFromKeys(selectedKeys);
        const hasFolderSelection = mergeSelection.some(item => item.type === 'folder');

        if (mergeSelection.length === 0) {
            alert("Please select at least one file or folder to merge");
            return;
        }

        if (mergeSelection.length === 1 && !hasFolderSelection) {
            alert("Please select at least 2 files to merge");
            return;
        }

        const outputName = prompt("Enter name for merged document:", "merged_documents") || "merged_documents";
        const mergeTracker = createMergeProgressTracker(outputName);

        try {
            setIsMerging(true);
            setMessage({ text: "Merging documents...", type: "info" });

            await ensureCaseDbCsrfToken();
            const response = await fetch('https://api.casedb.co/merge-documents.php', {
                method: 'POST',
                credentials: 'include',
                headers: withCsrfHeaders({
                    'Content-Type': 'application/json',
                }, 'POST'),
                body: JSON.stringify({
                    file_keys: mergeSelection.map(item => item.key),
                    output_name: outputName,
                    case_id: case_id,
                    case_name: caseName,
                    target_folder: folderName || '{{Name}}',
                    merge_id: mergeTracker.mergeId,
                    cases: true // Using cases database
                })
            });

            const result = await response.json();

            if (result.success) {
                // Determine success message based on upload status
                let successMessage = "Documents merged successfully!";
                if (result.uploaded) {
                    successMessage = `Documents merged and saved to ${caseName}/merged documents!`;
                    // Refresh the current folder to show the new file
                    if (refreshCurrentFolder) {
                        setTimeout(() => refreshCurrentFolder(), 1000);
                    }
                } else if (result.upload_error) {
                    successMessage = `Documents merged! Note: ${result.upload_error}`;
                }

                // Show warnings if any files were skipped
                if (result.warning && result.skipped_files) {
                    const skippedList = result.skipped_files.map(f => f.split('/').pop()).join(', ');
                    setMessage({ 
                        text: `${result.warning}. Skipped files: ${skippedList}`, 
                        type: "warning" 
                    });
                    
                    // Clear the warning after a few seconds and show success
                    setTimeout(() => {
                        setMessage({ 
                            text: `${successMessage} (${result.processed_files}/${result.total_files} files processed)`, 
                            type: "success" 
                        });
                    }, 4000);
                } else {
                    setMessage({ text: successMessage, type: "success" });
                }
                mergeTracker.complete();

                // Only auto-download if S3 upload failed or user preference
                // For successful S3 uploads, ask if user also wants to download
                let shouldDownload = !result.uploaded;
                
                if (result.uploaded && result.content) {
                    // Show modal for download confirmation instead of using confirm()
                    setMergeResult(result);
                    setShowMergeDownloadModal(true);
                    setIsMerging(false);
                    return; // Exit here, modal will handle the download
                }

                if (shouldDownload && result.content) {
                    downloadMergedFile(result);
                } else {
                    // Just clear selection if no download needed
                    setSelectedKeys([]);
                    setActiveFile([]);
                }
                setIsMerging(false);
            } else {
                setIsMerging(false);
                mergeTracker.fail(new Error(result.message || 'Merge failed'));
                throw new Error(result.message || 'Merge failed');
            }
        } catch (error) {
            console.error("Merge error:", error);
            setMessage({ text: `Failed to merge documents: ${error.message}`, type: "error" });
            mergeTracker.fail(error);
            setIsMerging(false);
        }
    };

    // Bates Stamping Functions
    const buildBatesFolderSettings = (tree, defaults) => {
        const settings = {};
        const walk = (node) => {
            if (!node || !node.path) return;
            settings[node.path] = {
                prefix: defaults.prefix || '',
                suffix: defaults.suffix || '',
                padding: defaults.padding || 5,
                starting_number: defaults.startNumber || 1
            };
            (node.children || []).forEach(walk);
        };
        (tree || []).forEach(walk);
        return settings;
    };

    const analyzeBatesSelection = async ({
        folderKeys = [],
        fileKeys = [],
        orderedFolderKeys = [],
        fallbackFiles = []
    }) => {
        const requestedFolderOrder = buildBatesFolderOrder(orderedFolderKeys, fileKeys);
        const orderedFolderRequest = requestedFolderOrder.eligible
            ? requestedFolderOrder.orderedFolderKeys
            : [];
        batesAnalyzeControllerRef.current?.abort();
        const controller = new AbortController();
        const generation = batesAnalyzeGenerationRef.current + 1;
        batesAnalyzeControllerRef.current = controller;
        batesAnalyzeGenerationRef.current = generation;
        setBatesAnalyzeLoading(true);
        setBatesSelectionSnapshot(null);
        try {
            await ensureCaseDbCsrfToken();
            const response = await fetch('https://api.casedb.co/bates-stamp.php', {
                method: 'POST',
                headers: withCsrfHeaders({
                    'Content-Type': 'application/json',
                }, 'POST'),
                body: JSON.stringify({
                    action: 'analyze',
                    folder_keys: folderKeys,
                    file_keys: fileKeys,
                    ...(orderedFolderRequest.length > 0
                        ? { ordered_folder_keys: orderedFolderRequest }
                        : {}),
                    case_id: case_id,
                    case_name: caseName
                }),
                credentials: 'include',
                signal: controller.signal
            });

            const result = await response.json();
            if (!response.ok || !result.success) {
                throw new Error(result.error || result.message || 'Failed to analyze selection');
            }
            if (generation !== batesAnalyzeGenerationRef.current) return null;
            if (!hasValidBatesSelectionSnapshot(result.selection_snapshot)) {
                throw new Error('The server did not return a usable selection snapshot. Please try again.');
            }

            let canonicalFolderOrder = null;
            if (orderedFolderRequest.length > 0) {
                const orderedAnalyzeResult = validateBatesOrderedAnalyzeResult(orderedFolderRequest, result);
                if (!orderedAnalyzeResult.valid) {
                    throw new Error('The server returned inconsistent folder-order data. Please analyze the selection again.');
                }
                canonicalFolderOrder = orderedAnalyzeResult.canonicalFolderOrder;
            }

            const inventory = normalizeBatesAnalyzeFiles(result.files, fallbackFiles);
            if (result.success) {
                setBatesAnalyzedFiles(inventory);
                setBatesSelectionSnapshot(result.selection_snapshot);
                setBatesSelection(prev => ({
                    ...prev,
                    folders: canonicalFolderOrder
                        ? canonicalFolderOrder.orderedFolderKeys
                        : prev.folders,
                    tree: result.folder_tree || [],
                    orderedFolderParent: canonicalFolderOrder?.parentKey || ''
                }));
                setBatesFolderSettings(buildBatesFolderSettings(result.folder_tree || [], batesSettings));
                const inventoryKeys = new Set(inventory.map(getBatesFileKey));
                setBatesExhibitOverrides(prev => Object.fromEntries(
                    Object.entries(prev).filter(([sourceKey]) => inventoryKeys.has(sourceKey))
                ));
                return { ...result, files: inventory };
            }
        } catch (error) {
            if (error?.name === 'AbortError' || generation !== batesAnalyzeGenerationRef.current) return null;
            console.error('Bates analyze error:', error);
            setBatesAnalyzedFiles([]);
            setBatesSelectionSnapshot(null);
            setBatesExhibitOverrides({});
            setBatesSelection(prev => ({ ...prev, tree: [], orderedFolderParent: '' }));
            setBatesFolderSettings({});
            setMessage({ text: `Failed to analyze Bates selection: ${error.message}`, type: 'error' });
            return null;
        } finally {
            if (generation === batesAnalyzeGenerationRef.current) {
                setBatesAnalyzeLoading(false);
                batesAnalyzeControllerRef.current = null;
            }
        }
    };

    const closeBatesModal = () => {
        if (batesStartGuardRef.current) return;
        batesAnalyzeGenerationRef.current += 1;
        batesAnalyzeControllerRef.current?.abort();
        batesAnalyzeControllerRef.current = null;
        setBatesAnalyzeLoading(false);
        setShowBatesModal(false);
    };

    const flattenBatesTree = (nodes = []) => {
        const result = [];
        const walk = (node) => {
            if (!node) return;
            result.push(node);
            (node.children || []).forEach(walk);
        };
        nodes.forEach(walk);
        return result;
    };

    const renderBatesFolderTree = (nodes = [], level = 0) => {
        return nodes.map(node => {
            const displayPath = (node.path || '').replace(/^{{Name}}\/?/, caseName ? `${caseName}/` : '');
            return (
                <div key={node.path} style={{ paddingLeft: level * 16, marginBottom: '6px' }}>
                    <div className="bates-folder-row">
                        <Folder size={14} />
                        <span className="bates-folder-name">{displayPath || caseName}</span>
                    </div>
                    {node.children && node.children.length > 0 && renderBatesFolderTree(node.children, level + 1)}
                </div>
            );
        });
    };

    const initializeBatesStamping = async (explicitSelectedKeys = null) => {
        if (batesProcessing || batesStartGuardRef.current) {
            setMessage({ text: 'A Bates stamping job is already running for this case.', type: 'info' });
            return;
        }
        const selectedKeysForRun = Array.from(new Set(
            (Array.isArray(explicitSelectedKeys) ? explicitSelectedKeys : selectedKeys)
                .filter(key => typeof key === 'string' && key !== '')
        ));
        if (selectedKeysForRun.length === 0) {
            alert('Please select one or more files/folders to Bates stamp.');
            return;
        }

        const normalizedKeys = uniqueDocumentSelectionKeys(selectedKeysForRun);
        const folderKeys = normalizedKeys.filter(k => k.endsWith('/'));
        const fileKeys = normalizedKeys.filter(k => !k.endsWith('/'));
        const initialFolderOrder = buildBatesFolderOrder(folderKeys, fileKeys);
        const orderedFolderKeys = initialFolderOrder.eligible
            ? initialFolderOrder.orderedFolderKeys
            : [];

        if (folderKeys.length === 0 && fileKeys.length === 0) {
            alert('Please select one or more files/folders to Bates stamp.');
            return;
        }
        if (folderKeys.length > 0 && fileKeys.length === 0 && !initialFolderOrder.eligible) {
            setMessage({
                text: 'To set the Bates processing order, select only sibling folders from one parent folder.',
                type: 'error'
            });
            return;
        }

        const initialFiles = fileKeys.map(key => {
            const item = selectedDocumentItems.find(item => item.key === key);
            return {
                id: key,
                key: key,
                name: item?.name || key.split('/').pop(),
                size: item?.file?.size || 0,
                last_modified: item?.last_modified,
                file_path: item?.file?.key || key
            };
        });

        batesAnalyzeGenerationRef.current += 1;
        batesAnalyzeControllerRef.current?.abort();
        setBatesFiles(initialFiles);
        setBatesAnalyzedFiles([]);
        setBatesSelectionSnapshot(null);
        setBatesSelection({
            mode: folderKeys.length > 0 ? 'folder' : 'files',
            files: initialFiles,
            folders: folderKeys,
            tree: [],
            orderedFolderParent: initialFolderOrder.parentKey
        });
        setBatesStampMode('continuous');
        setBatesMergeMode('none');
        setBatesExhibitOverrides({});
        setBatesExhibitSequence({
            format: 'PTX',
            customFormat: '',
            startNumber: 1,
            padding: 4,
            stampStyle: 'auto',
            scope: 'all'
        });
        setBatesExhibitPageLabels({
            enabled: false,
            padding: 3,
            separator: '-',
            position: 'bottom-left'
        });
        setBatesSettings(prev => ({
            ...prev,
            destination: {
                type: 'current',
                path: batesCurrentFolderPath
            },
            position: prev.pageLabelMode === 'exhibit' ? 'auto' : prev.position,
            replaceOriginals: prev.pageLabelMode === 'exhibit' ? true : prev.replaceOriginals,
            exhibitOutputNameMode: 'identifier',
            exhibitDisplayIdentifierMode: 'identifier',
            exhibitAutoPlacementPolicy: 'strict',
            preserveStructure: true,
            conflictPolicy: 'dedupe'
        }));

        setBatesStep(1);
        setShowBatesModal(true);
        await analyzeBatesSelection({
            folderKeys,
            fileKeys,
            orderedFolderKeys,
            fallbackFiles: initialFiles
        });
    };

    const addFilesToBates = async (filesToAdd) => {
        if (!filesToAdd || filesToAdd.length === 0) return;

        const newFiles = filesToAdd.reduce((result, file) => {
            const key = String(file?.file_path || file?.key || '');
            if (!key || /[\0\r\n]/.test(key)) return result;
            result.push({
                id: file?.id || key,
                key,
                source_key: key,
                name: file?.name || file?.file_name || key.split('/').pop(),
                size: Number(file?.size || file?.file_size || 0),
                last_modified: file?.last_modified || file?.uploaded_date
            });
            return result;
        }, []);

        if (newFiles.length === 0) {
            setMessage({ text: 'That document does not have a usable file path.', type: 'error' });
            return;
        }

        const byKey = new Map((batesSelection.files || []).map(file => [getBatesFileKey(file), file]));
        newFiles.forEach(file => byKey.set(getBatesFileKey(file), file));
        const nextFiles = Array.from(byKey.values());
        setBatesFiles(nextFiles);
        setBatesSelection(prev => ({ ...prev, files: nextFiles }));
        await analyzeBatesSelection({
            folderKeys: batesSelection.folders || [],
            fileKeys: nextFiles.map(getBatesFileKey),
            fallbackFiles: nextFiles
        });
    };

    const updateBatesPageLabelMode = (pageLabelMode) => {
        const nextMode = ['bates', 'document', 'exhibit'].includes(pageLabelMode) ? pageLabelMode : 'bates';
        setBatesSettings(prev => ({
            ...prev,
            pageLabelMode: nextMode,
            position: nextMode === 'exhibit'
                ? 'auto'
                : (prev.position === 'auto' ? 'bottom-right' : prev.position),
            replaceOriginals: nextMode === 'exhibit' ? true : prev.replaceOriginals,
            exhibitOutputNameMode: nextMode === 'exhibit' ? 'identifier' : prev.exhibitOutputNameMode,
            exhibitDisplayIdentifierMode: nextMode === 'exhibit'
                ? 'identifier'
                : prev.exhibitDisplayIdentifierMode,
            exhibitAutoPlacementPolicy: 'strict',
            stampRedText: nextMode === 'exhibit' ? false : prev.stampRedText,
            shrinkToMargin: nextMode === 'exhibit' ? false : prev.shrinkToMargin
        }));

        if (nextMode === 'exhibit') {
            setBatesMergeMode('none');
            setBatesStampMode(batesSelection.mode === 'folder' ? 'per-folder' : 'continuous');
        }
    };

    useEffect(() => {
        if (batesStampMode === 'continuous' && batesMergeMode === 'per-folder') {
            setBatesMergeMode('none');
        }
        if (batesStampMode === 'per-folder' && batesMergeMode === 'all') {
            setBatesMergeMode('none');
        }
        if (batesSettings.pageLabelMode === 'exhibit' && batesMergeMode !== 'none') {
            setBatesMergeMode('none');
        }
    }, [batesStampMode, batesMergeMode, batesSettings.pageLabelMode]);

    const removeBatesFile = async (fileId) => {
        const nextFiles = (batesSelection.files || []).filter(file => getBatesFileKey(file) !== fileId);
        setBatesFiles(nextFiles);
        setBatesAnalyzedFiles(prev => prev.filter(file => getBatesFileKey(file) !== fileId));
        setBatesSelection(prev => ({ ...prev, files: nextFiles }));
        setBatesExhibitOverrides(prev => {
            const next = { ...prev };
            delete next[fileId];
            return next;
        });

        if (nextFiles.length === 0 && (batesSelection.folders || []).length === 0) {
            batesAnalyzeGenerationRef.current += 1;
            batesAnalyzeControllerRef.current?.abort();
            setBatesAnalyzeLoading(false);
            setBatesAnalyzedFiles([]);
            setBatesSelectionSnapshot(null);
            return;
        }

        await analyzeBatesSelection({
            folderKeys: batesSelection.folders || [],
            fileKeys: nextFiles.map(getBatesFileKey),
            fallbackFiles: nextFiles
        });
    };

    const reorderBatesFiles = (dragIndex, hoverIndex) => {
        setBatesFiles(prev => moveBatesOrderItem(prev, dragIndex, hoverIndex));
    };

    const moveBatesFile = (fromIndex, toIndex) => {
        setBatesFiles(prev => moveBatesOrderItem(prev, fromIndex, toIndex));
    };

    const moveBatesFolder = async (fromIndex, toIndex) => {
        const currentFolderOrder = buildBatesFolderOrder(
            batesSelection.folders,
            (batesSelection.files || []).map(getBatesFileKey)
        );
        if (!currentFolderOrder.eligible) return;

        const nextFolderKeys = moveBatesOrderItem(
            currentFolderOrder.orderedFolderKeys,
            fromIndex,
            toIndex
        );
        if (nextFolderKeys === currentFolderOrder.orderedFolderKeys) return;

        setBatesSelection(prev => ({
            ...prev,
            folders: nextFolderKeys,
            orderedFolderParent: currentFolderOrder.parentKey
        }));
        setBatesSelectionSnapshot(null);
        await analyzeBatesSelection({
            folderKeys: nextFolderKeys,
            fileKeys: [],
            orderedFolderKeys: nextFolderKeys,
            fallbackFiles: []
        });
    };

    const removeBatesFolder = async (folderKey) => {
        const nextFolderKeys = (batesSelection.folders || []).filter(key => key !== folderKey);
        if (nextFolderKeys.length === (batesSelection.folders || []).length) return;

        setBatesSelection(prev => ({
            ...prev,
            folders: nextFolderKeys,
            tree: nextFolderKeys.length > 0 ? prev.tree : [],
            orderedFolderParent: nextFolderKeys.length > 0 ? prev.orderedFolderParent : ''
        }));
        setBatesSelectionSnapshot(null);

        if (nextFolderKeys.length === 0) {
            batesAnalyzeGenerationRef.current += 1;
            batesAnalyzeControllerRef.current?.abort();
            batesAnalyzeControllerRef.current = null;
            setBatesAnalyzeLoading(false);
            setBatesAnalyzedFiles([]);
            setBatesExhibitOverrides({});
            setBatesFolderSettings({});
            return;
        }

        const nextFolderOrder = buildBatesFolderOrder(nextFolderKeys, []);
        if (!nextFolderOrder.eligible) {
            setMessage({
                text: 'The remaining folders are not siblings under one parent. Close the tool and select the folders again.',
                type: 'error'
            });
            return;
        }

        await analyzeBatesSelection({
            folderKeys: nextFolderKeys,
            fileKeys: [],
            orderedFolderKeys: nextFolderOrder.orderedFolderKeys,
            fallbackFiles: []
        });
    };

    const getCurrentBatesAnalyzeRequest = () => {
        const folderKeys = batesSelection.folders || [];
        const fileKeys = (batesSelection.files || []).map(getBatesFileKey);
        const folderOrder = buildBatesFolderOrder(folderKeys, fileKeys);
        return {
            folderKeys,
            fileKeys,
            ...(folderOrder.eligible
                ? { orderedFolderKeys: folderOrder.orderedFolderKeys }
                : {}),
            fallbackFiles: batesSelection.files || []
        };
    };

    const pollBatesStatus = (taskId, uploadId, analyzeRequest) => {
        batesPollGenerationRef.current += 1;
        batesPollControllerRef.current?.abort();
        if (batesPollTimerRef.current) clearTimeout(batesPollTimerRef.current);

        const generation = batesPollGenerationRef.current;
        const controller = new AbortController();
        const caseAtStart = String(case_id);
        const deadline = Date.now() + (6 * 60 * 60 * 1000);
        const maxConsecutiveFailures = 10;
        let consecutiveFailures = 0;
        batesPollControllerRef.current = controller;
        batesActiveTasksRef.current.set(caseAtStart, {
            taskId,
            uploadId,
            analyzeRequest,
            caseId: caseAtStart
        });

        const isCurrent = () => !controller.signal.aborted
            && generation === batesPollGenerationRef.current
            && caseAtStart === batesCaseIdRef.current;
        const finish = () => {
            controller.abort();
            if (batesPollTimerRef.current) clearTimeout(batesPollTimerRef.current);
            batesPollTimerRef.current = null;
            if (batesPollControllerRef.current === controller) {
                batesPollControllerRef.current = null;
            }
            const activeTask = batesActiveTasksRef.current.get(caseAtStart);
            if (activeTask?.taskId === taskId) {
                batesActiveTasksRef.current.delete(caseAtStart);
            }
        };
        const failPolling = (message) => {
            if (!isCurrent()) return;
            finish();
            dispatch({ type: 'FAIL_UPLOAD', payload: { id: uploadId, error: message } });
            setMessage({ text: message, type: 'error' });
            setBatesProcessing(false);
        };
        const schedule = (delay) => {
            if (!isCurrent()) return;
            batesPollTimerRef.current = setTimeout(poll, delay);
        };

        const poll = async () => {
            if (!isCurrent()) return;
            if (Date.now() >= deadline) {
                failPolling('Bates task status timed out after six hours. The task was not restarted; reopen the case to review its outputs before trying again.');
                return;
            }
            try {
                const statusResponse = await fetch(`https://api.casedb.co/bates-stamp.php?bates_status=${encodeURIComponent(taskId)}`, {
                    credentials: 'include',
                    signal: controller.signal
                });
                if (!isCurrent()) return;

                if (!statusResponse.ok) {
                    if ([401, 403, 404].includes(statusResponse.status)) {
                        failPolling('Bates task status is unavailable. Reopen the tool and review the destination before starting another job.');
                        return;
                    }
                    consecutiveFailures += 1;
                    if (consecutiveFailures >= maxConsecutiveFailures) {
                        failPolling('Bates task status could not be reached after repeated attempts. The task was not restarted; review the destination before trying again.');
                        return;
                    }
                    schedule(Math.min(30000, 2000 * (2 ** Math.min(consecutiveFailures, 4))));
                    return;
                }

                const status = await statusResponse.json();
                if (!isCurrent()) return;
                consecutiveFailures = 0;
                const progress = typeof status.progress === 'number' ? status.progress : 0;

                dispatch({
                    type: 'UPDATE_UPLOAD',
                    payload: {
                        id: uploadId,
                        progress,
                        status: (status.status === 'complete' || status.status === 'partial') ? 'complete' : 'uploading'
                    }
                });

                if (status.status === 'complete' || status.status === 'partial') {
                    const result = normalizeBatesCompletionResult(status);
                    finish();
                    dispatch({ type: 'COMPLETE_UPLOAD', payload: { id: uploadId } });
                    setBatesResult(result);
                    setMessage(buildBatesCompletionNotice({
                        operationLabel: batesSettings.pageLabelMode === 'exhibit' ? 'Exhibit' : 'Bates',
                        result,
                        status: status.status
                    }));
                    if (refreshCurrentFolder && caseAtStart === batesCaseIdRef.current) {
                        refreshCurrentFolder();
                    }
                    setSelectedKeys([]);
                    setActiveFile([]);
                    setBatesStep(batesConfigStep);
                    setBatesSettings(prev => ({
                        ...prev,
                        prefix: '',
                        suffix: '',
                        startNumber: 1,
                        stampRedText: false,
                        shrinkToMargin: false
                    }));
                    setBatesProcessing(false);
                    return;
                }

                if (status.status === 'error') {
                    const operationLabel = batesSettings.pageLabelMode === 'exhibit' ? 'Exhibit stamping' : 'Bates stamping';
                    const result = status.response || null;
                    const firstWarning = Array.isArray(result?.warnings) ? result.warnings[0] : '';
                    finish();
                    if (result) setBatesResult(result);
                    if (isBatesSelectionChanged(status)) {
                        const selectionChangedMessage = status.message
                            || result?.message
                            || 'The selected files changed after analysis. Analyze the selection again.';
                        dispatch({ type: 'FAIL_UPLOAD', payload: { id: uploadId, error: selectionChangedMessage } });
                        setBatesProcessing(false);
                        setShowBatesModal(true);
                        setBatesStep(batesConfigStep);
                        setMessage({ text: `${selectionChangedMessage} Refreshing the selection now.`, type: 'warning' });
                        const refreshedSelection = await analyzeBatesSelection(analyzeRequest || getCurrentBatesAnalyzeRequest());
                        if (refreshedSelection && caseAtStart === batesCaseIdRef.current) {
                            setMessage({ text: 'The selection was refreshed. Review it, then start stamping again.', type: 'info' });
                        }
                        return;
                    }
                    dispatch({
                        type: 'FAIL_UPLOAD',
                        payload: { id: uploadId, error: status.error || firstWarning || status.message || `${operationLabel} failed` }
                    });
                    setMessage({
                        text: `${operationLabel} failed: ${status.error || firstWarning || status.message || 'The documents could not be processed.'}`,
                        type: 'error'
                    });
                    setBatesProcessing(false);
                    return;
                }

                schedule(2000);
            } catch (error) {
                if (error?.name === 'AbortError' || !isCurrent()) return;
                consecutiveFailures += 1;
                if (consecutiveFailures >= maxConsecutiveFailures) {
                    failPolling('Bates task status could not be reached after repeated attempts. The task was not restarted; review the destination before trying again.');
                    return;
                }
                schedule(Math.min(30000, 2000 * (2 ** Math.min(consecutiveFailures, 4))));
            }
        };

        poll();
        return finish;
    };
    batesResumePollRef.current = pollBatesStatus;

    const processBatesStamping = async () => {
        if (batesStartGuardRef.current) return;
        if ((batesSelection.files?.length || 0) === 0 && (batesSelection.folders?.length || 0) === 0) {
            alert("Please select files or folders to stamp");
            return;
        }
        if (!hasValidBatesSelectionSnapshot(batesSelectionSnapshot)) {
            setMessage({
                text: 'Analyze the current selection before starting Bates stamping.',
                type: 'error'
            });
            return;
        }

        const exhibitResolution = buildExhibitFileResolution(
            batesAnalyzedFiles,
            batesExhibitOverrides,
            batesSettings.exhibitStampStyle,
            batesSettings.exhibitDisplayIdentifierMode
        );
        if (batesSettings.pageLabelMode === 'exhibit' && !exhibitResolution.canStart) {
            setMessage({
                text: exhibitResolution.processable.length === 0
                    ? 'None of the selected files can be converted for exhibit stamping.'
                    : 'Every convertible file needs a valid, unique exhibit identifier before stamping.',
                type: 'error'
            });
            return;
        }
        const exhibitPageLabelPlan = buildExhibitPageLabelPlan(
            exhibitResolution,
            batesExhibitPageLabels,
            batesSettings.position
        );
        if (batesSettings.pageLabelMode === 'exhibit' && !exhibitPageLabelPlan.valid) {
            setMessage({
                text: exhibitPageLabelPlan.errors[0] || 'Review the exhibit page-label settings before stamping.',
                type: 'error'
            });
            return;
        }
        batesStartGuardRef.current = true;

        setBatesProcessing(true);
        setBatesProgress({});

        let asyncStarted = false;
        const analyzeRequest = getCurrentBatesAnalyzeRequest();

        try {
            setMessage({ text: `Starting ${batesSettings.pageLabelMode === 'exhibit' ? 'exhibit' : 'Bates'} stamping process...`, type: "info" });
            const stampRequest = {
                action: 'stamp',
                file_keys: batesSelection.files.map(getBatesFileKey),
                folder_keys: batesSelection.folders,
                ...(analyzeRequest.orderedFolderKeys?.length > 0
                    ? { ordered_folder_keys: analyzeRequest.orderedFolderKeys }
                    : {}),
                case_id: case_id,
                case_name: caseName,
                destination: batesSettings.destination,
                mode: batesStampMode,
                merge_mode: batesSettings.pageLabelMode === 'exhibit' ? 'none' : batesMergeMode,
                output_name: batesMergedName,
                preserve_structure: batesSettings.preserveStructure,
                conflict_policy: batesSettings.conflictPolicy,
                subfolder_inherit_rules: batesSubfoldersFollowRules,
                subfolder_continuous: batesContinuousSubfolders,
                async: true,
                selection_snapshot: batesSelectionSnapshot,
                default_settings: {
                    prefix: batesSettings.prefix,
                    suffix: batesSettings.suffix,
                    starting_number: batesSettings.startNumber,
                    padding: batesSettings.padding,
                    position: batesSettings.position,
                    replace_originals: batesSettings.replaceOriginals,
                    stamp_red_text: batesSettings.pageLabelMode === 'exhibit' ? false : batesSettings.stampRedText,
                    shrink_to_margin: batesSettings.pageLabelMode === 'exhibit' ? false : batesSettings.shrinkToMargin,
                    page_label_mode: batesSettings.pageLabelMode,
                    doc_label_source: batesSettings.docLabelSource,
                    exhibit_stamp_style: batesSettings.exhibitStampStyle,
                    exhibit_output_name_mode: batesSettings.pageLabelMode === 'exhibit'
                        ? batesSettings.exhibitOutputNameMode
                        : undefined,
                    exhibit_auto_placement_policy: batesSettings.pageLabelMode === 'exhibit'
                        && batesSettings.position === 'auto'
                        ? batesSettings.exhibitAutoPlacementPolicy
                        : 'strict'
                },
                exhibit_overrides: batesSettings.pageLabelMode === 'exhibit'
                    ? exhibitResolution.exhibitOverrides
                    : [],
                ...(batesSettings.pageLabelMode === 'exhibit'
                    ? { exhibit_page_labels: exhibitPageLabelPlan.payload }
                    : {}),
                folder_settings: batesFolderSettings
            };
            stampRequest.client_operation_key = await buildBatesClientOperationKey();
            await ensureCaseDbCsrfToken();
            // Do not retry a mutation after a transport failure until the
            // deployed API reserves client operation keys idempotently.
            const response = await fetch('https://api.casedb.co/bates-stamp.php', {
                method: 'POST',
                headers: withCsrfHeaders({
                    'Content-Type': 'application/json',
                }, 'POST'),
                body: JSON.stringify(stampRequest),
                credentials: 'include'
            });

            const result = await response.json();
            if (isBatesSelectionChanged(result)) {
                const selectionChangedMessage = result.error
                    || result.message
                    || 'The selected files changed after analysis. Analyze the selection again.';
                setMessage({ text: `${selectionChangedMessage} Refreshing the selection now.`, type: 'warning' });
                const refreshedSelection = await analyzeBatesSelection(analyzeRequest);
                if (refreshedSelection) {
                    setMessage({ text: 'The selection was refreshed. Review it, then start stamping again.', type: 'info' });
                }
                return;
            }
            if (result?.reason_code === 'unsafe_exhibit_destination') {
                setBatesStep(batesDestinationStep);
                setMessage({
                    text: result.error
                        || result.message
                        || 'Choose a separate destination folder for exhibit outputs.',
                    type: 'error'
                });
                return;
            }
            if (!response.ok || !result.success) {
                throw new Error(result.error || result.message || `${batesSettings.pageLabelMode === 'exhibit' ? 'Exhibit' : 'Bates'} stamping failed`);
            }

            if (result.async) {
                if (!isValidBatesTaskId(result.task_id)) {
                    throw new Error('The server returned an invalid Bates task identifier.');
                }
                dispatch({
                    type: 'ADD_UPLOAD',
                    payload: {
                        id: result.task_id,
                        fileName: `${batesSettings.pageLabelMode === 'exhibit' ? 'Exhibit' : 'Bates'} stamping (${(batesSelection.files?.length || 0) + (batesSelection.folders?.length || 0)} items)`,
                        name: batesSettings.pageLabelMode === 'exhibit' ? 'Exhibit stamping' : 'Bates stamping',
                        operation: 'bates',
                        progress: 5,
                        status: 'uploading'
                    }
                });
                setShowBatesModal(false);
                pollBatesStatus(result.task_id, result.task_id, analyzeRequest);
                asyncStarted = true;
                batesStartGuardRef.current = false;
                return;
            }

            if (result.success) {
                const completionResult = normalizeBatesCompletionResult(result);
                setBatesResult(completionResult);
                setMessage(buildBatesCompletionNotice({
                    operationLabel: batesSettings.pageLabelMode === 'exhibit' ? 'Exhibit' : 'Bates',
                    result: completionResult,
                    status: result.status || 'complete'
                }));
                setShowBatesModal(false);
                
                // Refresh the current folder to show the new folder
                if (refreshCurrentFolder) {
                    setTimeout(() => refreshCurrentFolder(), 1000);
                }
                
                // Clear selection and close modal
                setSelectedKeys([]);
                setActiveFile([]);
                setBatesStep(batesConfigStep); // Show success step

                setBatesSettings(prev => ({
                    ...prev,
                    prefix: '',
                    suffix: '',
                    startNumber: 1,
                    stampRedText: false,
                    shrinkToMargin: false
                }));
                
                // Modal already closed; keep tray updated
            }
        } catch (error) {
            console.error("Bates stamping error:", error);
            setMessage({ 
                text: `Failed to process Bates stamping: ${error.message}`, 
                type: "error" 
            });
        } finally {
            if (!asyncStarted) {
                batesStartGuardRef.current = false;
                setBatesProcessing(false);
            }
        }
    };

    const markFileAsRead = async (fileId) => {
        try {
            const response = await fetch("https://api.casedb.co/documents.php", {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    file_id: fileId,
                    action: "mark_read"
                }),
                credentials: "include"
            });
    
            const result = await response.json();
            if (!result.success) {
                console.warn("Failed to mark file as read:", result.message);
            }
        } catch (error) {
            console.error("Error marking file as read:", error);
        }
    };

    // Hot docs functionality
    const showErrorMessage = (msg) => {
        setMessage({ text: msg, type: 'error' });
        setTimeout(() => setMessage(null), 5000);
    };

    const showSuccessMessage = (msg) => {
        setMessage({ text: msg, type: 'success' });
        setTimeout(() => setMessage(null), 3000);
    };

    const toggleHotDoc = async (file, state = 'single') => {
        try {
            const fileKey = file.key || file.file_path;
            if (!fileKey) {
                showErrorMessage('File key not found');
                return;
            }

            const isCurrentlyHotDoc = hotDocsList.has(fileKey);
            const action = isCurrentlyHotDoc ? 'remove' : 'add';

            const response = await fetch('https://api.casedb.co/hot-docs.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: action,
                    fileKey: fileKey,
                    addedBy: user_id || 'system',
                    case_id: case_id
                }),
            });

            const result = await response.json();

            if (result.success) {
                // Update local state
                setHotDocsList(prev => {
                    const newSet = new Set(prev);
                    if (action === 'add') {
                        newSet.add(fileKey);
                    } else {
                        newSet.delete(fileKey);
                    }
                    return newSet;
                });

                const actionText = action === 'add' ? 'added to' : 'removed from';
                showSuccessMessage(`File ${actionText} hot docs`);
            } else {
                showErrorMessage(result.error || 'Failed to update hot docs');
            }
        } catch (error) {
            console.error('Error toggling hot doc:', error);
            showErrorMessage('Failed to update hot docs');
        }
    };

    const fetchHotDocs = async () => {
        try {
            const url = new URL('https://api.casedb.co/hot-docs.php');
            if (case_id) {
                url.searchParams.append('case_id', case_id);
            }
            
            const response = await fetch(url);
            const result = await response.json();
            
            if (result.success) {
                const hotDocsSet = new Set(result.hotDocs.map(doc => doc.file_key));
                setHotDocsList(hotDocsSet);
                console.log(`Fetched ${result.total_count || result.hotDocs.length} hot docs for case ${case_id}`);
            }
        } catch (error) {
            console.error('Error fetching hot docs:', error);
        }
    };

    const selectAllHotDocs = () => {
        const hotDocFiles = filteredItems.filter(item => 
            item.type === 'file' && hotDocsList.has(item.key || item.file_path)
        );
        const hotDocKeys = hotDocFiles.map(file => file.key);
        setSelectedKeys(hotDocKeys);
        setActiveFile(hotDocKeys);
    };

    const getFolderMeta = (subfolderName) => {
        const fullPath = `${folderName}/${subfolderName}`.replace(/^\/|\/$/g, '');
        const folderArray = folders[fullPath];
        if (!Array.isArray(folderArray)) return null;
    
        return folderArray.find(f => f.name === "(Folder)");
    };

    const performRename = async () => {
        if (!renameTarget || !newNameInput.trim() || renameSubmitting) return;

        const activeRenameTarget = renameTarget;
        const isFolder = activeRenameTarget.type === 'folder' || activeRenameTarget.is_folder;
        let finalName = newNameInput;
        let folderReservationId = null;
        let folderReservationSettled = false;
        let folderSourcePath = null;
        let folderTargetPath = null;
        const dialogOperationToken = createFolderRenameReservationId();
        const dialogIsCurrent = () => renameDialogTokenRef.current === dialogOperationToken;
        const settleFolderReservation = (success, payload = {}) => {
            if (!folderReservationId || folderReservationSettled) return;
            dispatch({
                type: 'SETTLE_FOLDER_RENAME',
                payload: {
                    id: folderReservationId,
                    success,
                    sourcePath: folderSourcePath,
                    targetPath: folderTargetPath,
                    ...payload
                }
            });
            folderReservationSettled = true;
        };

        // Files retain their extension. Folder text is always one leaf, so a
        // user-entered date such as 8/20/26 cannot accidentally become a path.
        try {
            // Debug logging to understand the renameTarget structure
            console.log('Rename Debug:', {
                renameTarget: activeRenameTarget,
                newNameInput,
                isFolder,
                originalName: activeRenameTarget.name || activeRenameTarget.file_name,
                originalKey: activeRenameTarget.key
            });

            // Handle extension preservation for files (not folders)
            if (!isFolder && (activeRenameTarget.name || activeRenameTarget.file_name)) {
                const originalName = (activeRenameTarget.name || activeRenameTarget.file_name).replace(/\/$/, "");
                console.log('Extension logic - originalName:', originalName);

                // Decode the original name to get the real extension
                const decodedOriginal = decodeFileName(originalName);

                // Use the same extension extraction logic as the modal display
                if (decodedOriginal.includes('.')) {
                    const originalExt = '.' + decodedOriginal.split('.').pop();
                    console.log('Found extension:', originalExt);
                    
                    // Always add the extension since the input field doesn't include it
                    finalName = newNameInput + originalExt;
                    console.log('Added extension. Final name:', finalName);
                } else {
                    const extInput = (newExtensionInput || '').trim().replace(/^\./, '');
                    if (extInput) {
                        finalName = newNameInput + '.' + extInput;
                        console.log('Added user-provided extension. Final name:', finalName);
                    } else {
                        console.log('No extension found in original name');
                    }
                }
            } else {
                console.log('Skipping extension logic (folder or no original name)');
            }

            const encodedFinalName = isFolder
                ? normalizeFolderLeafName(finalName)
                : encodeFileName(finalName);
            const sourcePath = isFolder
                ? normalizeDocumentCaseFolderPath(activeRenameTarget.key)
                : null;
            const sourceParentPath = isFolder
                ? sourcePath.split('/').slice(0, -1).join('/')
                : null;
            const requestedTargetPath = isFolder
                ? `${sourceParentPath || '{{Name}}'}/${encodedFinalName}`
                : null;
            folderSourcePath = sourcePath;
            folderTargetPath = requestedTargetPath;
            if (isFolder && findDocumentFolderRenameConflict(
                [sourcePath, requestedTargetPath],
                folderRenames,
                case_id,
                { includeAncestors: true, fencingOnly: false }
            )) {
                throw new Error('That folder location is already being renamed. Choose a non-overlapping folder or wait for it to finish.');
            }
            const resolveTerminalPaths = (result = {}) => ({
                sourcePath: normalizeDocumentCaseFolderPath(
                    result?.job?.old_key || result?.old_key || sourcePath
                ),
                targetPath: normalizeDocumentCaseFolderPath(
                    result?.job?.new_key || result?.new_key || requestedTargetPath
                )
            });

            console.log('Encoded final name for backend:', encodedFinalName);
            renameDialogTokenRef.current = dialogOperationToken;
            setRenameSubmitting(true);
            setRenameStatus({
                type: 'info',
                text: isFolder ? 'Requesting the folder rename...' : 'Renaming the file...'
            });

            if (isFolder) {
                folderReservationId = dialogOperationToken;
                dispatch({
                    type: 'REGISTER_FOLDER_RENAME',
                    payload: {
                        id: folderReservationId,
                        caseId: case_id,
                        sourcePath,
                        targetPath: requestedTargetPath,
                        fencing: true,
                        sourceAdditionsAllowed: false,
                        progress: 5,
                        statusMessage: 'Requesting the folder rename...'
                    }
                });
            }

            // Use the new upload service method that routes through s3_upload.php
            const result = await simpleUploadService.renameFile(
                activeRenameTarget.key,
                encodedFinalName,
                case_id,
                isFolder,
                { dispatch },  // Pass upload context
                {
                    onAccepted: (acceptedResult) => {
                        const acceptedPaths = resolveTerminalPaths(acceptedResult);
                        const acceptedPathsReservedValue = acceptedResult?.job?.paths_reserved
                            ?? acceptedResult?.paths_reserved;
                        const acceptedPathsReserved = typeof acceptedPathsReservedValue === 'boolean'
                            ? acceptedPathsReservedValue
                            : (acceptedPathsReservedValue === 1 || acceptedPathsReservedValue === '1'
                                ? true
                                : (acceptedPathsReservedValue === 0 || acceptedPathsReservedValue === '0'
                                    ? false
                                    : undefined));
                        const acceptedRename = {
                            ...acceptedPaths,
                            fencing: typeof acceptedPathsReserved === 'boolean'
                                ? acceptedPathsReserved
                                : true,
                            progress: 10,
                            statusMessage: 'Folder rename queued. You can continue working in other folders.'
                        };
                        dispatch({
                            type: 'UPDATE_FOLDER_RENAME',
                            payload: { id: folderReservationId, ...acceptedRename }
                        });
                        if (dialogIsCurrent()) {
                            setRenameStatus({ type: 'info', text: acceptedRename.statusMessage });
                        }
                    },
                    onProgress: ({
                        progress,
                        statusMessage,
                        status,
                        phase,
                        waiting,
                        processing,
                        pathsReserved,
                        sourceAdditionsAllowed
                    }) => {
                        dispatch({
                            type: 'UPDATE_FOLDER_RENAME',
                            payload: {
                                id: folderReservationId,
                                ...(Number.isFinite(progress) ? { progress } : {}),
                                ...(statusMessage ? { statusMessage } : {}),
                                ...(status ? { backendStatus: status } : {}),
                                ...(phase ? { phase } : {}),
                                ...(typeof waiting === 'boolean' ? { waiting } : {}),
                                ...(typeof processing === 'boolean' ? { processing } : {}),
                                ...(typeof pathsReserved === 'boolean'
                                    ? { pathsReserved, fencing: pathsReserved }
                                    : {}),
                                ...(typeof sourceAdditionsAllowed === 'boolean'
                                    ? { sourceAdditionsAllowed }
                                    : {})
                            }
                        });
                        if (dialogIsCurrent() && statusMessage) {
                            setRenameStatus({ type: 'info', text: statusMessage });
                        }
                    }
                }
            );

            if (result.success) {
                const folderCompletionNotice = isFolder
                    ? getDocumentFolderRenameCompletionNotice(result)
                    : null;
                if (isFolder) {
                    const terminalPaths = resolveTerminalPaths(result);
                    folderSourcePath = terminalPaths.sourcePath;
                    folderTargetPath = terminalPaths.targetPath;
                    settleFolderReservation(true, {
                        sourceKey: activeRenameTarget.key,
                        targetKey: result?.job?.new_key || result?.new_key || terminalPaths.targetPath,
                        ...terminalPaths,
                        result,
                        progress: 100,
                        statusMessage: folderCompletionNotice.statusMessage,
                        ...(folderCompletionNotice.warning
                            ? { warning: folderCompletionNotice.warning }
                            : {}),
                        ...(folderCompletionNotice.warningCode
                            ? { warningCode: folderCompletionNotice.warningCode }
                            : {})
                    });
                }

                if (!isFolder) {
                    if (refreshCurrentFolder) await refreshCurrentFolder();
                    else if (fetchDocuments) await fetchDocuments();
                }

                if (dialogIsCurrent()) {
                    // Only the operation that still owns the open dialog may
                    // clear selection/preview state. A background folder
                    // completion must not wipe a newer unrelated workflow.
                    setSelectedKeys([]);
                    setActiveFile([]);
                    setSidePreview(null);
                    setSidePreviewFile(null);
                    renameDialogTokenRef.current = null;
                    setRenameTarget(null);
                    setNewNameInput("");
                    setNewExtensionInput("");
                    setRenameStatus(null);
                    setRenameSubmitting(false);
                }
                setMessage(isFolder
                    ? folderCompletionNotice.message
                    : { type: 'success', text: 'File rename complete.' });
            } else {
                const errorMessage = result.message || result.error || 'The rename could not be completed.';
                console.error("Rename failed:", errorMessage);
                if (isFolder) {
                    const terminalPaths = resolveTerminalPaths(result);
                    folderSourcePath = terminalPaths.sourcePath;
                    folderTargetPath = terminalPaths.targetPath;
                    settleFolderReservation(false, {
                        sourceKey: activeRenameTarget.key,
                        targetKey: result?.job?.new_key || result?.new_key || terminalPaths.targetPath,
                        ...terminalPaths,
                        result,
                        error: errorMessage
                    });
                }
                if (dialogIsCurrent()) {
                    setRenameStatus({ type: 'error', text: errorMessage });
                    setRenameSubmitting(false);
                }
                setMessage({
                    type: 'error',
                    text: `${isFolder ? 'Folder' : 'File'} rename failed: ${errorMessage}`
                });
            }
        } catch (err) {
            console.error("Rename error:", err);
            const errorMessage = err?.message || 'The rename could not be completed.';
            if (isFolder && folderReservationId) {
                const failedSourcePath = folderSourcePath
                    || normalizeDocumentCaseFolderPath(activeRenameTarget.key);
                const failedTargetPath = folderTargetPath || failedSourcePath;
                folderSourcePath = failedSourcePath;
                folderTargetPath = failedTargetPath;
                settleFolderReservation(false, {
                    sourceKey: activeRenameTarget.key,
                    targetKey: failedTargetPath,
                    sourcePath: failedSourcePath,
                    targetPath: failedTargetPath,
                    error: errorMessage
                });
            }
            if (dialogIsCurrent()) {
                setRenameStatus({ type: 'error', text: errorMessage });
                setRenameSubmitting(false);
            }
            setMessage({
                type: 'error',
                text: `${isFolder ? 'Folder' : 'File'} rename failed: ${errorMessage}`
            });
        } finally {
            // `renameFile` resolves only after a queued folder job reaches a
            // terminal state. Retain that terminal record until the owning
            // Section synchronously reconciles its cache and acknowledges it.
            if (folderReservationId && !folderReservationSettled) {
                settleFolderReservation(false, {
                    error: 'The folder rename did not reach a usable terminal result.'
                });
            }
            if (dialogIsCurrent()) setRenameSubmitting(false);
        }
    };

    // Render breadcrumb navigation for nested files with folder navigation
    const renderBreadcrumbs = (folderPath, caseName) => {
        if (!folderPath) return caseName;
        
        const pathParts = folderPath.split('/').filter(part => part.trim() !== '');
        const breadcrumbs = ['{{Name}}', ...pathParts]; // Use {{Name}} to match document-breadcrumbs pattern
        
        return (
            <div className="file-breadcrumbs">
                {breadcrumbs.map((part, index) => {
                    const isLast = index === breadcrumbs.length - 1;
                    // Construct path like the document-breadcrumbs do
                    const path = breadcrumbs.slice(0, index + 1).join('/');
                    
                    return (
                        <React.Fragment key={index}>
                            {index > 0 && <span className="breadcrumb-separator"> / </span>}
                            <span 
                                className="breadcrumb-part" // Always use breadcrumb-part since all are clickable
                                style={{ 
                                    cursor: 'pointer',
                                    color: index === breadcrumbs.length - 1 ? 'var(--text-color)' : 'inherit',
                                    opacity: index === breadcrumbs.length - 1 ? 1 : 0.7
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (onBreadcrumbClick) {
                                        // All breadcrumbs are clickable in search results, including the last one
                                        onBreadcrumbClick(path);
                                        setSearchAllFiles(false);
                                        setSearchQuery('');
                                    }
                                }}
                            >
                                {part === '{{Name}}' ? caseName : part}
                            </span>
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    const authoritativePageItems = useMemo(() => {
        if (!Array.isArray(items)) return null;

        return items
            .filter(item => item?.name && item.name !== '(Folder)')
            .map(item => {
                if (item.type === 'folder') {
                    return { ...item, type: 'folder' };
                }

                return {
                    ...item,
                    type: 'file',
                    file: item,
                    size_bytes: item.size_bytes,
                    author_name: item.author_name || item.uploaded_by_name || '',
                    meta: metaData?.find(meta => meta.file_path === item.key) || {}
                };
            });
    }, [items, metaData]);

    const searchItems = searchAllFiles && searchResults.length > 0
        ?
            // Use optimized search results with enhanced metadata, sorted by relevance
            searchResults
                .map(file => ({
                    type: file.type || 'file',
                    id: file.id,
                    file_id: file.file_id,
                    folder_id: file.folder_id,
                    name: file.name,
                    key: file.key,
                    file: file.type === 'file' ? file : null,
                    last_modified: file.last_modified,
                    size: file.size,
                    folder_path: file.folder_path,
                    breadcrumbs: file.breadcrumbs || [],
                    is_nested: file.is_nested || false,
                    author_name: file.author_name,
                    note_count: file.note_count || 0,
                    extension: file.extension,
                    // Merge with existing metadata if available
                    meta: {
                        ...(metaData?.find(meta => meta.file_path === file.key) || {}),
                        author_id: file.author_id,
                        author_name: file.author_name,
                        note_count: file.note_count,
                        tags: file.tags
                    }
                }))
                .sort((a, b) => {
                    // Sort search results alphabetically by name
                    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
                })
        : safeSearchQuery
        ?
            // Regular search in current folder
            [
                ...Object.keys(folders)
                .filter(key => key !== "root")
                .map(folderPath => {
                    const meta = folders[folderPath]?.find(f => f.name === "(Folder)");
                    
                    let folderKey;
                    if (folderPath === '{{Name}}') {
                        folderKey = `cases/${case_id}/{{Name}}/`;
                    } else {
                        const relativePath = folderPath.replace(/^{{Name}}\/?/, '');
                        if (relativePath) {
                            folderKey = `cases/${case_id}/{{Name}}/${relativePath}/`;
                        } else {
                            folderKey = `cases/${case_id}/{{Name}}/`;
                        }
                    }
                    
                    return {
                    type: 'folder',
                    name: folderPath.replace(/^{{Name}}\/?/, ''),
                    key: folderKey,
                    last_modified: meta?.last_modified || null,
                    size: meta?.size !== undefined ? meta.size : Math.max((folders[folderPath]?.length || 1) - 1, 0),
                    };
                }),
                ...Object.values(folders).flat().filter(file => file.name !== "(Folder)").map(file => ({
                    type: 'file',
                    name: file.name,
                    key: file.key,
                    file: file,
                    last_modified: file.last_modified,
                    size: file.size,
                    size_bytes: file.size_bytes,
                    author_name: file.author_name || file.uploaded_by_name || '',
                    meta: metaData?.find(meta => meta.file_path === file.key) || {},
                }))
            ]
        : authoritativePageItems !== null
        ? authoritativePageItems
        :
            // No search - show current folder contents
            [
                ...Object.keys(currentFolderSubfolders).map(subfolderName => {
                    const folderData = currentFolderSubfolders[subfolderName];
                    // Use the folder data from the API response if available, or construct the full path
                    // folderName contains the current path like "{{Name}}/01 Pleadings" or "{{Name}}/Client Docs"
                    let fullPath;
                    if (folderName && folderName !== 'root' && folderName !== '' && folderName !== '{{Name}}') {
                        // Remove {{Name}}/ prefix from folderName if it exists, then add subfolderName
                        const currentPath = folderName.replace(/^{{Name}}\/?/, '');
                        if (currentPath) {
                            // Ensure currentPath ends with / and append subfolderName
                            const normalizedPath = currentPath.endsWith('/') ? currentPath : `${currentPath}/`;
                            fullPath = `${normalizedPath}${subfolderName}/`;
                        } else {
                            fullPath = `${subfolderName}/`;
                        }
                    } else {
                        fullPath = `${subfolderName}/`;
                    }
                    const constructedKey = `cases/${case_id}/{{Name}}/${fullPath}`;
                    console.log('Folder key construction:', {
                        subfolderName,
                        folderName,
                        currentPathAfterStrip: folderName.replace(/^{{Name}}\/?/, ''),
                        fullPath,
                        constructedKey,
                        folderDataKey: folderData.key
                    });

                    // If this folder (or any of its children) was just deleted, hide it until data refresh completes
                    const isDeleted = deletedPrefixes.some(prefix => constructedKey.startsWith(prefix));
                    if (isDeleted) {
                        return null;
                    }

                    // Get size from cache if original is 0 or undefined
                    const originalSize = folderData.size;
                    const folderKey = `${case_id}:${subfolderName}`;
                    let size = originalSize;
                    if ((!size || size === 0) && folderSizeCache.current.has(folderKey)) {
                        size = folderSizeCache.current.get(folderKey);
                        console.log(`Using cached size for ${subfolderName}: ${size} (original: ${originalSize})`);
                    }

                    return {
                        type: 'folder',
                        id: folderData.id,
                        folder_id: folderData.folder_id,
                        name: subfolderName,
                        key: constructedKey, // Temporarily force our constructed key
                        last_modified: folderData.last_modified || null,
                        size: size !== undefined ? size : 0 // Use cached size or fallback
                    };
                }).filter(Boolean),
                ...currentFolderFiles.filter(file => file.name !== "(Folder)").map(file => ({
                    type: 'file',
                    name: file.name,
                    key: file.key,
                    file: file,
                    last_modified: file.last_modified,
                    size: file.size,
                    size_bytes: file.size_bytes,
                    author_name: file.author_name || file.uploaded_by_name || '',
                    meta: metaData?.find(meta => meta.file_path === file.key) || {},
                }))
            ];

    const filteredItemsBeforeSort = searchItems.filter(item => {
        // When hotDocsOnly is true, filter out all folders and non-hot-doc files
        if (hotDocsOnly) {
            if (item.type === 'folder') {
                return false; // Hide all folders when hotDocsOnly is true
            }
            if (item.type === 'file') {
                const fileKey = item.key || item.file_path;
                if (!hotDocsList.has(fileKey)) {
                    return false;
                }
            }
        }

        const fileTypeFilter = String(searchFilters.fileType || '').toLowerCase();
        if (fileTypeFilter) {
            if (item.type !== 'file') return false;
            const extension = getDocumentFileExtension(item);
            if (fileTypeFilter === 'jpg') {
                if (!['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tif', 'tiff'].includes(extension)) return false;
            } else if (extension !== fileTypeFilter) {
                return false;
            }
        }

        if ((searchFilters.dateFrom || searchFilters.dateTo) && item.type === 'file') {
            const modified = item.last_modified ? new Date(item.last_modified) : null;
            if (!modified || Number.isNaN(modified.getTime())) return false;
            if (searchFilters.dateFrom) {
                const fromDate = new Date(`${searchFilters.dateFrom}T00:00:00`);
                if (!Number.isNaN(fromDate.getTime()) && modified < fromDate) return false;
            }
            if (searchFilters.dateTo) {
                const toDate = new Date(`${searchFilters.dateTo}T23:59:59`);
                if (!Number.isNaN(toDate.getTime()) && modified > toDate) return false;
            }
        }

        const folderFilter = normalizeDocumentFolderFilter(searchFilters.folder);
        if (folderFilter) {
            const itemPath = normalizeDocumentFolderFilter(getDocumentItemPath(item));
            if (!itemPath.includes(folderFilter)) return false;
        }

        if (!safeSearchQuery) return true;
    
        if (safeSearchType === "Folder" && item.type !== "folder") return false;
        // Allow both files and folders for general searches
        if (safeSearchType === "Doc Type" && item.type !== "file") return false;
        if (safeSearchType === "Author" && item.type !== "file") return false;
        if (safeSearchType === "Last Modified" && item.type !== "file") return false;
    
        let target = '';
    
        if (safeSearchType === 'Title') {
            target = item.name ?? '';
        } else if (safeSearchType === 'Author' && item.type === 'file') {
            target = userMap[item.meta?.author_id] ?? '';
        } else if (safeSearchType === 'Doc Type' && item.type === 'file') {
            target = item.name?.split('.').pop() ?? '';
        } else if (safeSearchType === 'Folder' && item.type === 'folder') {
            target = item.name ?? '';
        } else if (safeSearchType === 'Last Modified' && item.type === 'file') {
            target = formatLastModifiedSearch(item.last_modified);
        } else {
            // General search - include both files and folders
            if (item.type === 'file') {
                target = `${decodeFileName(item.name || '')} ${getDocumentItemPath(item)} ${userMap[item.meta?.author_id] ?? item.author_name ?? ''} ${formatLastModifiedSearch(item.last_modified)} ${getDocumentFileExtension(item)}`;
            } else if (item.type === 'folder') {
                target = `${decodeFileName(item.name || '')} ${getDocumentItemPath(item)} ${formatLastModifiedSearch(item.last_modified)}`;
            }
        }
        
        return matchesDocumentSearch(safeSearchQuery, target, searchMatchMode);
    });
    const filteredItems = (
        authoritativePageItems !== null
        && !searchAllFiles
        && !safeSearchQuery
    )
        ? filteredItemsBeforeSort
        : sortItems(filteredItemsBeforeSort);

    const selectedDocumentItems = buildSelectedDocumentItems(selectedKeys, [
        ...selectedItemCacheRef.current.values(), ...searchItems, ...filteredItems,
    ]);
    const selectedFolderInfoTargets = buildSelectedFolderInfoTargets(selectedKeys, selectedDocumentItems);

    useEffect(() => {
        // Keep metadata only for the selected entries, including entries that are
        // no longer on this page or in this folder.
        selectedItemCacheRef.current = new Map(selectedDocumentItems.map(item => [item.key, item]));
    });

    function buildMergeSelectionFromKeys(keys) {
        const normalizeFolderMergeKey = (key) => {
            const normalized = String(key || '').replace(/\/+$/g, '');
            return normalized ? `${normalized}/` : '';
        };

        const knownItems = new Map();
        [...selectedDocumentItems, ...searchItems, ...filteredItems].forEach(item => {
            if (item?.key) {
                knownItems.set(item.key, item);
                if (item.type === 'folder') {
                    knownItems.set(normalizeFolderMergeKey(item.key), item);
                }
            }
        });

        const seen = new Set();
        return (keys || []).map(rawKey => {
            const key = String(rawKey || '');
            if (!key) return null;

            const folderKey = normalizeFolderMergeKey(key);
            const item = knownItems.get(key) || knownItems.get(folderKey);
            const isFolder = item?.type === 'folder' || key.endsWith('/');

            if (isFolder) {
                const canonicalKey = folderKey;
                if (!canonicalKey || seen.has(canonicalKey)) return null;
                seen.add(canonicalKey);

                return {
                    key: canonicalKey,
                    name: item?.name || canonicalKey.replace(/\/$/, '').split('/').pop() || 'Folder',
                    size: item?.size || 0,
                    type: 'folder'
                };
            }

            if (key.endsWith('/')) {
                return null;
            }

            if (seen.has(key)) return null;
            seen.add(key);

            return {
                key,
                name: item?.name || key.split('/').pop(),
                size: item?.size || item?.file?.size || 0,
                type: 'file'
            };
        }).filter(Boolean);
    }

    console.log('DocumentSection filtering debug:', {
        searchItemsLength: searchItems.length,
        searchItems: searchItems.slice(0, 5), // First 5 items
        filteredItemsLength: filteredItems.length,
        filteredItems: filteredItems.slice(0, 5), // First 5 items
        filteredItemsKeys: filteredItems.map(item => item.key),
        safeSearchQuery,
        hotDocsOnly
    });

    const handleRenameClick = (item) => {
        const isFolder = item.type === 'folder' || item.is_folder;
        if (findDocumentFolderRenameConflict(
            [item?.key],
            folderRenames,
            case_id,
            { includeAncestors: isFolder, fencingOnly: false }
        )) {
            setMessage({
                type: 'info',
                text: 'That item is inside a folder rename that is still finishing. Other folders remain available.'
            });
            return;
        }

        renameDialogTokenRef.current = null;
        setRenameSubmitting(false);
        setRenameStatus(null);
        setRenameTarget(item);
        const fullName = item?.name?.replace(/\/$/, "") || "";
        
        // Decode the filename for user-friendly display in the input field
        const decodedName = decodeFileName(fullName);
        
        if (!isFolder && decodedName.includes('.')) {
            const lastDotIndex = decodedName.lastIndexOf('.');
            const nameWithoutExt = decodedName.substring(0, lastDotIndex);
            const extension = decodedName.substring(lastDotIndex + 1);
            setNewNameInput(nameWithoutExt);
            setNewExtensionInput(extension);
        } else {
            setNewNameInput(decodedName);
            setNewExtensionInput('');
        }
    };

    const closeRenameDialog = () => {
        const dialogIsFolder = renameTarget?.type === 'folder' || renameTarget?.is_folder;
        if (renameSubmitting && !dialogIsFolder) return;

        renameDialogTokenRef.current = null;
        setRenameTarget(null);
        setNewNameInput("");
        setNewExtensionInput("");
        setRenameStatus(null);
        setRenameSubmitting(false);
    };

    const updateTag = async (id, tag, action = null) => {
        if (!id || !tag || String(tag).trim().length === 0) return;
        try {
            const response = await fetch("https://api.casedb.co/documents.php", {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    file_id: id,
                    tag: typeof tag === 'string' ? tag : tag[0],
                    action: action
                }),
                credentials: "include"
            });

            const result = await response.json();
            if (result.success) {
                // Force a complete refresh to ensure metadata is updated
                if (refreshCurrentFolder) {
                    await refreshCurrentFolder();
                } else {
                    await fetchDocuments();
                }
                
                // Close the rename modal/target
                setRenameTarget(null);
                
                // Force re-render by updating a dependency
                // This ensures React detects the metaData change
                setMessage({ type: 'success', text: 'Tag updated successfully' });
                setTimeout(() => setMessage(null), 2000);
            } else {
                console.error("Tag update failed:", result.message);
                setMessage({ type: 'error', text: result.message || 'Failed to update tag' });
                setTimeout(() => setMessage(null), 3000);
            }

        } catch (error) {
            console.error("Tag update error:", error);
            setMessage({ type: 'error', text: 'Failed to update tag' });
            setTimeout(() => setMessage(null), 3000);
        }
    };

    const applyItemSelectionGesture = (event, itemKey, { forceToggle = false } = {}) => {
        const result = resolveDocumentSelectionGesture({
            selectedKeys,
            visibleKeys: filteredItems.map(getDocumentSelectionKey).filter(Boolean),
            itemKey,
            anchorKey: selectionAnchorKeyRef.current,
            shiftKey: Boolean(event?.shiftKey),
            toggleKey: Boolean(event?.ctrlKey || event?.metaKey),
            forceToggle,
        });
        if (!result.handled) return false;

        event?.preventDefault?.();
        event?.stopPropagation?.();
        if (clickTimer.current) {
            clearTimeout(clickTimer.current);
            clickTimer.current = null;
        }
        selectionAnchorKeyRef.current = result.anchorKey;
        setSelectedKeys(result.selectedKeys);
        setActiveFile(result.selectedKeys);
        return true;
    };

    const handleDocumentDragContextMenu = (event) => {
        if (!event?.ctrlKey || !event.currentTarget?.draggable) return;
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDocumentDragStart = (event, type, item) => {
        const operation = event.ctrlKey || event.metaKey || event.altKey ? 'copy' : 'move';
        const payload = buildDocumentDragPayload({
            type,
            item,
            sourceFolder: folderName,
            selectedKeys,
            dragSelectedItems: true,
        });
        payload.operation = operation;

        event.dataTransfer.setData('application/json', JSON.stringify(payload));
        event.dataTransfer.effectAllowed = 'copyMove';

        if (typeof event.dataTransfer.setDragImage === 'function') {
            clearDocumentDragPreview();
            const draggedKey = getDocumentSelectionKey(item);
            const sourceKeys = Array.isArray(payload.sourceKeys) && payload.sourceKeys.length > 0
                ? payload.sourceKeys
                : [draggedKey];
            const visibleItemsByKey = new Map(
                filteredItems
                    .map(visibleItem => [getDocumentSelectionKey(visibleItem), visibleItem])
                    .filter(([key]) => Boolean(key))
            );
            const preview = createDocumentDragPreview({ sourceKeys, draggedKey, visibleItemsByKey });

            if (preview) {
                documentDragPreviewRef.current = preview;
                try {
                    const offsets = getDocumentDragImageOffsets(preview, event);
                    event.dataTransfer.setDragImage(preview, offsets.x, offsets.y);
                } catch (_) {
                    preview.remove();
                    documentDragPreviewRef.current = null;
                    return;
                }
                window.setTimeout(() => {
                    if (documentDragPreviewRef.current === preview) {
                        preview.remove();
                        documentDragPreviewRef.current = null;
                    }
                }, 0);
            }
        }
    };

    const clearDocumentDragPreview = () => {
        documentDragPreviewRef.current?.remove?.();
        documentDragPreviewRef.current = null;
    };

    const resetShareCreationScope = () => {
        // Each creator opening has its own response scope, even for another
        // selection in the same case or a reopened copy of the same selection.
        shareScopeRef.current = { caseId: String(case_id), userId: String(user_id) };
        setShareLink("");
    };

    const generateShareLink = async (item) => {
        resetShareCreationScope();
        setShareTarget(item);
        setShareModal(true);
    };

    // Smart sharing handler that checks for selected items or current folder
    const handleSmartShare = () => {
        resetShareCreationScope();
        if (selectedKeys.length > 0) {
            // Share selected files/folders
            const selectedItems = selectedDocumentItems;
            
            if (selectedItems.length === 1) {
                // Single item selected - share that item
                setShareTarget(selectedItems[0]);
            } else {
                // Multiple items selected - create a multi-item share target
                setShareTarget({
                    type: 'multi',
                    name: `${selectedItems.length} selected items`,
                    items: selectedItems,
                    selectedKeys: selectedKeys
                });
            }
        } else if (folderName && folderName !== 'root' && folderName !== '' && folderName !== '{{Name}}') {
            // No items selected but we're in a subfolder - share current folder
            setShareTarget({
                type: 'folder',
                name: folderName.split('/').pop() || folderName,
                key: `cases/${case_id}/${folderName}/`,
                folderPath: folderName
            });
        } else {
            // Default to case root sharing
            const rootTarget = { key: `cases/${case_id}/{{Name}}/`, type: 'folder', name: '{{Name}}' };
            setShareTarget(rootTarget);
        }
        setShareModal(true);
    };

    const createShareableLink = async (
        expirationDays,
        allowUpload = false,
        password = null,
        includeFutureUploads = false,
        allowProtectedDocuments = false,
        protectedDocumentsConfirmed = false
    ) => {
        const requestScope = shareScopeRef.current;
        try {
            // Handle multi-item sharing
            if (shareTarget.type === 'multi') {
                // The case root contains selections from every visited folder;
                // selected_items remains the server-enforced access allowlist.
                const response = await fetch("https://api.casedb.co/share.php", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        case_id: case_id,
                        target_key: `cases/${case_id}/{{Name}}/`,
                        is_folder: true,
                        expires_in: expirationDays * 24 * 60 * 60,
                        allow_upload: allowUpload,
                        password: password || undefined,
                        selected_items: shareTarget.selectedKeys, // Pass selected items to backend
                        include_future_uploads: includeFutureUploads,
                        allow_protected_documents: allowProtectedDocuments === true,
                        protected_documents_confirmed: allowProtectedDocuments === true && protectedDocumentsConfirmed === true,
                    }),
                    credentials: 'include'
                });

                const result = await response.json();
                if (result.success) {
                    if (shareScopeRef.current === requestScope) {
                        setShareLink(result.share_link);
                    }
                } else {
                    console.error("Multi-item share link generation failed:", result.message);
                    throw new Error(result.message || 'CaseDB could not create the link.');
                }
                return result;
            }

            // Regular single-item sharing logic
            let payloadTargetKey = shareTarget.key;
            let payloadPath = undefined;
            let payloadIsFolder = shareTarget.type === 'folder';

            // If shareTarget.name is '{{Name}}' and it's a folder, we want to target the case root folder
            if (payloadIsFolder && shareTarget.name === '{{Name}}') {
                // For root, target_key should be cases/{case_id}/{{Name}}/ and path left undefined so backend treats as root
                payloadTargetKey = `cases/${case_id}/{{Name}}/`;
                payloadPath = undefined;
            } else if (payloadIsFolder) {
                // Prefer an explicit folder path when sharing the current folder; otherwise build the child folder path.
                payloadPath = shareTarget.folderPath || (folderName ? (folderName + '/' + shareTarget.name) : shareTarget.name);
            } else {
                // For files, leave path undefined; backend will infer from target_key
                payloadPath = undefined;
            }

            const response = await fetch("https://api.casedb.co/share.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    case_id: case_id,
                    target_key: payloadTargetKey,
                    path: payloadPath,
                    is_folder: payloadIsFolder,
                    expires_in: expirationDays * 24 * 60 * 60,
                    allow_upload: allowUpload,
                    password: password || undefined,
                    include_future_uploads: includeFutureUploads,
                    allow_protected_documents: allowProtectedDocuments === true,
                    protected_documents_confirmed: allowProtectedDocuments === true && protectedDocumentsConfirmed === true,
                }),
                credentials: 'include'
            });

            const result = await response.json();
            if (result.success) {
                if (shareScopeRef.current === requestScope) {
                    setShareLink(result.share_link);
                }
            } else {
                console.error("Share link generation failed:", result.message);
                throw new Error(result.message || 'CaseDB could not create the link.');
            }
            return result;
        } catch (error) {
            console.error("Error generating share link:", error);
            throw error;
        }
    };

    const normalizeSelectedKeyDisplay = (key) => key;

    const handleMove = async () => {
        console.log('handleMove called with:', {
            moveTargetFolder,
            moveTargetFolderType: typeof moveTargetFolder,
            selectedKeys,
            folderName,
            case_id
        });
        
        // Enhanced protection against multiple rapid calls
        if (moveProcessing) {
            console.warn('Move already in progress, ignoring duplicate call');
            return;
        }
        if (!moveTargetFolder || selectedKeys.length === 0) {
            alert(`Please select a destination folder and files/folders to ${copyFiles ? 'copy' : 'move'}`);
            return;
        }

        // Extract file names from selected keys
        const fileNames = selectedKeys.map(key => {
            const fileName = key.split('/').pop();
            return fileName;
        }).filter(name => name && !name.endsWith('/')); // Filter out folders

        // Check for conflicts before proceeding
        if (fileNames.length > 0) {
            const conflictCheck = await checkFileConflicts(fileNames, moveTargetFolder);
            
            if (conflictCheck.hasConflicts) {
                // Show conflict resolution modal
                setConflictFiles(conflictCheck.conflicts);
                setPendingOperation({
                    type: copyFiles ? 'copy' : 'move',
                    selectedKeys: selectedKeys,
                    targetFolder: moveTargetFolder,
                    suggestions: conflictCheck.suggestions
                });
                setShowConflictModal(true);
                return; // Don't close the move modal yet
            }
        }

        // No conflicts or no files to check, proceed with move/copy
        await executeMoveOrCopy(selectedKeys, moveTargetFolder, copyFiles);
    };

    const executeMoveOrCopy = async (keysToProcess, targetFolder, isCopy, resolutions = {}, suggestions = {}) => {
        // Set processing state first to prevent race conditions
        setMoveProcessing(true);
        
        // Close modal after setting processing state
        setShowMoveModal(false);

        // If we have resolutions with keep-both, we need to handle renaming
        // For now, we'll pass this information to the backend
        const renameMap = {};
        Object.keys(resolutions).forEach(fileName => {
            if (resolutions[fileName] === 'keep-both' && suggestions[fileName]) {
                renameMap[fileName] = suggestions[fileName];
            }
        });

        // Preserve each item's original folder even after navigating elsewhere.
        const normalizedSourceKeys = uniqueDocumentSelectionKeys(keysToProcess);
        
        try {
            let result;
            if (isCopy) {
                result = await simpleUploadService.copyFilesWithProgress(
                    normalizedSourceKeys,
                    case_id,
                    targetFolder,
                    { dispatch },  // Pass upload context
                    renameMap  // Pass rename map
                );
            } else {
                const basePrefix = `cases/${case_id}/{{Name}}/`;
                const buildDisplayFiles = (keys) => {
                    const displayFiles = [];

                    const addDisplay = (name) => {
                        if (name) {
                            displayFiles.push({ name });
                        }
                    };

                    const getFolderEntries = (folderKey) => {
                        if (!folderKey) return null;
                        const relPath = folderKey.startsWith(basePrefix)
                            ? folderKey.substring(basePrefix.length)
                            : folderKey;
                        const trimmed = relPath.replace(/\/$/, '');
                        const candidates = [
                            trimmed,
                            `{{Name}}/${trimmed}`,
                            trimmed.replace(/^{{Name}}\/?/, '')
                        ].filter(Boolean);

                        for (const candidate of candidates) {
                            if (Array.isArray(folders?.[candidate])) {
                                return folders[candidate];
                            }
                        }
                        return null;
                    };

                    keys.forEach((key) => {
                        if (typeof key === 'string' && key.endsWith('/')) {
                            const folderEntries = getFolderEntries(key);
                            if (Array.isArray(folderEntries) && folderEntries.length > 0) {
                                folderEntries
                                    .filter(entry => entry?.name && entry.name !== '(Folder)')
                                    .forEach(entry => addDisplay(entry.name));
                            } else {
                                const fallbackName = key.split('/').filter(Boolean).pop();
                                addDisplay(fallbackName || key);
                            }
                        } else {
                            addDisplay(key.split('/').pop() || key);
                        }
                    });

                    return displayFiles;
                };

                const displayFiles = buildDisplayFiles(normalizedSourceKeys);

                // Use simpleUploadService for move with progress tracking
                result = await simpleUploadService.moveFiles(
                    normalizedSourceKeys,
                    targetFolder,
                    case_id,
                    { dispatch },  // Pass upload context
                    renameMap,  // Pass rename map
                    displayFiles
                );
                
            }

            if (!result?.success) {
                throw new Error(result?.message || result?.error || `The ${isCopy ? 'copy' : 'move'} failed.`);
            }

            // The mutation is committed. Clear operation state before doing
            // best-effort reconciliation so a failed refresh cannot invite a
            // duplicate move/copy retry.
            const operationType = isCopy ? 'copied' : 'moved';
            const undoUnavailable = getDocumentUndoUnavailableMessage(result);
            const resetState = () => {
                setSelectedKeys([]);
                setActiveFile([]);
                setCopyFiles(false);
                setMoveTargetFolder('');
            };
            resetState();

            const refreshOutcome = await settleDocumentMutationRefreshes([
                refreshCurrentFolder
                    ? () => refreshCurrentFolder()
                    : () => fetchDocuments?.(),
                () => refreshFolderTree?.(),
            ]);
            setMessage({
                text: `Files ${operationType} successfully!${undoUnavailable ? ` ${undoUnavailable}` : ''}${refreshOutcome.warning ? ` ${refreshOutcome.warning}` : ''}`,
                type: refreshOutcome.success ? 'success' : 'warning'
            });

            // Publish only after local cleanup and refresh settlement so an
            // immediate Ctrl/Cmd+Z cannot race the original reconciliation.
            publishDocumentUndoReceipt({
                caseId: case_id,
                operation: isCopy ? 'copy' : 'move',
                result,
                sourceKeys: normalizedSourceKeys,
            });
            
        } catch (error) {
            console.error(`${isCopy ? 'Copy' : 'Move'} error:`, error);
            setMessage({
                type: 'error',
                text: `Error ${isCopy ? 'copying' : 'moving'} files: ${error.message}`
            });
        } finally {
            setMoveProcessing(false);
        }
    };

    const extractFolderPaths = useCallback(() => {
        const paths = Object.keys(folders)
            .map(path => (path === 'root' ? '{{Name}}' : path))
            .filter(isDocumentCaseLogicalPathAllowed)
            .map(path => {
            const displayPath = displayDocumentPath(path, caseName);
            return { 
                path: path,
                displayPath: displayPath || caseName
            };
        });

        if (!paths.find(p => p.displayPath === caseName)) {
            paths.unshift({ path: '{{Name}}', displayPath: caseName });
        }

        return paths;
    }, [folders, caseName]);

    const formatSearchSelectPath = useCallback((path) => {
        if (!path) return caseName || '';
        const normalizedPath = path.replace(/^cases\/[^/]+\/?/i, '').replace(/\/+$/g, '');
        const rawParts = normalizedPath.split('/').filter(Boolean);
        if (rawParts.length === 0) return caseName || '';

        const hasNameToken = rawParts[0] === '{{Name}}';
        const rawRootLabel = hasNameToken ? (caseName || 'Case documents') : (rawParts[0] || caseName || '');
        const rootLabel = rawRootLabel && rawRootLabel.length > 25
            ? `${rawRootLabel.slice(0, 25)}…`
            : rawRootLabel;
        const displayParts = hasNameToken
            ? [rootLabel, ...rawParts.slice(1)]
            : rawParts.map((part, index) => (index === 0 ? (part || rootLabel) : part));

        if (displayParts.length > 3) {
            const beforeLast = displayParts[displayParts.length - 2];
            const last = displayParts[displayParts.length - 1];
            return `${rootLabel}/../${beforeLast}/${last}`;
        }

        return displayParts.join('/') || rootLabel;
    }, [caseName]);

    const ensureFolderOption = useCallback((path) => {
        if (!path) return;
        const normalized = path === 'root' ? '{{Name}}' : path;
        if (!isDocumentCaseLogicalPathAllowed(normalized)) return;
        setAvailableFolders(prev => {
            if (prev?.some?.(p => p.path === normalized)) {
                return prev;
            }
            const displayPath = displayDocumentPath(normalized, caseName);
            const next = [...(prev || []), { path: normalized, displayPath: displayPath || caseName }];
            return next.sort((a, b) => a.displayPath.localeCompare(b.displayPath));
        });
    }, [caseName]);

    const loadAllFolderPaths = useCallback(async () => {
        if (!case_id) {
            setAvailableFolders(extractFolderPaths());
            return;
        }

        try {
            const response = await fetch(`https://api.casedb.co/documents.php?case_id=${case_id}&structure_only=true&t=${Date.now()}`, { credentials: 'include' });
            const data = await response.json();

            if (data?.success && data?.folders) {
                const rawPaths = Object.keys(data.folders);
                const normalized = rawPaths
                    .map(path => (path === 'root' ? '{{Name}}' : path))
                    .filter(isDocumentCaseLogicalPathAllowed);
                const uniquePaths = Array.from(new Set(normalized));
                const paths = uniquePaths
                    .map(path => {
                        const displayPath = displayDocumentPath(path, caseName);
                        return {
                            path,
                            displayPath: displayPath || caseName
                        };
                    })
                    .sort((a, b) => a.displayPath.localeCompare(b.displayPath));

                if (!paths.find(p => p.displayPath === caseName)) {
                    paths.unshift({ path: '{{Name}}', displayPath: caseName });
                }

                setAvailableFolders(paths);
                return;
            }
        } catch (error) {
            console.error('Failed to preload folder paths:', error);
        }

        setAvailableFolders(extractFolderPaths());
    }, [case_id, caseName, extractFolderPaths]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const target = event.target;
            if (target?.closest?.('.expanded-actions')) return;
            if (menuRef.current && menuRef.current.contains(target)) return;
            setMore(null);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        setMultiFiles(null);
            setFolderFiles(null);
        setSidePreview(null);
        setSidePreviewFile(null);
    }, [folderName]);

    useEffect(() => {
        if (deletedPrefixes.length === 0) return;
        setDeletedPrefixes([]);
    }, [currentFolderFiles, currentFolderSubfolders]);

    useEffect(() => {
        if (moveTargetFolder) {
            ensureFolderOption(moveTargetFolder);
        }
    }, [moveTargetFolder, ensureFolderOption]);

    useEffect(() => {
        if (mergeSettings?.targetFolder) {
            ensureFolderOption(mergeSettings.targetFolder);
        }
    }, [mergeSettings?.targetFolder, ensureFolderOption]);

    useEffect(() => {
        const path = batesSettings?.destination?.path;
        if (path) {
            ensureFolderOption(path);
        }
    }, [batesSettings?.destination?.path, ensureFolderOption]);

    useEffect(() => {
        if (metaData && metaData.length > 0) {
            const uniqueAuthorIds = [...new Set(metaData.map(file => file.author_id).filter(Boolean))];
            uniqueAuthorIds.forEach(fetchUser);
        }
    }, [metaData]);

    useEffect(() => {
        // Selection spans folders/pages, but never a different case or user.
        setSelectedKeys([]);
        setActiveFile([]);
        setShareModal(false);
        setShowCaseShareLinks(false);
        setShareTarget(null);
        setShareLink("");
        selectedItemCacheRef.current.clear();
        selectionAnchorKeyRef.current = null;
    }, [case_id, user_id]);

    useEffect(() => {
        if (showMoveModal) {
            loadAllFolderPaths();
        }
    }, [showMoveModal, loadAllFolderPaths]);

    useEffect(() => {
        if (showAdvancedMergeModal) {
            loadAllFolderPaths();
        }
    }, [showAdvancedMergeModal, loadAllFolderPaths]);

    useEffect(() => {
        if (showBatesModal && batesSettings?.destination?.type === 'custom') {
            loadAllFolderPaths();
        }
    }, [showBatesModal, batesSettings?.destination?.type, loadAllFolderPaths]);

    useEffect(() => {
        fetchHotDocs();
    }, []);

    // Handle automatic file opening from URL parameter
    useEffect(() => {
        if (initialFileToOpen && files && files.length > 0) {
            // Find the file with matching name
            const fileToOpen = Object.values(files).find(file => 
                file.name === initialFileToOpen || 
                file.name === decodeURIComponent(initialFileToOpen)
            );
            
            if (fileToOpen) {
                // Open the file automatically
                handleDoubleClick(fileToOpen);
                
                // Clear the URL parameter to prevent reopening on subsequent renders
                // Only clear after successfully finding and opening the file
                const url = new URL(window.location);
                url.searchParams.delete('file');
                window.history.replaceState({}, '', url);
            }
            // Don't clear URL parameters if file is not found - Section.js might still be fetching
        }
    }, [initialFileToOpen, files]);

    useEffect(() => {
        console.log("Parent Case:", isParentCase);
    }, [isParentCase]);

    // Recalculate menu position after menu is rendered with actual dimensions
    useEffect(() => {
        if (more !== null && menuRef.current) {
            const index = typeof more === 'string' ? more.replace('sub', '') : more;
            const isSubfolder = typeof more === 'string' && more.includes('sub');
            
            setTimeout(() => {
                const position = calculateMenuPosition(index, isSubfolder, menuRef.current);
                const menuId = isSubfolder ? `${index}sub` : index;
                
                setMenuPosition(prev => ({
                    ...prev,
                    [menuId]: { left: position.left, top: position.top }
                }));
                
                setMenuPositionAbove(prev => ({
                    ...prev,
                    [menuId]: position.showAbove
                }));
            }, 0);
        }
    }, [more]);

    /*
        Search Your Filevine Even Faster

        Filevine's document search supports a variety of advanced syntax options to make it easier and faster to find the documents you're looking for.


        Use AND, OR, NOT, and a variety of other operators to better refine your results.


        `"John Smith" AND "Jane Doe"

        `"John Smith" OR "Jane Doe"

        `"John Smith" NOT "Jane Doe"

        `"John Sm*"


        Learn more about all of our supported syntax in our help center.
    */

    const isFolderBatesFlow = batesSelection.mode === 'folder';
    const isExhibitStampMode = batesSettings.pageLabelMode === 'exhibit';
    const batesOutputNamingLabel = isExhibitStampMode
        ? (batesSettings.exhibitOutputNameMode === 'identifier'
            ? 'Use exhibit identifiers (PTX0001.pdf)'
            : 'Keep source file names')
        : (batesSettings.replaceOriginals ? 'Keep source file names' : 'Use generated stamped names');
    const batesFolderOrder = useMemo(() => buildBatesFolderOrder(
        batesSelection.folders,
        (batesSelection.files || []).map(getBatesFileKey)
    ), [batesSelection.folders, batesSelection.files]);
    const isOrderedFolderBatesFlow = isFolderBatesFlow && batesFolderOrder.eligible;
    const batesStampingTypeStep = isOrderedFolderBatesFlow || !isFolderBatesFlow ? 2 : 1;
    const batesDestinationStep = isOrderedFolderBatesFlow || !isFolderBatesFlow ? 3 : 2;
    const batesConfigStep = isOrderedFolderBatesFlow || !isFolderBatesFlow ? 4 : 3;
    const batesFolderOrderParentLabel = getBatesFolderDisplayPath(
        batesSelection.orderedFolderParent || batesFolderOrder.parentKey,
        case_id,
        caseName
    );
    const batesAnalyzedByKey = useMemo(() => new Map(
        (batesAnalyzedFiles || []).map(file => [getBatesFileKey(file), file])
    ), [batesAnalyzedFiles]);
    const batesAnalyzedByUniqueName = useMemo(() => {
        const byName = new Map();
        (batesAnalyzedFiles || []).forEach(file => {
            const name = String(file?.name || '');
            if (!name) return;
            byName.set(name, byName.has(name) ? null : file);
        });
        return byName;
    }, [batesAnalyzedFiles]);
    const exhibitStampResolution = useMemo(() => buildExhibitFileResolution(
        batesAnalyzedFiles,
        batesExhibitOverrides,
        batesSettings.exhibitStampStyle,
        batesSettings.exhibitDisplayIdentifierMode
    ), [
        batesAnalyzedFiles,
        batesExhibitOverrides,
        batesSettings.exhibitStampStyle,
        batesSettings.exhibitDisplayIdentifierMode
    ]);
    const exhibitPageLabelPlan = useMemo(() => buildExhibitPageLabelPlan(
        exhibitStampResolution,
        batesExhibitPageLabels,
        batesSettings.position
    ), [exhibitStampResolution, batesExhibitPageLabels, batesSettings.position]);
    const hasBatesSnapshot = hasValidBatesSelectionSnapshot(batesSelectionSnapshot);
    const firstExhibitStampEntry = exhibitStampResolution.processable[0] || exhibitStampResolution.entries[0] || null;
    const firstExhibitStampPreview = firstExhibitStampEntry
        ? {
            valid: firstExhibitStampEntry.processable && firstExhibitStampEntry.resolved,
            value: firstExhibitStampEntry.normalizedDisplayIdentifier,
            reason: firstExhibitStampEntry.processable
                ? firstExhibitStampEntry.reason
                : (firstExhibitStampEntry.file?.parsed?.reason || `Unsupported ${firstExhibitStampEntry.file?.extension || 'unknown'} file`)
        }
        : null;
    const resolvedExhibitStampStyle = firstExhibitStampEntry?.stampStyle || resolveExhibitStampStyle(
        batesSettings.exhibitStampStyle,
        firstExhibitStampPreview
    );
    const invalidExhibitStampFiles = isExhibitStampMode ? exhibitStampResolution.unresolved : [];
    const batesExhibitSequenceTargetCount = batesExhibitSequence.scope === 'all'
        ? exhibitStampResolution.processable.length
        : exhibitStampResolution.unresolved.length;
    const editableExhibitStampFiles = exhibitStampResolution.entries.filter(entry => (
        entry.processable
        && (
            !entry.resolved
            || !entry.autoResolved
            || Object.prototype.hasOwnProperty.call(batesExhibitOverrides[entry.sourceKey] || {}, 'identifier')
        )
    ));
    const batesTotalSteps = batesConfigStep;
    const batesNextDisabled = batesAnalyzeLoading
        || !hasBatesSnapshot
        || (batesStep === 1
            && ((batesSelection.files?.length || 0) + (batesSelection.folders?.length || 0) === 0));
    const showSidePreview = sidePreview && windowWidth >= 768;
    const activeSearchChips = useMemo(() => {
        const chips = [];
        chips.push(['Scope', searchScope === 'case' ? 'Whole case' : 'Current folder']);
        chips.push(['Field', searchTypeLabel]);

        if (safeSearchQuery) {
            const matchLabel = DOCUMENT_SEARCH_MATCH_OPTIONS.find(([value]) => value === searchMatchMode)?.[1] || 'All words';
            chips.push(['Match', matchLabel]);
        }

        const fileTypeLabel = DOCUMENT_FILE_TYPE_OPTIONS.find(([value]) => value === searchFilters.fileType)?.[1];
        if (searchFilters.fileType && fileTypeLabel) chips.push(['Type', fileTypeLabel]);
        if (searchFilters.dateFrom) chips.push(['From', searchFilters.dateFrom]);
        if (searchFilters.dateTo) chips.push(['To', searchFilters.dateTo]);
        if (searchFilters.folder) chips.push(['Folder', searchFilters.folder]);
        if (searchFilters.semantic) chips.push(['Mode', 'Semantic']);
        if (hotDocsOnly) chips.push(['Filter', 'Hot docs']);

        return chips;
    }, [hotDocsOnly, safeSearchQuery, searchFilters.dateFrom, searchFilters.dateTo, searchFilters.fileType, searchFilters.folder, searchFilters.semantic, searchMatchMode, searchScope, searchTypeLabel]);
    const activeSearchFilterCount = useMemo(() => {
        return [
            searchScope !== 'case',
            safeSearchType !== 'All',
            searchMatchMode !== 'all_words',
            Boolean(searchFilters.fileType),
            Boolean(searchFilters.dateFrom),
            Boolean(searchFilters.dateTo),
            Boolean(searchFilters.folder),
            Boolean(searchFilters.semantic),
            hotDocsOnly
        ].filter(Boolean).length;
    }, [hotDocsOnly, safeSearchType, searchFilters.dateFrom, searchFilters.dateTo, searchFilters.fileType, searchFilters.folder, searchFilters.semantic, searchMatchMode, searchScope]);
    const canClearDocumentSearch = useMemo(() => {
        return Boolean(
            searchQuery.trim()
            || searchAllFiles
            || searchResults.length > 0
            || activeSearchFilterCount > 0
            || isSearching
            || isSearchQueued
        );
    }, [activeSearchFilterCount, isSearchQueued, isSearching, searchAllFiles, searchQuery, searchResults.length]);

    const hasDocumentPagination = !isLoading
        && pagination
        && (pagination.total_pages > 1 || pagination.has_next || pagination.has_prev);

    return (
        <div 
            className={`documents-container ${showSidePreview ? 'with-preview' : ''}`}
            role="region"
            aria-label="Case documents"
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            style={{ position: 'relative' }}
        >
            {/* Drag and Drop Overlay */}
            {isDragOver && (
                <div 
                    className="drag-drop-overlay"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px dashed var(--text-color)',
                        borderRadius: '12px',
                        margin: '2px',
                        backdropFilter: 'blur(5px)',
                        pointerEvents: 'none'
                    }}
                >
                    <div 
                        style={{
                            color: 'white',
                            fontSize: '24px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                            padding: '15px',
                            borderRadius: '6px',
                            backgroundColor: 'rgba(0, 0, 0, 0.2)'
                        }}
                    >
                        <div style={{ marginBottom: '8px' }}>Drop file(s)/folder to upload</div>
                        <div style={{ fontSize: '18px', fontWeight: 'normal' }}>
                            to {displayDocumentPath(folderName, caseName) || 'current folder'}
                        </div>
                    </div>
                </div>
            )}
            <div className={`document-section ${hasDocumentPagination ? 'has-document-pagination' : ''}`}>
                {unresolvedUploadGroups.filter(group => String(group.caseId) === String(case_id)).map(group => (
                    <details key={group.id} className="form-group full" aria-label={group.retryItems ? 'Failed uploads available for retry' : 'Uploads needing confirmation'}>
                        <summary>{group.files.length} upload(s) {group.retryItems ? 'failed' : 'need confirmation'} — {displayDocumentPath(group.destinationPath, caseName)}</summary>
                        <p className="subtext">{group.retryItems
                            ? 'These files failed after you changed your selection. Review only these failed files before retrying; their original destination and name choices are preserved.'
                            : 'These files are not queued for retry because their final result could not be confirmed. Check the destinations below before selecting them again to avoid duplicate uploads.'}</p>
                        <ul>
                            {group.files.map((item, index) => <li key={index} className="small-text">{displayDocumentPath(item.destinationPath, caseName)}</li>)}
                        </ul>
                        {group.retryItems && <button type="button" className="action" onClick={() => {
                            if (uploadingRef.current || String(group.caseId) !== currentUploadCaseRef.current) return;
                            uploadSelectionGenerationRef.current += 1;
                            setFilesToRename(group.retryItems.map(item => ({ ...item })));
                            setUploadDestinationPath(group.destinationPath);
                            setUploadSelectionCaseId(group.caseId);
                            setSkipExistingUploads(group.skipExisting === true);
                            setShowRenameModal(true);
                            setUnresolvedUploadGroups(previous => previous.filter(item => item.id !== group.id));
                        }}>Review failed uploads</button>}
                        <button type="button" className="action alt" onClick={() => setUnresolvedUploadGroups(previous => previous.filter(item => item.id !== group.id))}>Dismiss upload notice</button>
                    </details>
                ))}
                <div className="document-filters">
                    {windowWidth >= 768 && (
                        <div className='form-box alt' onClick={() => setDocNav((prev) => !prev)}>
                            <ChevronRight size={16} style={{ transform: docNav ? 'rotate(180deg)' : 'rotate(0deg)'}}/>
                        </div>
                    )}
                    <div className="document-search-panel" ref={documentSearchFilterRef}>
                        <div className="document-search-primary-row">
                            <SearchBar 
                                placeholder={searchScope === 'case' ? 'Search all case documents...' : 'Search current folder...'} 
                                expanded={true} 
                                setExpanded={() => {}} 
                                value={searchQuery}
                                setSearchQuery={(query) => {
                                    setSearchQuery(query);
                                    queueDocumentSearch(query);
                                }} 
                            />
                            <button
                                type="button"
                                className={`action quaternary small-text document-search-filter-button ${showDocumentSearchFilters ? 'active' : ''}`}
                                onClick={() => setShowDocumentSearchFilters(prev => !prev)}
                                title="Search filters"
                            >
                                <SlidersHorizontal size={16} />
                                Filters
                                {activeSearchFilterCount > 0 && (
                                    <span className="document-search-filter-count">{activeSearchFilterCount}</span>
                                )}
                            </button>
                            <button
                                type="button"
                                className="action document-search-action"
                                title="Search documents"
                                onClick={() => {
                                    runDocumentSearch(searchQuery, 1, true);
                                    setShowDocumentSearchFilters(false);
                                }}
                                disabled={searchScope === 'case' && searchQuery.trim().length < 2}
                            >
                                Search
                            </button>
                            <button
                                type="button"
                                className="action alt document-search-action"
                                title="Clear document search"
                                disabled={!canClearDocumentSearch}
                                onClick={() => {
                                    clearDocumentSearch();
                                    setSearchFilters(DEFAULT_DOCUMENT_SEARCH_FILTERS);
                                    setSearchMatchMode('all_words');
                                    setSearchType('All');
                                    setShowDocumentSearchFilters(false);
                                }}
                            >
                                Clear
                            </button>
                        </div>
                        {showDocumentSearchFilters && (
                            <div className="document-search-filter-popover">
                                <div className="document-search-filter-popover-header">
                                    <span>Search filters</span>
                                    <button
                                        type="button"
                                        className="document-search-filter-close"
                                        onClick={() => setShowDocumentSearchFilters(false)}
                                        title="Close filters"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                                <div className="document-search-filter-row">
                                    <div className="document-search-control document-search-scope-control">
                                        <span className="subtext">Scope</span>
                                        <div className="this-or-that document-search-scope" style={{ backgroundColor: 'var(--background-color) !important' }} role="group" aria-label="Document search scope">
                                            <div
                                                className={`tot-option document-search-scope-option ${searchScope === 'folder' ? 'active' : ''}`}
                                                onClick={() => {
                                                    cancelActiveDocumentSearch();
                                                    setSearchScope('folder');
                                                    setSearchAllFiles(false);
                                                    setSearchResults([]);
                                                    setSearchPerformance(null);
                                                }}
                                                title="Search only within the current folder. Best for quick searches when you know the relevant folder."
                                            >
                                                Current
                                            </div>
                                            <div
                                                className={`tot-option document-search-scope-option ${searchScope === 'case' ? 'active' : ''}`}
                                                onClick={() => {
                                                    cancelActiveDocumentSearch();
                                                    setSearchScope('case');
                                                }}
                                                title="Search across all folders in the case. Works best with more specific queries of 3 or more characters."
                                            >
                                                All
                                            </div>
                                        </div>
                                    </div>
                                    <div className="document-search-control document-search-field-control">
                                        <span className="subtext">Field</span>
                                        <Dropdown
                                            options={DOCUMENT_SEARCH_TYPE_OPTIONS}
                                            value={safeSearchType}
                                            onChange={(value) => {
                                                cancelActiveDocumentSearch();
                                                setSearchType(value || 'All');
                                            }}
                                            placeholder="All fields"
                                            expanded={false}
                                        />
                                    </div>
                                    <div className="document-search-control">
                                        <span className="subtext">Match</span>
                                        <div className='filter-group' style={{ width: '100%' }}>
                                            <div className='select-wrapper' style={{ width: '100%' }}>
                                                <select
                                                    className="filter-select document-search-select"
                                                    value={searchMatchMode}
                                                    onChange={(event) => {
                                                        cancelActiveDocumentSearch();
                                                        setSearchMatchMode(event.target.value);
                                                    }}
                                                >
                                                    {DOCUMENT_SEARCH_MATCH_OPTIONS.map(([value, label]) => (
                                                        <option key={value} value={value}>{label}</option>
                                                    ))}
                                                </select>
                                                <ChevronDown size={16} className='select-chevron' />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="document-search-control">
                                        <span className="subtext">Type</span>
                                        <div className='filter-group' style={{ width: '100%' }}>
                                            <div className='select-wrapper' style={{ width: '100%' }}>
                                                <select
                                                    className="filter-select document-search-select"
                                                    value={searchFilters.fileType}
                                                    onChange={(event) => updateSearchFilter('fileType', event.target.value)}
                                                >
                                                    {DOCUMENT_FILE_TYPE_OPTIONS.map(([value, label]) => (
                                                        <option key={value || 'any'} value={value}>{label}</option>
                                                    ))}
                                                </select>
                                                <ChevronDown size={16} className='select-chevron' />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="document-search-control document-search-date-control">
                                        <span className="subtext">From</span>
                                        <input
                                            type="date"
                                            className="document-search-date-input"
                                            value={searchFilters.dateFrom}
                                            onChange={(event) => updateSearchFilter('dateFrom', event.target.value)}
                                        />
                                    </div>
                                    <div className="document-search-control document-search-date-control">
                                        <span className="subtext">To</span>
                                        <input
                                            type="date"
                                            className="document-search-date-input"
                                            value={searchFilters.dateTo}
                                            onChange={(event) => updateSearchFilter('dateTo', event.target.value)}
                                        />
                                    </div>
                                    <div className="document-search-control document-search-folder-control">
                                        <span className="subtext">Folder</span>
                                        <input
                                            type="text"
                                            className="document-search-folder-input"
                                            value={searchFilters.folder}
                                            onChange={(event) => updateSearchFilter('folder', event.target.value)}
                                            placeholder="Any folder"
                                        />
                                    </div>
                                    <div className="document-search-control document-search-hot-docs-control">
                                        <Checkbox
                                            label="Hot Docs Only"
                                            value={hotDocsOnly}
                                                onChange={() => {
                                                    cancelActiveDocumentSearch();
                                                    setHotDocsOnly(prev => !prev);
                                                }}
                                        />
                                        {hotDocsOnly && hotDocsList.length > 0 && (
                                            <button 
                                                className="action tertiary small-text"
                                                onClick={selectAllHotDocs}
                                                title="Select all Hot Docs in current folder"
                                            >
                                                Select All
                                            </button>
                                        )}
                                    </div>
                                    <div className="document-search-control document-search-semantic-control">
                                        <Checkbox
                                            label="Semantic filename search"
                                            value={!!searchFilters.semantic}
                                            hint="Use embedded file-name searches to find documents based on meaning, rather than literal text."
                                            onChange={() => updateSearchFilter('semantic', !searchFilters.semantic)}
                                        />
                                    </div>
                                </div>
                                {activeSearchChips.length > 0 && (
                                    <div className="document-search-chip-row">
                                        {activeSearchChips.map(([label, value]) => (
                                            <span className="document-search-chip" key={`${label}-${value}`}>
                                                <strong>{label}</strong>
                                                {value}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {isParentCase && (
                        <button
                            className="action quaternary small-text"
                            onClick={() => {
                                const key = getCurrentFolderSyncKey();
                                if (key) {
                                    toggleFolderSyncPoint(key);
                                }
                            }}
                            title={isFolderSyncPoint(getCurrentFolderSyncKey()) ? 'Remove sync point for this folder' : 'Mark this folder as a sync point'}
                            disabled={syncBusy}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                        >
                            {isFolderSyncPoint(getCurrentFolderSyncKey()) ? <CloudCheck size={16} /> : <CloudBackup size={16} />}
                            {isFolderSyncPoint(getCurrentFolderSyncKey())
                                ? (syncStatus?.pending ? 'Syncing...' : 'Synced')
                                : 'Sync This Folder'}
                        </button>
                    )}
                    <button
                        type="button"
                        className="action quaternary small-text upload-files-btn document-toolbar-icon-button"
                        onClick={() => {
                            if (preventUploadDuringFolderRename(
                                joinDocumentUploadPath(folderName, '__new_upload__')
                            )) return;
                            setShowUploadModal(true);
                        }}
                        disabled={folderRenameBlocksUploadPicker(folderName)}
                        title={folderRenameBlocksUploadPicker(folderName)
                            ? 'This folder is still being renamed. Uploads will be available when it finishes.'
                            : 'Upload files'}
                        aria-label={folderRenameBlocksUploadPicker(folderName)
                            ? 'Uploads unavailable while this folder is being renamed'
                            : 'Upload files'}
                        onDragEnter={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            event.currentTarget.classList.add('drag-over');
                        }}
                        onDragOver={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            event.currentTarget.classList.add('drag-over');
                        }}
                        onDragLeave={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            event.currentTarget.classList.remove('drag-over');
                        }}
                        onDrop={handleDrop}
                    >
                        <Upload size={18} aria-hidden="true" />
                    </button>
                    {showCreateFolder && (
                        <button className='action quaternary small-text' onClick={() => {
                            setCreateFolderError('');
                            setCreateFolder(true);
                        }} title='Create folder'>
                            <FolderPlus />
                        </button>
                    )}
                    {showGenerateFolders && selectedKeys.length === 0 && (
                        <button
                            className='action quaternary small-text'
                            onClick={handleGenerateFolders}
                            title='Generate missing template folders for this case'
                        >
                            Generate Folders
                        </button>
                    )}
                    {/* View-density control is intentionally hidden while the document toolbar is simplified.
                    <div className='this-or-that' style={{ maxWidth: '72px' }}>
                        <div
                            className={`tot-option ${ view === 0 ? 'active' : '' }`}
                            onClick={() => setView(0)}
                            title='Minimize document view'
                        >
                            <Rows3 />
                        </div>
                        <div
                            className={`tot-option${ view === 1 ? ' active' : '' }`}
                            onClick={() => setView(1)}
                            title='Expand document view'
                        >
                            <Rows2 />
                        </div>
                    </div>
                    */}
                    {createFolder &&
                        <CreateFolder
                            folderName={newFolderName}
                            setFolderName={(value) => {
                                setNewFolderName(value);
                                setCreateFolderError('');
                            }}
                            error={createFolderError}
                            onCreate={async () => {
                                const validationError = getFolderLeafNameValidationError(newFolderName);
                                if (validationError) {
                                    setCreateFolderError(validationError);
                                    return;
                                }
                                
                                try {
                                    await createFolderOnS3({
                                        caseId: case_id,
                                        folderName: newFolderName.trim(),
                                        currentPath: folderName
                                    });
                                    setNewFolderName('');
                                    setCreateFolderError('');
                                    setCreateFolder(false);
                                } catch (error) {
                                    console.error('Failed to create folder:', error);
                                    setCreateFolderError(error?.message || 'The folder could not be created.');
                                    // Keep the modal open so user can try again
                                }
                            }}
                            onCancel={() => {
                                setNewFolderName('');
                                setCreateFolderError('');
                                setCreateFolder(false);
                            }}
                        />
                    }
                    {selectedKeys.length > 0 && (
                        <>
                            <DocumentSelectionSummary
                                selectedKeys={selectedKeys}
                                folderName={folderName}
                                onRemove={key => {
                                    setSelectedKeys(current => current.filter(item => item !== key));
                                    setActiveFile(current => current.filter(item => item !== key));
                                }}
                                onClear={() => { setSelectedKeys([]); setActiveFile([]); }}
                            />
                            <div className="document-selection-actions" role="group" aria-label="Selected document actions">
                                <button
                                    type="button"
                                    className="form-box alt"
                                    onClick={openMoveModal}
                                    title="Move or copy selected items"
                                    aria-label="Move or copy selected items"
                                >
                                    <DriveFileMoveRounded size={18} />
                                </button>
                                <button
                                    type="button"
                                    className="form-box alt"
                                    onClick={() => handleBulkDownload()}
                                    title="Download selected items"
                                    aria-label="Download selected items"
                                >
                                    <ArrowDownToLine size={18} />
                                </button>
                                <button
                                    type="button"
                                    className="form-box alt"
                                    onClick={() => handleBulkDelete('confirm')}
                                    title="Delete selected items"
                                    aria-label="Delete selected items"
                                >
                                    <Trash size={18} />
                                </button>
                                <button
                                    type="button"
                                    className="form-box alt merge-advanced"
                                    onClick={handleAdvancedMerge}
                                    title={isMerging ? "Merge in progress" : "Merge selected documents"}
                                    aria-label={isMerging ? "Merge in progress" : "Merge selected documents"}
                                    disabled={isMerging}
                                >
                                    <Merge size={18} />
                                </button>
                                <button
                                    type="button"
                                    className="form-box alt"
                                    onClick={initializeBatesStamping}
                                    title={batesProcessing ? "Bates stamping in progress" : "Stamp selected documents"}
                                    aria-label={batesProcessing ? "Bates stamping in progress" : "Stamp selected documents"}
                                    disabled={batesProcessing}
                                >
                                    <Stamp size={18} />
                                </button>
                                {selectedFolderInfoTargets.length > 0 && (
                                    <button
                                        type="button"
                                        className="form-box alt"
                                        onClick={() => setFolderInfoTargets([...selectedFolderInfoTargets])}
                                        title={`Calculate size and contents for ${selectedFolderInfoTargets.length === 1 ? 'selected folder' : `${selectedFolderInfoTargets.length} selected folders`}`}
                                        aria-label="Calculate selected folder information"
                                    >
                                        <Info size={18} />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="action quaternary small-text document-share-links-button"
                                    onClick={() => setShowCaseShareLinks(true)}
                                    title="View or create share links"
                                    aria-label="View or create share links"
                                >
                                    <Link size={16} />
                                    <span>Share Links</span>
                                </button>
                            </div>
                        </>
                    )}
                    {selectedKeys.length === 0 && (
                        <button
                            type="button"
                            className="action quaternary small-text document-share-links-button"
                            onClick={() => setShowCaseShareLinks(true)}
                            title="View or create share links"
                        >
                            <Link size={16} />
                            Share Links
                        </button>
                    )}
                    <div className="subtext document-breadcrumbs document-filter-breadcrumbs">
                        {folderParts.map((part, index) => {
                            const isLast = index === folderParts.length - 1;
                            const path = folderParts.slice(0, index + 1).join('/');
                            return (
                                <React.Fragment key={index}>
                                    {index > 0 && <ChevronRight size={16}/>}
                                    <span
                                        style={{ cursor: isLast ? 'default' : 'pointer', color: isLast ? 'inherit' : 'var(--primary-color)' }}
                                        onClick={() => !isLast && onBreadcrumbClick && onBreadcrumbClick(path)}
                                    >
                                        {part === "{{Name}}" ? caseName : part}
                                    </span>
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
                {splitArchiveSelection && (
                    <SplitArchiveChoiceModal
                        busy={splitArchiveStarting}
                        detection={splitArchiveSelection}
                        error={splitArchiveError}
                        onCancel={closeSplitArchiveChoice}
                        onKeepArchive={keepSplitArchiveOnly}
                        onPrepare={prepareSplitArchives}
                    />
                )}
                {showUploadModal && (
                    <UploadModal
                        onClose={() => setShowUploadModal(false)}
                        onUpload={(e) => handleUploadFiles(e)}
                        uploadWaiting={uploadWaiting}
                    />
                )}
                {showRenameModal && String(uploadSelectionCaseId) === String(case_id) && (
                    <Modal
                        title="Rename Files"
                        instructions="Follow DB naming conventions."
                        onClose={() => {
                            setShowRenameModal(false);
                            setShowUploadModal(false);
                        }}
                        header={
                            <div className='modal-header-actions'>
                                <button
                                    onClick={uploadRenamedFiles}
                                    className="action"
                                    disabled={(Boolean(onFileUpload) && skipExistingUploads && !duplicateCheckReady) || Boolean(findPendingRenameUploadDestination(
                                        uploadDestinationPath,
                                        filesToRename
                                    ))}
                                >
                                    Upload
                                </button>
                                <button onClick={() => {
                                    setShowRenameModal(false);
                                    setShowUploadModal(false);
                                }} className="action alt">Cancel</button>
                            </div>
                        }
                    >
                        <div className='modal-content-wrapper'>
                            <p className="subtext">Upload destination: {displayDocumentPath(uploadDestinationPath, caseName)}</p>
                            {onFileUpload && <div className="form-group full">
                                <Checkbox
                                    value={skipExistingUploads}
                                    onChange={() => {
                                        setUploadDuplicateCheck(null);
                                        setSkipExistingUploads(current => !current);
                                    }}
                                    label="Skip existing files"
                                    ariaLabel="Skip existing files"
                                    hint="Leave files already saved at the same folder path and filename unchanged. Upload only files that are missing."
                                />
                                {skipExistingUploads && (
                                    duplicateCheckReady ? <p className="subtext" role="status">
                                        {hiddenUploadItems.size} existing file(s) hidden and will be skipped. {uploadDuplicateCheck.total - hiddenUploadItems.size} file(s) remain to upload.
                                    </p> : duplicateCheckIsCurrent && uploadDuplicateCheck.status === 'error' ? <div role="alert">
                                        <p className="small-text error">{uploadDuplicateCheck.error}</p>
                                        <button type="button" className="action quaternary" onClick={() => setUploadDuplicateCheckRetry(value => value + 1)}>Retry check</button>
                                    </div> : <p className="subtext" role="status">
                                        Checking for existing files… {duplicateCheckIsCurrent ? uploadDuplicateCheck.completed : 0} of {filesToRename.filter(item => item.file).length} checked.
                                    </p>
                                )}
                            </div>}
                            <div className='rename-files' style={{ gap: '5px' }}>
                                {filesToRename.map((item, idx) => hiddenUploadItems.has(item) ? null : (
                                    <div key={`${idx}-${item}`} className="form-group full">
                                        <label className="subtext">{displayDocumentPath(item.displayPath || (item.file && item.relativePath) || item.originalName, caseName)}</label>
                                        <div className='file-rename-container'>
                                            <input
                                                type="text"
                                                value={item.newName}
                                                placeholder={decodeFileName(item.originalName)}
                                                onChange={(e) => {
                                                    const updated = [...filesToRename];
                                                    updated[idx] = { ...updated[idx], newName: e.target.value };
                                                    setFilesToRename(updated);
                                                }}
                                            />
                                                {item.file && !item.extension ? (
                                                    <div className="file-extension-input" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <span className="subtext" style={{ paddingBottom: "5px" }}>.</span>
                                                        <input
                                                            type="text"
                                                            value={item.newExtension ?? ''}
                                                            placeholder="ext"
                                                            onChange={(e) => {
                                                                const updated = [...filesToRename];
                                                                updated[idx] = { ...updated[idx], newExtension: e.target.value };
                                                                setFilesToRename(updated);
                                                            }}
                                                            style={{ maxWidth: '80px' }}
                                                        />
                                                    </div>
                                                ) : (
                                                    item.extension && <span className="subtext" style={{ paddingBottom: "5px"}}>.{item.extension}</span>
                                                )}
                                        </div>
                                        {!item.file && getFolderLeafNameValidationError(item.newName) && (
                                            <div className="small-text error" role="alert">
                                                {getFolderLeafNameValidationError(item.newName)}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Modal>
                )}
                {showConflictModal && (pendingOperation?.type !== 'upload' || isCurrentUploadSelection(pendingOperation.selection)) && (
                    <FileConflictModal
                        conflicts={conflictFiles}
                        onResolve={handleConflictResolution}
                        onSkipExisting={pendingOperation?.type === 'upload' ? handleSkipExistingConflicts : undefined}
                        onCancel={() => {
                            setShowConflictModal(false);
                            setPendingOperation(null);
                            setConflictFiles([]);
                        }}
                    />
                )}
                {renameTarget && (
                    <Modal
                        title="Rename"
                        instructions="Enter a new name for the item."
                        onClose={closeRenameDialog}
                        header={
                            <div className='modal-header-actions'>
                                <button
                                    onClick={performRename}
                                    className="action"
                                    disabled={renameSubmitting || !newNameInput.trim()}
                                >
                                    {renameSubmitting
                                        ? ((renameTarget.type === 'folder' || renameTarget.is_folder) ? 'Renaming folder...' : 'Renaming...')
                                        : 'Rename'}
                                </button>
                                <button
                                    onClick={closeRenameDialog}
                                    className="action alt"
                                    disabled={renameSubmitting && !(renameTarget.type === 'folder' || renameTarget.is_folder)}
                                >
                                    {renameSubmitting && (renameTarget.type === 'folder' || renameTarget.is_folder)
                                        ? 'Continue Working'
                                        : 'Cancel'}
                                </button>
                            </div>
                        }
                    >
                        <div className='modal-content-wrapper'>
                            <div className='rename-files'>
                                <div className='form-group full'>
                                    <label className="subtext">
                                        {renameTarget?.name ? decodeFileName(renameTarget.name) : ''}
                                    </label>
                                    <div className='file-rename-container'>
                                        <input
                                            type="text"
                                            value={newNameInput}
                                            onChange={(e) => {
                                                setNewNameInput(e.target.value);
                                                setRenameStatus(null);
                                            }}
                                            placeholder="New name"
                                            disabled={renameSubmitting}
                                        />
                                        {renameTarget &&
                                            (() => {
                                                const originalName = renameTarget.name || renameTarget.file_name;
                                                if (!originalName) return null;

                                                const isFolder = renameTarget.type === 'folder' || renameTarget.is_folder;
                                                if (isFolder) return null;

                                                // Decode the filename to get the real extension
                                                const decodedName = decodeFileName(originalName);
                                                if (decodedName.includes('.')) {
                                                    const extension = decodedName.split('.').pop();
                                                    return (
                                                        <span className="subtext" style={{ paddingBottom: "5px" }}>
                                                            .{extension}
                                                        </span>
                                                    );
                                                }
                                                return (
                                                    <div className="file-extension-input" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <span className="small-text">.</span>
                                                        <input
                                                            type="text"
                                                            value={newExtensionInput}
                                                            placeholder="ext"
                                                            onChange={(e) => setNewExtensionInput(e.target.value)}
                                                            style={{ maxWidth: '80px' }}
                                                        />
                                                    </div>
                                                );
                                            })()}
                                    </div>
                                    {renameStatus && (
                                        <div
                                            className={`small-text${renameStatus.type === 'error' ? ' error' : ''}`}
                                            style={renameStatus.type === 'error' ? undefined : { color: 'var(--subtext-color)' }}
                                            role={renameStatus.type === 'error' ? 'alert' : 'status'}
                                        >
                                            {renameStatus.text}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Modal>
                )}
                {showMoveModal && (
                    <Modal
                        title={copyFiles ? "Copy Files" : "Move Files"}
                        instructions="Select a destination folder and choose whether to move or copy the files."
                        onClose={() => {
                            setShowMoveModal(false);
                            setCopyFiles(false);
                            setMoveTargetFolder('');
                        }}
                        footer={
                            <div className='modal-footer-actions'>
                                <button 
                                    onClick={moveProcessing ? undefined : handleMove} 
                                    className={`action ${moveProcessing ? 'disabled' : ''}`}
                                    disabled={moveProcessing || selectedKeys.length === 0 || !moveTargetFolder}
                                    style={{ 
                                        pointerEvents: moveProcessing ? 'none' : 'auto',
                                        opacity: moveProcessing ? 0.6 : 1
                                    }}
                                >
                                    {copyFiles ? (moveProcessing ? 'Copying...' : 'Copy') : (moveProcessing ? 'Moving...' : 'Move')}
                                </button>
                                <button onClick={() => {
                                    setShowMoveModal(false);
                                    setCopyFiles(false);
                                }} className="action alt">Cancel</button>
                            </div>
                        }
                        overflowy={true}
                    >
                        <div className='modal-content-wrapper'>
                            <div className='form-group full'>
                                <label className="subtext">
                                    Destination Folder
                                </label>
                                <div className='move-folder-actions'>
                                    <SearchSelect
                                        value={moveTargetFolder}
                                        onChange={setMoveTargetFolder}
                                        options={Object.fromEntries(availableFolders.map(folder => [folder.path, formatSearchSelectPath(folder.path || folder.displayPath)]))}
                                        placeholder="Select destination folder..."
                                        minMatchScore={0.5}
                                    />
                                    <button
                                        className="action tertiary"
                                        style={{ backgroundColor: 'var(--background-color)' }}
                                        onClick={() => {
                                            setMovePickerInitialPath(getCurrentFolderPathForPicker());
                                            setShowMoveFolderPicker(true);
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = 'var(--fill)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = 'var(--border-color)';
                                        }}
                                    >
                                        Browse...
                                    </button>
                                </div>
                            </div>
                            <div className='form-group nm full' style={{ display: 'flex', justifyContent: 'flex-end' }}>

                            </div>
                            <div className='form-group nm full'>
                                <Checkbox
                                    label="Copy files instead of moving them"
                                    value={copyFiles}
                                    onChange={() => setCopyFiles(prev => !prev)}
                                />
                            </div>
                            <div className='selected-files-list'>
                                <label className="subtext">Selected Items ({selectedKeys.length})</label>
                                <div className="selected-files" style={{ backgroundColor: 'var(--background-color)' }}>
                                    {selectedKeys.slice(0, 5).map((key, index) => {
                                        const fullKey = formatSearchSelectPath(normalizeSelectedKeyDisplay(key));
                                        return (
                                            <div key={index} className="selected-file">
                                                {fullKey}
                                            </div>
                                        );
                                    })}
                                    {selectedKeys.length > 5 && (
                                        <div className="selected-file">
                                            +{selectedKeys.length - 5} more...
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Modal>
                )}
                {showBatesFolderPicker && (
                    <MoveFolderPicker
                        isOpen={showBatesFolderPicker}
                        onClose={() => setShowBatesFolderPicker(false)}
                        onSelect={(path) => {
                            ensureFolderOption(path);
                            setBatesSettings(prev => ({
                                ...prev,
                                destination: { ...prev.destination, path }
                            }));
                            setShowBatesFolderPicker(false);
                        }}
                        fetchFolderContents={fetchFolderContents}
                        caseName={caseName}
                        currentFolderPath={getCurrentFolderPathForPicker()}
                        initialPath={batesSettings.destination.path || '{{Name}}'}
                        initialSelection={batesSettings.destination.path || ''}
                    />
                )}
                {showMergeFolderPicker && (
                    <MoveFolderPicker
                        isOpen={showMergeFolderPicker}
                        onClose={() => setShowMergeFolderPicker(false)}
                        onSelect={(path) => {
                            ensureFolderOption(path);
                            setMergeSettings(prev => ({
                                ...prev,
                                targetFolder: path
                            }));
                            setShowMergeFolderPicker(false);
                        }}
                        fetchFolderContents={fetchFolderContents}
                        caseName={caseName}
                        currentFolderPath={getCurrentFolderPathForPicker()}
                        initialPath={mergeSettings.targetFolder || getCurrentFolderPathForPicker()}
                        initialSelection={mergeSettings.targetFolder || getCurrentFolderPathForPicker()}
                    />
                )}
                {deleteModal && (
                    <Modal
                        title="Delete Folder/File"
                        onClose={() => setDeleteModal(false)}
                        footer={
                            <div className='modal-footer-actions'>
                                <button type="button" onClick={() => handleBulkDelete('delete')} className="action destroy">Delete</button>
                                <button type="button" onClick={() => setDeleteModal(false)} className="action alt">Cancel</button>
                            </div>
                        }
                    >
                        <div className='modal-content-wrapper document-delete-confirmation'>
                            <p className='small-text document-delete-confirmation-prompt'>
                                Are you sure you want to delete {selectedKeys.length} selected {selectedKeys.length === 1 ? 'item' : 'items'}? Selections from other folders are included.
                            </p>
                            <div
                                className="document-delete-confirmation-list"
                                role="list"
                                aria-label="Items selected for deletion"
                                tabIndex="0"
                            >
                                {buildDocumentSelectionDisplayRows(selectedKeys, folderName).map((row) => {
                                    const isFolder = row.key.endsWith('/');
                                    return (
                                        <div className="document-delete-confirmation-row" role="listitem" key={row.key}>
                                            <span className="document-delete-confirmation-icon" aria-hidden="true">
                                                {isFolder ? <Folder size={16} /> : <FileText size={16} />}
                                            </span>
                                            <span className="document-delete-confirmation-copy">
                                                <span className="document-delete-confirmation-name">{decodeFileName(row.name)}</span>
                                                <span className="small-text">{decodeFileName(row.parentPath || caseName || 'Case documents')}</span>
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                            <p className='small-text document-delete-confirmation-warning'>This action cannot be undone.</p>
                            {deleteInProgress && (
                                <p className='small-text document-delete-confirmation-progress' role="status">
                                    A delete is in progress. Please keep this tab open.
                                </p>
                            )}
                        </div>
                    </Modal>
                )}
                {showMergeDownloadModal && mergeResult && (
                    <Modal
                        title={isMerging ? "Merging Documents..." : "Document Merged Successfully"}
                        onClose={isMerging ? null : () => {
                            setShowMergeDownloadModal(false);
                            setMergeResult(null);
                            setSelectedKeys([]);
                            setActiveFile([]);
                        }}
                        footer={
                            <div className='modal-footer-actions'>
                                <button 
                                    onClick={() => downloadMergedFile(mergeResult)} 
                                    className="action"
                                    disabled={isMerging}
                                >
                                    {isMerging ? 'Merging...' : 'Download Copy'}
                                </button>
                                <button 
                                    onClick={() => {
                                        setShowMergeDownloadModal(false);
                                        setMergeResult(null);
                                        setSelectedKeys([]);
                                        setActiveFile([]);
                                    }} 
                                    className="action alt"
                                    disabled={isMerging}
                                >
                                    No Thanks
                                </button>
                            </div>
                        }
                    >
                        <div className='modal-content-wrapper'>
                            <p className='subtext' style={{ textAlign: 'center', width: '100%' }}>
                                Document saved successfully to: <strong>{mergeResult.s3_path}</strong>
                            </p>
                            <p className='subtext' style={{ textAlign: 'center', width: '100%' }}>
                                Would you also like to download a copy to your computer?
                            </p>
                        </div>
                    </Modal>
                )}
                {/* Enhanced Search Results Header */}
                {searchAllFiles && searchResults.length > 0 && (
                    <div className="search-results-header">
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>
                            <div>
                                <p style={{ margin: 0, fontSize: '14px', color: 'var(--subtext-color)' }}>
                                    Found <strong>{searchPagination.total_results?.toLocaleString()}</strong> {searchTypeLabel.toLowerCase()} result{searchPagination.total_results === 1 ? '' : 's'} for "<strong>{searchQuery}</strong>"
                                </p>
                            </div>
                            <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: 'var(--subtext-color)' }}>
                                <span>
                                    {searchScope === 'case' ? 'Whole case' : 'Current folder'} · Showing {searchResults.length} of {searchPagination.total_results?.toLocaleString()}
                                    {searchPerformance && (
                                        <> in {searchPerformance.execution_time}ms</>
                                    )}
                                </span>
                                {searchPagination.has_next && (
                                    <span>More results available</span>
                                )}
                            </div>
                        </div>
                    
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', alignItems: 'center' }}>
                            <button 
                                className="action alt"
                                onClick={() => {
                                    setSearchQuery('');
                                    setSearchResults([]);
                                    setSearchAllFiles(false);
                                }}
                                style={{ fontSize: '12px', padding: '6px 12px' }}
                            >
                                Clear Search
                            </button>
                            
                            {searchPagination.has_next && (
                                <button 
                                    className="action"
                                    onClick={loadMoreSearchResults}
                                    disabled={isSearching}
                                    style={{ fontSize: '12px', padding: '6px 12px' }}
                                >
                                    {isSearching ? (
                                        <>
                                            <span style={{ marginRight: '8px' }}>⟳</span>
                                            Loading...
                                        </>
                                    ) : (
                                        <>Load More Results</>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                )}
                
                <div className="document-content" style={{ display: 'flex', flex: hasDocumentPagination ? '1 1 0' : undefined, flexDirection: showSidePreview ? 'row' : 'column', alignItems: 'flex-start' }}>
                    <div className="document-table-container" style={{ flex: showSidePreview || hasDocumentPagination ? '1 1 0' : 'none', alignSelf: showSidePreview && hasDocumentPagination ? 'stretch' : undefined, minWidth: 0, position: 'relative' }}>
                    {(isLoading || isSearching || isSearchQueued) && (
                        <div
                            className="document-loading"
                            style={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '10px',
                                padding: '12px',
                                background: 'var(--background-color)',
                                zIndex: 2
                            }}
                        >
                            <Loader2 size={18} className="spinner" />
                            <span className="subtext">{isSearching ? 'Searching documents...' : isSearchQueued ? 'Preparing search...' : 'Loading documents...'}</span>
                        </div>
                    )}
                    {filteredItems.length > 0 ? (
                        view === 0 ? (
                            <table className={`exhibits ${windowWidth <= 900 ? 'hide-author' : ''} ${windowWidth <= 767 ? 'hide-date' : ''} ${windowWidth <= 600 ? 'hide-size' : ''} ${pagination?.total_pages > 1 ? 'paginated' : ''}`}>
                                <thead style={{ width: '100%' }}>
                                    <tr>
                                        <th className="file-name">
                                            <div className='file-select'>
                                                <Checkbox
                                                    value={
                                                        filteredItems.length > 0 &&
                                                        filteredItems.every(item => item?.key && (selectedKeys?.includes?.(item.key) ?? false))
                                                    }
                                                    onChange={() => {
                                                        const allKeys = Array.from(new Set(filteredItems.map(item => item.key).filter(Boolean)));
                                                        const newSelection = toggleVisibleDocumentSelection({ selectedKeys, visibleKeys: allKeys });
                                                        
                                                        setSelectedKeys(newSelection);
                                                        setActiveFile(newSelection);
                                                    }}
                                                    ariaLabel="Select or deselect all visible documents and folders"
                                                    mini
                                                />
                                            </div>
                                            <span 
                                                className="sortable-header" 
                                                onClick={() => handleSort('name')}
                                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                                            >
                                                Name
                                                {activeSortColumn === 'name' ? (
                                                    activeSortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                                                ) : (
                                                    <ArrowUpDown size={14} style={{ opacity: 0.5 }} />
                                                )}
                                            </span>
                                        </th>
                                        <th className="file-date">
                                            <span 
                                                className="sortable-header" 
                                                onClick={() => handleSort('date')}
                                                style={{ cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px' }}
                                            >
                                                Last Modified
                                                {activeSortColumn === 'date' ? (
                                                    activeSortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                                                ) : (
                                                    <ArrowUpDown size={14} style={{ opacity: 0.5 }} />
                                                )}
                                            </span>
                                        </th>
                                        <th className="file-size">
                                                <span 
                                                    className="sortable-header" 
                                                    onClick={() => handleSort('size')}
                                                    style={{ cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px' }}
                                                >
                                                    Size
                                                    {activeSortColumn === 'size' ? (
                                                        activeSortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                                                    ) : (
                                                        <ArrowUpDown size={14} style={{ opacity: 0.5 }} />
                                                    )}
                                                </span>
                                            </th>
                                        <th className="file-author">
                                            <span 
                                                className="sortable-header" 
                                                onClick={() => handleSort('author')}
                                                style={{ cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px' }}
                                            >
                                                Author
                                                {activeSortColumn === 'author' ? (
                                                    activeSortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                                                ) : (
                                                    <ArrowUpDown size={14} style={{ opacity: 0.5 }} />
                                                )}
                                            </span>
                                        </th>
                                        <th className="file-actions">...</th>
                                    </tr>
                                </thead>
                                <tbody style={{ width: '100%' }}>
                                    {filteredItems.map((item, index) => {
                                        if (item.type === 'folder' && !hotDocsOnly) {
                                        return (
                                            <EntityHoverPreview
                                                as="tr"
                                                key={item.key || `folder-${item.name}`}
                                                entityType="folder"
                                                entityId={getFolderPreviewPath(item)}
                                                entityName={item.name ? decodeFileName(item.name) : 'Folder'}
                                                entitySource={case_id}
                                                excludeSelector=".file-actions"
                                                draggable
                                                onDragStart={(event) => handleDocumentDragStart(event, 'folder', item)}
                                                onDragEnd={clearDocumentDragPreview}
                                                onContextMenu={handleDocumentDragContextMenu}
                                                onClick={(event) => {
                                                    markLiveItemSeen('folder', item);
                                                    if (!applyItemSelectionGesture(event, item.key)) {
                                                        handleFolderClick(item);
                                                    }
                                                }}
                                                onDoubleClick={(event) => {
                                                    if (event.shiftKey || event.ctrlKey || event.metaKey) {
                                                        event.preventDefault();
                                                        event.stopPropagation();
                                                        return;
                                                    }
                                                    if (windowWidth < 768) {
                                                        onFolderClick && onFolderClick(item.name);
                                                    } else {
                                                        handleFolderDoubleClick(item);
                                                    }
                                                }}
                                                aria-selected={selectedKeys?.includes?.(item.key) ?? false}
                                                title={`Shift-click to extend selection. Drag to move${selectedKeys.length > 1 ? ' selected items' : ''}; Option-drag on Mac or Ctrl-drag on Windows to copy.`}
                                                className={`exhibit ${activeFile.includes(item.key) ? 'active-file' : ''} ${more === `${index}sub` ? 'menu-active' : ''} ${(deleteInProgress && (selectedKeys?.includes?.(item.key) ?? false)) ? 'pending-delete' : ''}`}
                                                style={{ cursor: 'grab' }}
                                            >
                                                <td className="file-name folder subtext">
                                                    <div className="file-select">
                                                        <Checkbox
                                                            value={selectedKeys?.includes?.(item.key) ?? false}
                                                            onChange={(e) => {
                                                                markLiveItemSeen('folder', item);
                                                                applyItemSelectionGesture(e, item.key, { forceToggle: true });
                                                            }}
                                                            ariaLabel={`Select folder ${decodeFileName(item.name || '')}`}
                                                            mini
                                                        />
                                                    </div>
                                                    {getDocumentItemSizeBytes(item) > 0 ? <Folder size={16} fill='var(--text-color)' /> : <Folder size={16}/>}
                                                    <div className='file-preview-trigger'>
                                                        {searchAllFiles ? 
                                                            <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-color)' }}>
                                                                {(() => {
                                                                    const decodedName = decodeFileName(item.name);
                                                                    return decodedName.length > 65
                                                                        ? `${decodedName.split(".")[0].slice(0, 35)}(...)`
                                                                        : decodedName;
                                                                })()}
                                                            </span> : item.name ? decodeFileName(item.name) : ''}
                                                            {isFolderSyncPoint(getFolderKeyFromItem(item)) && syncStatus.pending && (
                                                                <span className="sync-status-icon" title="Syncing...">
                                                                    <CloudBackup size={16} />
                                                                </span>
                                                            )}
                                                    </div>
                                                    {searchAllFiles && (
                                                        <div className="file-location">
                                                            <ArrowRight size={12} className="location-icon" />
                                                            {renderBreadcrumbs(item.folder_path, caseName)}
                                                        </div>
                                                    )}
                                                </td>
                                                <td
                                                    className="file-date folder subtext"
                                                    title={isLiveNewItem('folder', item) ? 'New folder added since this page was opened' : (item.last_modified ? formatLastModified(item.last_modified)[5] : '')}
                                                >
                                                    {isLiveNewItem('folder', item)
                                                        ? <span className="document-live-new">New</span>
                                                        : (item.last_modified ? formatLastModified(item.last_modified)[0] : '-')}
                                                </td>
                                                <td className="file-size folder subtext">{item.size} items</td>
                                                <td className="file-author folder subtext">-</td>
                                                <td className="file-actions subtext">
                                                    <span className="file-actions-dots"
                                                        data-menu-index={`${index}sub`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const menuId = `${index}sub`;
                                                            const position = calculateMenuPosition(index, true);
                                                            setMenuPositionAbove(prev => ({
                                                                ...prev,
                                                                [menuId]: position.showAbove
                                                            }));
                                                            setMenuPosition(prev => ({
                                                                ...prev,
                                                                [menuId]: { left: position.left, top: position.top }
                                                            }));
                                                            setMore(more === menuId ? null : menuId);
                                                        }}
                                                    >...</span>
                                                    {more === `${index}sub` && typeof document !== 'undefined' && createPortal(
                                                        <div 
                                                            className={`file-actions-menu ${menuPositionAbove[`${index}sub`] ? 'position-above' : ''}`} 
                                                            ref={menuRef}
                                                            style={{
                                                                left: menuPosition[`${index}sub`]?.left || 0,
                                                                top: menuPosition[`${index}sub`]?.top || 0
                                                            }}
                                                        >
                                                            <span className='file-actions-menu-child' onClick={(e) => {
                                                                e.stopPropagation();
                                                                generateShareLink(item);
                                                                setMore(null);
                                                            }}>
                                                                <span>Share Link</span><Link />
                                                            </span>
                                                            <span className='file-actions-menu-child' onClick={() => {
                                                                if (!(selectedKeys?.includes?.(item.key) ?? false)) {
                                                                    setSelectedKeys([item.key]);
                                                                    setActiveFile([item.key]);
                                                                }
                                                                handleBulkDownload();
                                                            }}>
                                                                <span>Download</span><Download />
                                                            </span>
                                                            <span className="file-actions-menu-child" onClick={(e) => {
                                                                e.stopPropagation();
                                                                const folderKey = item.key || getFolderKeyFromItem(item);
                                                                const targets = buildSelectedFolderInfoTargets(
                                                                    [folderKey],
                                                                    [{ ...item, key: folderKey, type: 'folder' }]
                                                                );
                                                                if (targets.length > 0) {
                                                                    setFolderInfoTargets(targets);
                                                                }
                                                                setMore(null);
                                                            }}>
                                                                <span>Folder Information</span><Info />
                                                            </span>
                                                            <span className="file-actions-menu-child" onClick={() => handleRenameClick(item)}>
                                                                <span>Rename</span><Pencil />
                                                            </span>
                                                            <span className="file-actions-menu-child" onClick={(e) => {
                                                                e.stopPropagation();
                                                                const key = item.key || `cases/${case_id}/{{Name}}/`;
                                                                setSelectedKeys([key]);
                                                                setActiveFile([key]);
                                                                openMoveModal();
                                                                setMore(null);
                                                            }}>
                                                                <span>Move/Copy</span><DriveFileMoveRounded />
                                                            </span>
                                                            {isParentCase && (
                                                                <span className="file-actions-menu-child" onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    const key = getFolderKeyFromItem(item);
                                                                    if (key) {
                                                                        toggleFolderSyncPoint(key);
                                                                    }
                                                                    setMore(null);
                                                                }}>
                                                                    <span>{isFolderSyncPoint(getFolderKeyFromItem(item)) ? 'Remove Sync Point' : 'Mark as Sync Point'}</span><RefreshCcw />
                                                                </span>
                                                            )}
                                                            <span
                                                                className="file-actions-menu-child"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    const key = item.key || `cases/${case_id}/{{Name}}/`;
                                                                    if (!(selectedKeys?.includes?.(key) ?? false)) {
                                                                        setSelectedKeys([...(selectedKeys || []), key]);
                                                                        setActiveFile([...activeFile, key]);
                                                                    }
                                                                    setDeleteModal(true);
                                                                }}
                                                            >
                                                                <span>Delete</span>
                                                                <Trash />
                                                            </span>
                                                        </div>,
                                                        document.body
                                                    )}
                                                </td>
                                            </EntityHoverPreview>
                                        );
                                        } else {
                                            const file = item.file;
                                            const resolvedFileId = Number(
                                                file.id
                                                || file.file_id
                                                || metaData?.find(metaFile => metaFile.file_path === file.key)?.id
                                                || 0
                                            );

                                            return (
                                                <EntityHoverPreview
                                                    as="tr"
                                                    key={`file-${index}`}
                                                    entityType="document"
                                                    entityId={resolvedFileId}
                                                    entityName={file?.name ? decodeFileName(file.name) : ''}
                                                    entitySource={file?.key || file?.file_path || file?.path || file?.name || ''}
                                                    entitySize={file?.size || 0}
                                                    excludeSelector=".file-actions"
                                                    draggable
                                                    onDragStart={(event) => handleDocumentDragStart(event, 'file', file)}
                                                    onDragEnd={clearDocumentDragPreview}
                                                    onContextMenu={handleDocumentDragContextMenu}
                                                    onClick={(event) => {
                                                        markLiveItemSeen('file', file);
                                                        if (!applyItemSelectionGesture(event, file.key)) {
                                                            handleFileClick(file);
                                                        }
                                                    }}
                                                    onDoubleClick={(event) => {
                                                        if (event.shiftKey || event.ctrlKey || event.metaKey) {
                                                            event.preventDefault();
                                                            event.stopPropagation();
                                                            return;
                                                        }
                                                        handleFileDoubleClick(file);
                                                    }}
                                                    aria-selected={selectedKeys?.includes?.(file?.key) ?? false}
                                                    title={`Shift-click to extend selection. Drag to move${selectedKeys.length > 1 ? ' selected items' : ''}; Option-drag on Mac or Ctrl-drag on Windows to copy.`}
                                                    className={`exhibit ${activeFile.includes(file?.key) ? 'active-file' : ''} ${more === index ? 'menu-active' : ''} ${(deleteInProgress && (selectedKeys?.includes?.(file?.key) ?? false)) ? 'pending-delete' : ''}`}
                                                    style={{ cursor: 'grab' }}
                                                >
                                                    <td className="file-name subtext">
                                                        <div className="file-select">
                                                            <Checkbox
                                                                value={selectedKeys?.includes?.(file?.key) ?? false}
                                                                onChange={(e) => {
                                                                    markLiveItemSeen('file', file);
                                                                    applyItemSelectionGesture(e, file.key, { forceToggle: true });
                                                                }}
                                                                ariaLabel={`Select document ${decodeFileName(file?.name || '')}`}
                                                                mini
                                                            />
                                                        </div>
                                                        <FileSvg size={16} />
                                                        <div
                                                            className="subtext file-preview-trigger"
                                                            style={{ flexDirection: searchAllFiles ? 'column' : 'row', alignItems: searchAllFiles ? 'flex-start' : 'center', flexWrap: 'wrap', textWrap: 'wrap', padding: searchAllFiles ? '4px 0' : '0', gap: searchAllFiles ? '2.5px 0' : '' }}
                                                        >
                                                            {searchAllFiles ? (
                                                                <span style={{ fontWeight: '600', color: 'var(--text-color)' }}>
                                                                    {file?.name ? decodeFileName(file.name) : ''}
                                                                </span>
                                                            ) : (
                                                                file?.name ? 
                                                                    decodeFileName(file.name) 
                                                                    : ''
                                                            )}
                                                            {(() => {
                                                                const path = file?.key || file?.file_path;
                                                                const entry = path ? syncFileStatus[path] : null;
                                                                if (!entry) return null;
                                                                const isPending = !entry.processed_at;
                                                                return (
                                                                    <span className="sync-status-icon" title={isPending ? 'Syncing...' : 'Synced'}>
                                                                        {isPending ? <CloudBackup size={14} /> : <CloudCheck size={14} />}
                                                                    </span>
                                                                );
                                                            })()}
                                                            {hotDocsList.has(file?.key || file?.file_path) && (
                                                                <Star size={16} fill="var(--subtext-color)" />
                                                            )}
                                                            {searchAllFiles && (
                                                                <div className="file-location">
                                                                    <ArrowRight size={12} className="location-icon" />
                                                                    {renderBreadcrumbs(file?.folder_path, caseName)}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {metaData?.find(metaFile => metaFile?.file_path === file?.key)?.new 
                                                            && 
                                                            <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" fill="var(--fill)" className='new-tag'>
                                                                <path d="M44,14H4a2,2,0,0,0-2,2V32a2,2,0,0,0,2,2H44a2,2,0,0,0,2-2V16A2,2,0,0,0,44,14ZM17.3,29H14.8l-3-5-.7-1.3h0V29H8.7V19h2.5l3,5,.6,1.3h.1s-.1-1.2-.1-1.6V19h2.5Zm9.1,0H18.7V19h7.6v2H21.2v1.8h4.4v2H21.2v2.1h5.2Zm10.9,0H34.8l-1-4.8c-.2-.8-.4-1.9-.4-1.9h0s-.2,1.1-.3,1.9L32,29H29.6L26.8,19h2.5l1,4.2a20.1,20.1,0,0,1,.5,2.5h0l.5-2.4,1-4.3h2.3l.9,4.3.5,2.4h0l.5-2.5,1-4.2H40Z"/>
                                                            </svg>
                                                        }
                                                    </td>
                                                    <td
                                                        className="file-date subtext"
                                                        title={isLiveNewItem('file', file) ? 'New document added since this page was opened' : (file?.last_modified ? formatLastModified(file.last_modified)[5] : '')}
                                                    >
                                                        {isLiveNewItem('file', file)
                                                            ? <span className="document-live-new">New</span>
                                                            : (file?.last_modified ? formatLastModified(file.last_modified)[0] : '-')}
                                                    </td>
                                                    <td className="file-size subtext">
                                                        {formatSize(file?.size)}
                                                    </td>
                                                    <td className="file-author subtext">
                                                        {(() => {
                                                        const meta = metaData?.find(metaFile => metaFile?.file_path === file?.key);
                                                        return userMap[meta?.author_id] || "-";
                                                        })()}
                                                    </td>
                                                    <td className='file-actions subtext'>
                                                        <span className='file-actions-dots' 
                                                            data-menu-index={index}
                                                            onClick={(e) => { 
                                                                e.stopPropagation(); 
                                                                const position = calculateMenuPosition(index, false);
                                                                setMenuPositionAbove(prev => ({
                                                                    ...prev,
                                                                    [index]: position.showAbove
                                                                }));
                                                                setMenuPosition(prev => ({
                                                                    ...prev,
                                                                    [index]: { left: position.left, top: position.top }
                                                                }));
                                                                setMore(more === index ? null : index); 
                                                            }}
                                                        >...</span>
                                                        {more === index && typeof document !== 'undefined' && createPortal(
                                                            <div 
                                                                className={`file-actions-menu ${menuPositionAbove[index] ? 'position-above' : ''}`} 
                                                                ref={menuRef}
                                                                style={{
                                                                    left: menuPosition[index]?.left || 0,
                                                                    top: menuPosition[index]?.top || 0
                                                                }}
                                                            >
                                                                <span className='file-actions-menu-child' onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    generateShareLink({...item, key: file.key});
                                                                    setMore(null);
                                                                }}>
                                                                    <span>Share Link</span><Link />
                                                                </span>
                                                                <span className='file-actions-menu-child' onClick={() => {handleDownload(file); setMore(null);}}>
                                                                    <span>Download</span><Download />
                                                                </span>
                                                                <span className='file-actions-menu-child' onClick={() => {handleViewFile(file); setMore(null);}}>
                                                                    <span>View File</span><Eye />
                                                                </span>
                                                                {canRedactPdfFile(file?.name) && (
                                                                    <span className='file-actions-menu-child' onClick={() => {handleRedactFile(file); setMore(null);}}>
                                                                        <span>Redact</span><ScanLine />
                                                                    </span>
                                                                )}
                                                                {isAudioFile(file.name) && (
                                                                    <span className='file-actions-menu-child' onClick={() => {handleTranscribeAudio(file); setMore(null);}}>
                                                                        <span>Transcribe Audio</span><FileText />
                                                                    </span>
                                                                )}
                                                                <span className='file-actions-menu-child' onClick={() => {handleEditDocument(file); setMore(null);}}>
                                                                    <span>Edit</span><Edit />
                                                                </span>
                                                                {canConvertToPdfFile(file?.name) && (
                                                                    <span className='file-actions-menu-child' onClick={() => {handleConvertToPdf(file); setMore(null);}}>
                                                                        <span>Convert to PDF</span><FileText />
                                                                    </span>
                                                                )}
                                                                <span className="file-actions-menu-child" onClick={() => {handleRenameClick(file); setMore(null);}}>
                                                                    <span>Rename</span><Pencil />
                                                                </span>
                                                                <span className="file-actions-menu-child"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setMore(null);
                                                                        
                                                                        // Select this file for deletion and immediately show modal
                                                                        setSelectedKeys([file.key]);
                                                                        setActiveFile([file.key]);
                                                                        setDeleteModal(true);
                                                                    }}
                                                                >
                                                                    <span>Delete</span><Trash />
                                                                </span>
                                                                <span className='file-actions-menu-child' onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedKeys([file.key]);
                                                                    setActiveFile([file.key]);
                                                                    openMoveModal();
                                                                    setMore(null);
                                                                }}>
                                                                    <span>Move/Copy</span><Move />
                                                                </span>
                                                                <span className='file-actions-menu-child' onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleHotDoc(file);
                                                                    setMore(null);
                                                                }}>
                                                                    <span>
                                                                        {hotDocsList.has(file.key || file.file_path) ? 'Remove from Hot Docs' : 'Add to Hot Docs'}
                                                                    </span>
                                                                    <Star fill={hotDocsList.has(file.key || file.file_path) ? 'var(--subtext-color)' : 'none'} />
                                                                </span>
                                                            </div>,
                                                            document.body
                                                        )}
                                                    </td>
                                                </EntityHoverPreview>
                                            );
                                        }
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            <div className="document-grid">
                                {filteredItems.map((item, index) => {
                                    if (item.type === 'folder' && !hotDocsOnly) {
                                        return (
                                            <EntityHoverPreview
                                                as="div"
                                                key={item.key || `folder-${item.name}`}
                                                entityType="folder"
                                                entityId={getFolderPreviewPath(item)}
                                                entityName={item.name ? decodeFileName(item.name) : 'Folder'}
                                                entitySource={case_id}
                                                draggable
                                                onDragStart={(event) => handleDocumentDragStart(event, 'folder', item)}
                                                onDragEnd={clearDocumentDragPreview}
                                                onContextMenu={handleDocumentDragContextMenu}
                                                className={`doc-expanded ${activeFile.includes(item.key) ? 'active-file' : ''}`}
                                                onClick={(event) => {
                                                    markLiveItemSeen('folder', item);
                                                    if (!applyItemSelectionGesture(event, item.key)) {
                                                        handleFolderClick(item);
                                                    }
                                                }}
                                                onDoubleClick={(event) => {
                                                    if (event.shiftKey || event.ctrlKey || event.metaKey) {
                                                        event.preventDefault();
                                                        event.stopPropagation();
                                                        return;
                                                    }
                                                    handleFolderDoubleClick(item);
                                                }}
                                                aria-selected={selectedKeys?.includes?.(item.key) ?? false}
                                                title="Shift-click to extend selection. Drag to move; Option-drag on Mac or Ctrl-drag on Windows to copy."
                                                style={{ cursor: 'grab' }}
                                            >
                                                    <div className="expanded-folder subtext">
                                                    <FolderOpenIcon size={25} />
                                                    {decodeFileName(item.name)}
                                                </div>
                                                <div className="expanded-details">
                                                    <div className="expanded-date">
                                                        <span className="subtext">Last Updated:</span>
                                                        <span className="subtext-alt">
                                                            {isLiveNewItem('folder', item)
                                                                ? <span className="document-live-new">New</span>
                                                                : (item.last_modified ? formatLastModified(item.last_modified)[5] : '-')}
                                                        </span>
                                                    </div>
                                                    <div className="expanded-size">
                                                        <span className="subtext">Items:</span>
                                                        <span className="subtext-alt">{item.size}</span>
                                                    </div>
                                                </div>
                                            </EntityHoverPreview>
                                        );
                                    } else {
                                        const file = item.file;
                                        const resolvedFileId = Number(
                                            file.id
                                            || file.file_id
                                            || metaData?.find(metaFile => metaFile.file_path === file.key)?.id
                                            || 0
                                        );
                                        return (
                                        <div 
                                            key={`file-${index}`} 
                                            draggable
                                            onDragStart={(event) => handleDocumentDragStart(event, 'file', file)}
                                            onDragEnd={clearDocumentDragPreview}
                                            onContextMenu={handleDocumentDragContextMenu}
                                            id={resolvedFileId > 0 ? `document-file-${resolvedFileId}` : undefined}
                                            data-file-id={resolvedFileId > 0 ? resolvedFileId : undefined}
                                            data-file-name={file.name || ''}
                                            className={`doc-expanded ${activeFile.includes(file.key) ? 'active-file' : ''} ${more === index ? 'menu-active' : ''}`}
                                            aria-selected={selectedKeys?.includes?.(file.key) ?? false}
                                            title="Shift-click to extend selection. Drag to move; Option-drag on Mac or Ctrl-drag on Windows to copy."
                                            style={{ cursor: 'grab' }}
                                        >
                                            <div className="expanded-file">
                                                <EntityHoverPreview
                                                    as="div"
                                                    entityType="document"
                                                    entityId={resolvedFileId}
                                                    entityName={file?.name ? decodeFileName(file.name) : ''}
                                                    entitySource={file?.key || file?.file_path || file?.path || file?.name || ''}
                                                    entitySize={file?.size || 0}
                                                    onClick={(event) => {
                                                        markLiveItemSeen('file', file);
                                                        if (!applyItemSelectionGesture(event, file.key)) {
                                                            handleFileClick(file);
                                                        }
                                                    }}
                                                    onDoubleClick={(event) => {
                                                        if (event.shiftKey || event.ctrlKey || event.metaKey) {
                                                            event.preventDefault();
                                                            event.stopPropagation();
                                                            return;
                                                        }
                                                        handleFileDoubleClick(file);
                                                    }}
                                                    className="subtext file-preview-trigger"
                                                    style={{ cursor: 'pointer', width: 'auto' }}
                                                >
                                                    <FileSvg size={25} style={{ marginRight: 6 }} />
                                                    {decodeFileName(file.name)}
                                                    {(() => {
                                                        const path = file?.key || file?.file_path;
                                                        const entry = path ? syncFileStatus[path] : null;
                                                        if (!entry) return null;
                                                        const isPending = !entry.processed_at;
                                                        return (
                                                            <span className="sync-status-icon" title={isPending ? 'Syncing...' : 'Synced'}>
                                                                {isPending ? <CloudBackup size={14} /> : <CloudCheck size={14} />}
                                                            </span>
                                                        );
                                                    })()}
                                                    {metaData?.find(metaFile => metaFile.file_path === file.key)?.new 
                                                        && 
                                                        <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" fill="var(--fill)" className='new-tag'>
                                                            <path d="M44,14H4a2,2,0,0,0-2,2V32a2,2,0,0,0,2,2H44a2,2,0,0,0,2-2V16A2,2,0,0,0,44,14ZM17.3,29H14.8l-3-5-.7-1.3h0V29H8.7V19h2.5l3,5,.6,1.3h.1s-.1-1.2-.1-1.6V19h2.5Zm9.1,0H18.7V19h7.6v2H21.2v1.8h4.4v2H21.2v2.1h5.2Zm10.9,0H34.8l-1-4.8c-.2-.8-.4-1.9-.4-1.9h0s-.2,1.1-.3,1.9L32,29H29.6L26.8,19h2.5l1,4.2a20.1,20.1,0,0,1,.5,2.5h0l.5-2.4,1-4.3h2.3l.9,4.3.5,2.4h0l.5-2.5,1-4.2H40Z"/>
                                                        </svg>
                                                    }
                                                </EntityHoverPreview>
                                                <div className='expanded-tags'>
                                                    {metaData?.find(metaFile => metaFile.file_path === file.key)?.tags 
                                                        && JSON.parse(metaData?.find(metaFile => metaFile.file_path === file.key)?.tags)?.map((tag, idx) => (
                                                        <span key={idx} className='tag caps'>
                                                            #{tag}
                                                            <X size={12}
                                                                onClick={() => updateTag(metaData?.find(metaFile => metaFile.file_path === file.key)?.id, tag, 'remove')}
                                                            />
                                                        </span>
                                                    ))}
                                                    {(() => {
                                                        const fileId = metaData?.find(metaFile => metaFile.file_path === file.key)?.id;
                                                        const shouldShowInput = fileId && newFileTag[fileId] !== undefined;
                                                        return shouldShowInput && (
                                                            <span>
                                                                <input 
                                                                    className='tag input caps'
                                                                    type="text" 
                                                                    value={newFileTag[fileId] || ''} 
                                                                    onChange={(e) => {
                                                                        setNewFileTag(prev => ({ ...prev, [fileId]: e.target.value }));
                                                                    }}
                                                                    autoFocus
                                                                    onBlur={() => {
                                                                        if (newFileTag[fileId] && newFileTag[fileId].trim()) {
                                                                            updateTag(fileId, [newFileTag[fileId].trim()]);
                                                                        }
                                                                        setNewFileTag(prev => {
                                                                            const updated = { ...prev };
                                                                            delete updated[fileId];
                                                                            return updated;
                                                                        });
                                                                    }}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') {
                                                                            if (newFileTag[fileId] && newFileTag[fileId].trim()) {
                                                                                updateTag(fileId, [newFileTag[fileId].trim()]);
                                                                            }
                                                                            setNewFileTag(prev => {
                                                                                const updated = { ...prev };
                                                                                delete updated[fileId];
                                                                                return updated;
                                                                            });
                                                                        } else if (e.key === 'Escape') {
                                                                            setNewFileTag(prev => {
                                                                                const updated = { ...prev };
                                                                                delete updated[fileId];
                                                                                return updated;
                                                                            });
                                                                        }
                                                                    }}
                                                                    placeholder='Add a tag'
                                                                />
                                                            </span>
                                                        );
                                                    })()}
                                                    {(() => {
                                                        const fileId = metaData?.find(metaFile => metaFile.file_path === file.key)?.id;
                                                        
                                                        if (!fileId) {
                                                            return (
                                                                <span 
                                                                    className='tag caps disabled' 
                                                                    title="Cannot add tags to files without metadata"
                                                                    style={{ opacity: 0.5, cursor: 'not-allowed' }}
                                                                >
                                                                    + New
                                                                </span>
                                                            );
                                                        }
                                                        
                                                        return (
                                                            <span className='tag caps' onClick={() => {
                                                                setNewFileTag(prev => ({ ...prev, [fileId]: '' }));
                                                            }}>
                                                                + New
                                                            </span>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                            <div className='expanded-options'>
                                                <div className="expanded-details">
                                                    <div className="expanded-date">
                                                        <span className="subtext">Last Updated:</span>
                                                        <span className="subtext-alt">
                                                            {isLiveNewItem('file', file)
                                                                ? <span className="document-live-new">New</span>
                                                                : (file.last_modified ? formatLastModified(file.last_modified)[5] : '-')}
                                                        </span>
                                                    </div>
                                                    <div className="expanded-author">
                                                        <span className="subtext">Created by:</span>
                                                        <span className="subtext-alt">
                                                            {(() => {
                                                                const meta = metaData?.find(metaFile => metaFile.file_path === file.key);
                                                                return userMap[meta?.author_id] || userMap[12];
                                                            })()}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className='expanded-actions'>
                                                    {/* Hot Doc Star Button 
                                                    <span 
                                                        className={`hot-doc-star ${hotDocsList?.has(file?.key) ? 'starred' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleHotDoc(file.key);
                                                        }}
                                                        title={hotDocsList?.has(file.key) ? "Remove from Hot Docs" : "Add to Hot Docs"}
                                                    >
                                                        <Star 
                                                            size={20} 
                                                            fill={hotDocsList?.has(file.key) ? "var(--fill)" : "none"}
                                                        />
                                                    </span>*/}
                                                    <span className='expanded-dots' 
                                                        data-menu-index={index}
                                                        onMouseDown={(e) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                        }}
                                                        onClick={(e) => { 
                                                            e.stopPropagation(); 
                                                            const position = calculateMenuPosition(index, false);
                                                            setMenuPositionAbove(prev => ({
                                                                ...prev,
                                                                [index]: position.showAbove
                                                            }));
                                                            setMenuPosition(prev => ({
                                                                ...prev,
                                                                [index]: { left: position.left, top: position.top }
                                                            }));
                                                            setMore(more === index ? null : index); 
                                                        }}
                                                    ><Ellipsis size={25}/></span>
                                                    {more === index && typeof document !== 'undefined' && createPortal(
                                                        <div
                                                            className={`expanded-actions-menu ${menuPositionAbove[index] ? 'position-above' : ''}`}
                                                            ref={menuRef}
                                                            style={{
                                                                left: menuPosition[index]?.left || 0,
                                                                top: menuPosition[index]?.top || 0
                                                            }}
                                                            onMouseDown={(e) => {
                                                                e.stopPropagation();
                                                            }}
                                                        >
                                                            <span className='expanded-actions-menu-child small-text'
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    generateShareLink({...item, key: file.key});
                                                                    setMore(null);
                                                                }}
                                                            >
                                                                <span>Share Link</span><Link />
                                                            </span>
                                                            <span className='expanded-actions-menu-child small-text'
                                                                onClick={() => {
                                                                    handleDownload(file);
                                                                    setMore(null);
                                                                }}
                                                            >
                                                                <span>Download</span><ArrowDownToLine />
                                                            </span>
                                                            {canRedactPdfFile(file?.name) && (
                                                                <span className='expanded-actions-menu-child small-text'
                                                                    onClick={() => {
                                                                        handleRedactFile(file);
                                                                        setMore(null);
                                                                    }}
                                                                >
                                                                    <span>Redact</span><ScanLine />
                                                                </span>
                                                            )}
                                                            {canConvertToPdfFile(file?.name) && (
                                                                <span className='expanded-actions-menu-child small-text'
                                                                    onClick={() => {
                                                                        handleConvertToPdf(file);
                                                                        setMore(null);
                                                                    }}
                                                                >
                                                                    <span>Convert to PDF</span><FileText />
                                                                </span>
                                                            )}
                                                            <span className="expanded-actions-menu-child small-text"
                                                                onClick={() => {
                                                                    if (batesProcessing) {
                                                                        setMessage({ text: 'A Bates stamping job is already running for this case.', type: 'info' });
                                                                        setMore(null);
                                                                        return;
                                                                    }
                                                                    setSelectedKeys([file.key]);
                                                                    initializeBatesStamping([file.key]);
                                                                    setMore(null);
                                                                }}
                                                                aria-disabled={batesProcessing}
                                                            >
                                                                <span>Bates / Exhibit Stamp</span><Stamp />
                                                            </span>
                                                            <span className="expanded-actions-menu-child small-text"
                                                                onClick={() => {
                                                                    handleRenameClick(file);
                                                                    setMore(null);
                                                                }}
                                                            >
                                                                <span>Rename</span><Pencil />
                                                            </span>
                                                            <span className="expanded-actions-menu-child small-text"
                                                                onClick={() => {
                                                                    toggleHotDoc(file.key);
                                                                    setMore(null);
                                                                }}
                                                            >
                                                                <span>{hotDocsList?.has(file.key) ? "Remove from Hot Docs" : "Add to Hot Docs"}</span>
                                                                <Star 
                                                                    size={16} 
                                                                    fill={hotDocsList?.has(file.key) ? "var(--fill)" : "none"}
                                                                />
                                                            </span>
                                                            <span className="expanded-actions-menu-child small-text"
                                                                onClick={() => {
                                                                    setMore(null);
                                                                    
                                                                    // Select this file for deletion and immediately show modal
                                                                    setSelectedKeys([file.key]);
                                                                    setActiveFile([file.key]);
                                                                    setDeleteModal(true);
                                                                }}
                                                            >
                                                                <span>Delete</span><Trash />
                                                            </span>
                                                            <span className='expanded-actions-menu-child small-text' onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedKeys([file.key]);
                                                                setActiveFile([file.key]);
                                                                openMoveModal();
                                                                setMore(null);
                                                            }}>
                                                                <span>Move/Copy</span><Move />
                                                            </span>
                                                        </div>,
                                                        document.body
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        );
                                    }
                                })}
                            </div>
                        )
                    ) : (
                        (isLoading || isSearching || isSearchQueued) ? (
                            <div className="document-loading" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '24px', width: '100%' }}>
                                <Loader2 size={18} className="spinner" />
                                <span className="subtext">{isSearching ? 'Searching documents...' : isSearchQueued ? 'Preparing search...' : 'Loading documents...'}</span>
                            </div>
                        ) : (
                            <p className="subtext">This folder is empty.</p>
                        )
                    )}
                    </div>
                    
                    {showSidePreview && (
                        <div className="file-preview-panel" style={{ 
                            position: 'sticky', 
                            top: '81px',
                            alignSelf: 'flex-start',
                            maxHeight: 'calc(100vh - 140px)',
                            overflow: 'hidden'
                        }}>
                            <div className="preview-panel-header">
                                <h5>{sidePreviewFile?.name}</h5>
                                <button
                                    className="close-preview-panel" 
                                    onClick={() => {
                                        setSidePreview(null);
                                        setSidePreviewFile(null);
                                    }}
                                >
                                    <X size={18} />
                                </button>
                            </div>
                            <div className="preview-panel-content" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                                <FilePreviewPanel
                                    previewState={sidePreview}
                                    file={sidePreviewFile}
                                    onDownload={handleDownload}
                                    onFullView={handleDoubleClick}
                                    onEmailPdfPreview={handleEmailPdfPreview}
                                    onOpenFolder={(folder) => onFolderClick && onFolderClick(folder.name)}
                                    onFolderInfo={(folder) => {
                                        const folderKey = folder.key || getFolderKeyFromItem(folder);
                                        const targets = buildSelectedFolderInfoTargets(
                                            [folderKey],
                                            [{ ...folder, key: folderKey, type: 'folder' }]
                                        );
                                        if (targets.length > 0) {
                                            setFolderInfoTargets(targets);
                                        }
                                    }}
                                    onZipFolder={async (folder) => {
                                        const folderKey = folder.key || folder.name;
                                        await handleBulkDownload([folderKey]);
                                    }}
                                    onEditFile={handleEditDocument}
                                    onConvertToPdf={handleConvertToPdf}
                                    formatSize={formatSize}
                                    formatLastModified={formatLastModified}
                                    folderName={folderName}
                                    caseName={caseName}
                                    showFolderSyncOption={isParentCase}
                                    isFolderSyncPoint={isFolderSyncPoint(getFolderKeyFromItem(sidePreviewFile))}
                                    onToggleFolderSync={(folder) => {
                                        const key = getFolderKeyFromItem(folder);
                                        if (key) {
                                            toggleFolderSyncPoint(key);
                                        }
                                    }}
                                    syncBusy={syncBusy}
                                />
                                {sidePreviewFile && sidePreviewFile.type !== 'folder' && (
                                    <FileNotes 
                                        file={sidePreviewFile} 
                                        onUpdateNoteCount={updateFileNoteCount}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </div>
                {!isLoading && pagination && (pagination.total_pages > 1 || pagination.has_next || pagination.has_prev) && (
                    <div className="doc-pagination-controls">
                        <div className="doc-pagination-reveal">
                            <button
                                type="button"
                                className="action tertiary no-size doc-pagination-summary"
                                aria-label={`${pagination.total_pages} pages. Show pagination controls`}
                                onPointerDown={(event) => event.currentTarget.focus()}
                                onClick={(event) => event.currentTarget.focus()}
                            >
                                {pagination.total_pages} {pagination.total_pages === 1 ? 'Page' : 'Pages'}
                            </button>
                            <div className="doc-pagination-expanded">
                                <div className="pagination-info">
                                    {setPageSize && (
                                        <div className="page-size-selector">
                                            <span className="subtext" style={{ marginRight: '8px' }}>Items per page:</span>
                                            <select
                                                value={pageSize}
                                                onChange={(e) => {
                                                    const newPageSize = parseInt(e.target.value);
                                                    setPageSize(newPageSize);
                                                    // The useEffect in Section.js will handle refreshing with the new page size
                                                }}
                                                className="default-select"
                                                style={{ minWidth: '70px' }}
                                            >
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                                <option value={200}>200</option>
                                                <option value={500}>500</option>
                                            </select>
                                        </div>
                                    )}
                                </div>
                                <div className="pagination-buttons">
                                    <button
                                        className={`action ${!pagination.has_prev ? 'disabled' : ''}`}
                                        onClick={handlePreviousPage}
                                        disabled={!pagination.has_prev}
                                    >
                                        Previous
                                    </button>
                                    <span className="pagination-current">
                                        Page {pagination.current_page} of {pagination.total_pages}
                                    </span>
                                    <button
                                        className={`action ${!pagination.has_next ? 'disabled' : ''}`}
                                        onClick={handleNextPage}
                                        disabled={!pagination.has_next}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {shareModal && (
                    <ShareModal
                        onClose={() => {
                            resetShareCreationScope();
                            setShareModal(false);
                        }}
                        target={shareTarget}
                        createShareableLink={createShareableLink}
                        shareLink={shareLink}
                        caseName={caseName}
                        onViewLinks={() => {
                            resetShareCreationScope();
                            setShareModal(false);
                            setShowCaseShareLinks(true);
                        }}
                    />
                )}
                {showCaseShareLinks && (
                    <CaseShareLinksModal
                        caseId={case_id}
                        onClose={() => setShowCaseShareLinks(false)}
                        onCreate={() => {
                            setShowCaseShareLinks(false);
                            handleSmartShare();
                        }}
                    />
                )}

                {folderInfoTargets && (
                    <FolderInfoModal
                        caseId={case_id}
                        targets={folderInfoTargets}
                        onClose={() => setFolderInfoTargets(null)}
                    />
                )}
                
                {showAdvancedMergeModal && (
                    <Modal
                        title="Document Merge"
                        wide={true}
                        instructions="Configure merge settings and selected file/folder order"
                        onClose={isMerging ? null : () => setShowAdvancedMergeModal(false)}
                        footer={
                            <div className='modal-footer-actions'>
                                <button 
                                    onClick={isMerging ? null : async () => {
                                        const requestSettings = {
                                            ...mergeSettings,
                                            fileOrder: [...mergeSettings.fileOrder]
                                        };
                                        const mergeTracker = createMergeProgressTracker(requestSettings.fileName);

                                        try {
                                            setIsMerging(true);
                                            setShowAdvancedMergeModal(false);
                                            setMessage({ text: "Merge started. Progress will continue in the upload tray.", type: "info" });

                                            await ensureCaseDbCsrfToken();
                                            const response = await fetch('https://api.casedb.co/merge-documents.php', {
                                                method: 'POST',
                                                headers: withCsrfHeaders({
                                                    'Content-Type': 'application/json',
                                                }, 'POST'),
                                                body: JSON.stringify({
                                                    file_keys: requestSettings.fileOrder.map(f => f.key),
                                                    output_name: requestSettings.fileName,
                                                    case_id: case_id,
                                                    case_name: caseName,
                                                    target_folder: requestSettings.targetFolder,
                                                    ocr_pdf: requestSettings.ocrPdf,
                                                    merge_id: mergeTracker.mergeId,
                                                    cases: true
                                                }),
                                                credentials: 'include'
                                            });

                                            const result = await response.json();

                                            if (result.success) {
                                                let successMessage = "Documents merged successfully!";
                                                if (result.uploaded) {
                                                    successMessage = `Documents merged and saved to ${displayDocumentPath(requestSettings.targetFolder, caseName)}!`;
                                                    if (refreshCurrentFolder) {
                                                        setTimeout(() => refreshCurrentFolder(), 1000);
                                                    }
                                                }

                                                if (result.warning && result.skipped_files) {
                                                    const skippedList = result.skipped_files.map(f => f.split('/').pop()).join(', ');
                                                    setMessage({ 
                                                        text: `${result.warning}. Skipped files: ${skippedList}`, 
                                                        type: "warning" 
                                                    });
                                                    
                                                    setTimeout(() => {
                                                        setMessage({ 
                                                            text: `${successMessage} (${result.processed_files}/${result.total_files} files processed)`, 
                                                            type: "success" 
                                                        });
                                                    }, 4000);
                                                } else {
                                                    setMessage({ text: successMessage, type: "success" });
                                                }
                                                mergeTracker.complete();

                                                if (!result.uploaded && result.content) {
                                                    setMergeResult(result);
                                                    setShowMergeDownloadModal(true);
                                                }

                                                setSelectedKeys([]);
                                                setActiveFile([]);
                                                setIsMerging(false);
                                            } else {
                                                mergeTracker.fail(new Error(result.message || 'Merge failed'));
                                                setMessage({ text: `Merge failed: ${result.message}`, type: "error" });
                                                setIsMerging(false);
                                            }
                                        } catch (error) {
                                            console.error('Merge error:', error);
                                            mergeTracker.fail(error);
                                            setMessage({ text: `Merge error: ${error.message}`, type: "error" });
                                            setIsMerging(false);
                                        }
                                    }} 
                                    className="action"
                                    disabled={isMerging}
                                >
                                    {isMerging ? 'Merging...' : 'Merge Documents'}
                                </button>
                                <button 
                                    onClick={isMerging ? null : () => setShowAdvancedMergeModal(false)} 
                                    className="action alt"
                                    disabled={isMerging}
                                >
                                    Cancel
                                </button>
                            </div>
                        }
                    >
                        <div className='modal-content-wrapper'>
                            {/* File Name Input */}
                            <div className='form-group full nm merge-full-width-field'>
                                <label className="subtext">Merged Document Name</label>
                                <div className='file-rename-container'>
                                    <input
                                        type="text"
                                        value={mergeSettings.fileName}
                                        onChange={(e) => setMergeSettings(prev => ({
                                            ...prev,
                                            fileName: e.target.value
                                        }))}
                                        placeholder="Enter filename (without extension)"
                                    />
                                    <span className="subtext" style={{ padding: "6px 0" }}>.pdf</span>
                                </div>
                            </div>

                            {/* Target Folder Selection */}
                            <div className='form-group full merge-full-width-field'>
                                <label className="subtext">Save to Folder</label>
                                <div className='move-folder-actions'>
                                    <SearchSelect
                                        value={mergeSettings.targetFolder}
                                        onChange={(path) => setMergeSettings(prev => ({
                                            ...prev,
                                            targetFolder: path
                                        }))}
                                        options={Object.fromEntries(availableFolders.map(folder => [folder.path, formatSearchSelectPath(folder.path || folder.displayPath)]))}
                                        placeholder="Select destination folder..."
                                        minMatchScore={0.5}
                                    />
                                    <button
                                        className="action tertiary"
                                        style={{ backgroundColor: 'var(--background-color)' }}
                                        onClick={() => setShowMergeFolderPicker(true)}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = 'var(--fill)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = 'var(--border-color)';
                                        }}
                                    >
                                        Browse...
                                    </button>
                                </div>
                            </div>

                            <div className='form-group full'>
                                <Checkbox
                                    value={!!mergeSettings.ocrPdf}
                                    onChange={() => setMergeSettings(prev => ({
                                        ...prev,
                                        ocrPdf: !prev.ocrPdf
                                    }))}
                                    label="Add searchable OCR text layer"
                                />
                                <div className="subtext" style={{ marginTop: '6px' }}>
                                    Uses OCR on image-only pages so the merged PDF text can be selected and searched.
                                </div>
                            </div>

                            {/* File Order */}
                            <div className='form-group nm full'>
                                <label className="subtext">Selected Order (drag to reorder)</label>
                                <div className="subtext" style={{ marginBottom: '8px' }}>
                                    Selected folders are expanded recursively when the merge runs, preserving this top-level order.
                                </div>
                                <div className="file-order-list">
                                    {mergeSettings.fileOrder.map((file, index) => (
                                        <div 
                                            key={file.key} 
                                            className="file-order-item"
                                            draggable
                                            onDragStart={(e) => {
                                                e.dataTransfer.setData('text/plain', index.toString());
                                            }}
                                            onDragOver={(e) => e.preventDefault()}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
                                                const hoverIndex = index;
                                                
                                                if (dragIndex === hoverIndex) return;
                                                
                                                const newOrder = [...mergeSettings.fileOrder];
                                                const draggedItem = newOrder[dragIndex];
                                                newOrder.splice(dragIndex, 1);
                                                newOrder.splice(hoverIndex, 0, draggedItem);
                                                
                                                setMergeSettings(prev => ({
                                                    ...prev,
                                                    fileOrder: newOrder
                                                }));
                                            }}
                                        >
                                            <span className='file-order-handle'>
                                                <GripVertical size={20} />
                                            </span>
                                            <div className='file-name-container'>
                                                <span className="file-name-merge subtext">
                                                    {file.type === 'folder' && <FolderIcon size={16} />}
                                                    {file.name}
                                                </span>
                                                <span className="file-size-merge subtext">
                                                    {file.type === 'folder' ? `${file.size || 0} items` : formatSize(file.size)}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Modal>
                )}
                
                {filePreview && (
                    <>
                        <div className='modal-overlay'></div>
                        <div className="file-preview">
                            <button className="close-preview form-box alt" onClick={() => setFilePreview(null)}>
                                <X />
                            </button>
                            {filePreview}
                        </div>
                    </>
                )}
                {activeFolderRenames.length > 0 && (
                    <Alert
                        title={activeFolderRenames.length === 1 ? 'Folder rename in progress' : 'Folder renames in progress'}
                        message={activeFolderRenames.length === 1
                            ? `${activeFolderRenames[0].statusMessage || 'Renaming folder...'}${Number.isFinite(activeFolderRenames[0].progress) ? ` (${activeFolderRenames[0].progress}%)` : ''}`
                            : `${activeFolderRenames.length} non-overlapping folder renames are running. Uploads remain available outside those folders.`}
                        type="info"
                        duration={0}
                        dismissOnClick={false}
                        showClose={false}
                        showProgress={false}
                    />
                )}
                {message && <Alert message={message.text} type={message.type} onClose={() => setMessage(null)} />}
                {/* Bates Stamping Modal */}
                {showBatesModal && (
                    <Modal 
                        title={`${isExhibitStampMode ? 'Exhibit' : 'Bates'} Stamping - Step ${batesStep} of ${batesTotalSteps}`}
                        onClose={closeBatesModal}
                        wide={true}
                        footer={
                            <div className="modal-footer-actions bates-stamping-footer">
                                <div className="footer-left">
                                    {batesStep > 1 && (
                                        <button 
                                            className="action alt"
                                            onClick={() => setBatesStep(prev => prev - 1)}
                                        >
                                            <ArrowLeft size={16} />
                                            Previous
                                        </button>
                                    )}
                                </div>
                                
                                <div className="footer-right">
                                    <button 
                                        className="action destroy"
                                        onClick={closeBatesModal}
                                        disabled={batesProcessing}
                                    >
                                        Cancel
                                    </button>
                                    
                                    {batesStep < batesTotalSteps ? (
                                        <button 
                                            className="action alt"
                                            onClick={() => setBatesStep(prev => prev + 1)}
                                            disabled={batesNextDisabled}
                                        >
                                            Next
                                            <ArrowRight size={16} />
                                        </button>
                                    ) : (
                                        <button 
                                            className="action"
                                            onClick={() => processBatesStamping()}
                                            disabled={batesProcessing
                                                || batesAnalyzeLoading
                                                || !hasBatesSnapshot
                                                || (isExhibitStampMode && (!exhibitStampResolution.canStart || !exhibitPageLabelPlan.valid))}
                                        >
                                            <Stamp size={16} />
                                            Start {isExhibitStampMode ? 'Exhibit' : 'Bates'} Stamping
                                        </button>
                                    )}
                                </div>
                            </div>
                        }
                    >
                        <div className="modal-content-wrapper bates-stamping-modal">
                                {batesAnalyzeLoading ? (
                                    <div className="bates-analysis-banner" role="status">
                                        <Loader2 size={16} />
                                        <div>
                                            <strong>Analyzing the current selection</strong>
                                            <span>CaseDB is resolving the exact source files, conversion support, and exhibit identifiers.</span>
                                        </div>
                                    </div>
                                ) : (!hasBatesSnapshot && ((batesSelection.files?.length || 0) + (batesSelection.folders?.length || 0) > 0) && (
                                    <div className="bates-analysis-banner is-warning" role="alert">
                                        <Info size={16} />
                                        <div>
                                            <strong>Selection analysis is required</strong>
                                            <span>The batch cannot start until its current source snapshot is verified.</span>
                                        </div>
                                        <button
                                            type="button"
                                            className="action alt"
                                            onClick={() => analyzeBatesSelection(getCurrentBatesAnalyzeRequest())}
                                        >
                                            Analyze again
                                        </button>
                                    </div>
                                ))}
                                {batesStep === 1 && batesSelection.mode !== 'folder' && (
                                    <div className="bates-step">
                                        <h4 style={{ margin: '5px' }}>Review Selection</h4>
                                        <p className="step-description small-text" style={{ color: 'var(--subtext-color)' }}>
                                            Choose which documents you want to include in the stamping process.
                                        </p>
                                        <div className="bates-file-list">
                                            {batesFiles.map((file) => {
                                                const fileKey = getBatesFileKey(file);
                                                const analyzedFile = batesAnalyzedByKey.get(fileKey)
                                                    || batesAnalyzedByUniqueName.get(String(file?.name || ''));
                                                return (
                                                <div key={fileKey} className="bates-file-item">
                                                    <div className="bates-file-info subtext">
                                                        <FileText size={16} />
                                                        <span className="bates-file-name">{file.name}</span>
                                                        <span className="bates-file-size">({formatSize(file.size)})</span>
                                                        {analyzedFile && (
                                                            <span className={`bates-conversion-status ${analyzedFile.conversion_supported ? 'is-supported' : 'is-unsupported'}`}>
                                                                {analyzedFile.conversion_supported
                                                                    ? (analyzedFile.extension === 'pdf' ? 'PDF' : 'Converts to PDF')
                                                                    : (analyzedFile.parsed?.errorCode === 'source_file_not_found' ? 'Unavailable' : 'Unsupported')}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="file-actions">
                                                        <button 
                                                            className="btn-icon"
                                                            onClick={() => removeBatesFile(fileKey)}
                                                            title="Remove file"
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                            );})}
                                        </div>

                                        <div className="form-group full">
                                            <label className="subtext">Add More Documents</label>
                                            <DocumentSearch
                                                value=""
                                                onChange={(file) => {
                                                    if (file) addFilesToBates([file]);
                                                }}
                                                case_id={case_id}
                                                lead_id={null}
                                                caseName={caseName}
                                                sectionName="Bates Stamping"
                                                fieldId="bates_documents"
                                            />
                                        </div>
                                    </div>
                                )}

                                {isOrderedFolderBatesFlow && batesStep === 1 && (
                                    <div className="bates-step">
                                        {batesFolderOrder.eligible ? (
                                            <section className="bates-folder-order-section" aria-labelledby="bates-folder-order-heading">
                                                <div className="bates-folder-order-heading">
                                                    <div>
                                                        <h4 id="bates-folder-order-heading">Review Folder Order</h4>
                                                        <p className="step-description small-text" style={{ color: 'var(--subtext-color)' }}>
                                                            Drag folders or use the arrows to set the processing order. Files remain naturally sorted within each folder, and the folder hierarchy stays intact unless you choose to flatten it later.
                                                        </p>
                                                    </div>
                                                    <span className="bates-folder-order-count">
                                                        {batesFolderOrder.orderedFolderKeys.length} {batesFolderOrder.orderedFolderKeys.length === 1 ? 'folder' : 'folders'}
                                                    </span>
                                                </div>
                                                <p className="bates-folder-order-parent small-text">
                                                    Common parent: {batesFolderOrderParentLabel}
                                                </p>
                                                <div className="bates-ordering-list" aria-label="Selected folder order">
                                                    {batesFolderOrder.orderedFolderKeys.map((folderKey, index) => (
                                                        <div
                                                            key={folderKey}
                                                            className="bates-order-item bates-folder-order-item"
                                                            draggable={!batesAnalyzeLoading}
                                                            onDragStart={(event) => {
                                                                event.dataTransfer.effectAllowed = 'move';
                                                                event.dataTransfer.setData(BATES_FOLDER_DRAG_MIME, String(index));
                                                            }}
                                                            onDragOver={(event) => {
                                                                if (batesAnalyzeLoading) return;
                                                                event.preventDefault();
                                                                event.dataTransfer.dropEffect = 'move';
                                                            }}
                                                            onDrop={(event) => {
                                                                event.preventDefault();
                                                                const draggedIndex = event.dataTransfer.getData(BATES_FOLDER_DRAG_MIME);
                                                                if (!/^\d+$/.test(draggedIndex)) return;
                                                                moveBatesFolder(Number(draggedIndex), index);
                                                            }}
                                                        >
                                                            <GripVertical className="bates-folder-order-grip" size={16} aria-hidden="true" />
                                                            <span className="order-number">{index + 1}</span>
                                                            <Folder size={17} aria-hidden="true" />
                                                            <div className="bates-folder-order-copy">
                                                                <strong>{displayDocumentPath(getBatesFolderLeafName(folderKey), caseName)}</strong>
                                                                <span className="small-text" title={getBatesFolderDisplayPath(folderKey, case_id, caseName)}>
                                                                    {getBatesFolderDisplayPath(folderKey, case_id, caseName)}
                                                                </span>
                                                            </div>
                                                            <div className="order-controls">
                                                                <button
                                                                    type="button"
                                                                    className="btn-icon"
                                                                    onClick={() => moveBatesFolder(index, index - 1)}
                                                                    disabled={batesAnalyzeLoading || index === 0}
                                                                    aria-label={`Move ${getBatesFolderLeafName(folderKey)} up`}
                                                                    title="Move folder up"
                                                                >
                                                                    <ArrowUp size={16} />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn-icon"
                                                                    onClick={() => moveBatesFolder(index, index + 1)}
                                                                    disabled={batesAnalyzeLoading || index === batesFolderOrder.orderedFolderKeys.length - 1}
                                                                    aria-label={`Move ${getBatesFolderLeafName(folderKey)} down`}
                                                                    title="Move folder down"
                                                                >
                                                                    <ArrowDown size={16} />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn-icon"
                                                                    onClick={() => removeBatesFolder(folderKey)}
                                                                    disabled={batesAnalyzeLoading || batesFolderOrder.orderedFolderKeys.length === 1}
                                                                    aria-label={`Remove ${getBatesFolderLeafName(folderKey)}`}
                                                                    title="Remove folder"
                                                                >
                                                                    <X size={16} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </section>
                                        ) : (
                                            <div className="bates-folder-order-empty" role="status">
                                                <Info size={16} aria-hidden="true" />
                                                <div>
                                                    <strong>{(batesSelection.folders || []).length === 0
                                                        ? 'No folders selected'
                                                        : 'Folder ordering is unavailable for this selection'}</strong>
                                                    <span className="small-text" style={{ color: 'var(--subtext-color)' }}>
                                                        {(batesSelection.folders || []).length === 0
                                                            ? 'Close the tool and select the sibling folders you want to stamp.'
                                                            : 'Select only folders directly inside the same parent to set their processing order.'}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {batesStep === batesStampingTypeStep && (
                                    <div className="bates-step">
                                        <h4 style={{ margin: 0 }}>Stamping Type</h4>
                                        <p className="step-description small-text" style={{ color: 'var(--subtext-color)' }}>
                                            Choose whether to number every page or place one exhibit stamp on each document.
                                        </p>

                                        <div className="bates-stamp-type-options">
                                            <Checkbox
                                                value={!isExhibitStampMode}
                                                onChange={() => updateBatesPageLabelMode('bates')}
                                                label="Bates numbering and page labels"
                                            />
                                            <Checkbox
                                                value={isExhibitStampMode}
                                                onChange={() => updateBatesPageLabelMode('exhibit')}
                                                label="Exhibit stamps"
                                            />
                                        </div>

                                        {isExhibitStampMode ? (
                                            <div className="exhibit-mode-summary">
                                                <Stamp size={18} />
                                                <div>
                                                    <strong>One stamp per document</strong>
                                                    <p className="small-text" style={{ color: 'var(--subtext-color)' }}>CaseDB reads the exhibit identifier when it can and lets you resolve any unclear or duplicate identifiers before stamping page one. Documents are never merged in this mode.</p>
                                                    <p className="small-text" style={{ color: 'var(--subtext-color)' }}>You can optionally add a Bates-style label derived from that exhibit identifier to every page.</p>
                                                    <p className="small-text" style={{ color: 'var(--subtext-color)' }}>PDFs are stamped directly. Supported Office documents, images, text files, and email files are converted to PDF first.</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <h4 className="bates-subsection-title">Numbering Mode</h4>

                                        <div className="folder-options" style={{ marginBottom: '16px' }}>
                                            <Checkbox value={batesStampMode === 'continuous'} onChange={() => setBatesStampMode('continuous')} label="Continuous stamping across all documents (one sequence of numbers for all files)" />

                                            {batesSelection.mode === 'folder' && (
                                                <Checkbox
                                                    value={batesStampMode === 'per-folder'}
                                                    onChange={() => setBatesStampMode('per-folder')}
                                                    label="Stamp per folder (documents are stamped and placed into the same folder structure)"
                                                />
                                            )}
                                        </div>

                                        <div className="form-group full">
                                            <h4 style={{ margin: 0 }}>Merging Option(s)</h4>
                                            {batesStampMode === 'continuous' ? (
                                                <Checkbox value={batesMergeMode === 'all'} onChange={() => setBatesMergeMode(batesMergeMode === 'all' ? 'none' : 'all')} label="Merge all documents into a single PDF" />
                                            ) : (
                                                <Checkbox value={batesMergeMode === 'per-folder'} onChange={() => setBatesMergeMode(batesMergeMode === 'per-folder' ? 'none' : 'per-folder')} label="Merge documents per folder (one PDF per folder)" />
                                            )}
                                        </div>


                                        {batesMergeMode === 'all' && (
                                            <div className="form-group full">
                                                <label className="subtext">Merged PDF Name</label>
                                                <div className='file-rename-container'>
                                                    <input
                                                        type="text"
                                                        value={batesMergedName}
                                                        onChange={(e) => setBatesMergedName(e.target.value)}
                                                        placeholder="Bates_Merged"
                                                    />
                                                    <span className="subtext" style={{ paddingBottom: '5px' }}>.pdf</span>
                                                </div>
                                            </div>
                                        )}

                                        {batesStampMode === 'per-folder' && (
                                            <>
                                                <div className='per-folder-settings-container'>
                                                    <Checkbox
                                                        value={batesContinuousSubfolders}
                                                        onChange={() => {
                                                            setBatesContinuousSubfolders(prev => {
                                                                const next = !prev;
                                                                if (next) {
                                                                    setBatesSubfoldersFollowRules(true);
                                                                }
                                                                return next;
                                                            });
                                                        }}
                                                        label="Continue numbering across subfolders (do not reset in subfolders)"
                                                    />
                                                    <Checkbox
                                                        value={batesSubfoldersFollowRules}
                                                        onChange={() => {
                                                            if (batesContinuousSubfolders) return;
                                                            setBatesSubfoldersFollowRules(prev => !prev);
                                                        }}
                                                        label="Subfolders follow same stamp rules"
                                                    />
                                                </div>
                                                {!batesSubfoldersFollowRules && !batesContinuousSubfolders && (
                                                    <div className='bates-per-folder-settings'>
                                                        <label className="subtext">Per-folder Bates Settings</label>
                                                        <div className="bates-folder-settings">
                                                            {flattenBatesTree(batesSelection.tree || []).map(folder => (
                                                                <div key={folder.path} className="bates-folder-setting-row">
                                                                    <div className="bates-folder-label">
                                                                        {(folder.path || '').replace(/^{{Name}}\/?/, caseName ? `${caseName}/` : '')}
                                                                    </div>
                                                                    <div className="bates-folder-inputs">
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            placeholder="Prefix"
                                                                            value={batesFolderSettings[folder.path]?.prefix || ''}
                                                                            onChange={(e) => setBatesFolderSettings(prev => ({
                                                                                ...prev,
                                                                                [folder.path]: {
                                                                                    ...prev[folder.path],
                                                                                    prefix: e.target.value
                                                                                }
                                                                            }))}
                                                                        />
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            placeholder="Suffix"
                                                                            value={batesFolderSettings[folder.path]?.suffix || ''}
                                                                            onChange={(e) => setBatesFolderSettings(prev => ({
                                                                                ...prev,
                                                                                [folder.path]: {
                                                                                    ...prev[folder.path],
                                                                                    suffix: e.target.value
                                                                                }
                                                                            }))}
                                                                        />
                                                                        <input
                                                                            type="number"
                                                                            className="form-control"
                                                                            min="1"
                                                                            placeholder="Start"
                                                                            value={batesFolderSettings[folder.path]?.starting_number || 1}
                                                                            onChange={(e) => setBatesFolderSettings(prev => ({
                                                                                ...prev,
                                                                                [folder.path]: {
                                                                                    ...prev[folder.path],
                                                                                    starting_number: parseInt(e.target.value) || 1
                                                                                }
                                                                            }))}
                                                                        />
                                                                        <select
                                                                            className="form-control"
                                                                            value={batesFolderSettings[folder.path]?.padding || 5}
                                                                            onChange={(e) => setBatesFolderSettings(prev => ({
                                                                                ...prev,
                                                                                [folder.path]: {
                                                                                    ...prev[folder.path],
                                                                                    padding: parseInt(e.target.value)
                                                                                }
                                                                            }))}
                                                                        >
                                                                            <option value="3">Pad 3</option>
                                                                            <option value="4">Pad 4</option>
                                                                            <option value="5">Pad 5</option>
                                                                            <option value="6">Pad 6</option>
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                            </>
                                        )}
                                    </div>
                                )}

                                {batesStep === batesDestinationStep && (
                                    <div className="bates-step">
                                        <h4 style={{ margin: 0 }}>Choose Destination Folder</h4>
                                        <p className="step-description small-text" style={{ color: 'var(--subtext-color)' }}>
                                            Select where to save the stamped PDFs and how CaseDB should handle existing output paths.
                                        </p>
                                        
                                        <div className="folder-selection">
                                            <div className="current-folder">
                                                <strong style={{ color: 'var(--fill)' }}>Selected folder:</strong>{' '}
                                                {(() => {
                                                    const selected = (batesSelection?.folders || [])[0];
                                                    const label = selected || folderName;
                                                    if (!label) return caseName || 'Case documents';
                                                    return displayDocumentPath(label.replace(/^cases\/[^\/]+\/?/, ''), caseName);
                                                })()}
                                            </div>
                                            
                                            <div className="folder-options">
                                                <Checkbox value={batesSettings.destination.type === 'current'} 
                                                    onChange={() => setBatesSettings(prev => ({
                                                        ...prev,
                                                        destination: {
                                                            type: 'current',
                                                            path: batesCurrentFolderPath
                                                        }
                                                    }))}
                                                    label="Save in current folder"
                                                />

                                                <Checkbox 
                                                    value={batesSettings.destination.type === 'subfolder'}
                                                    onChange={() => setBatesSettings(prev => ({
                                                        ...prev,
                                                        destination: { type: 'subfolder', path: `${batesCurrentFolderPath}/${isExhibitStampMode ? 'Stamped Exhibits' : 'Bates Stamped'}` }
                                                    }))}
                                                    label={`Create "${isExhibitStampMode ? 'Stamped Exhibits' : 'Bates Stamped'}" subfolder`}
                                                />
                                                
                                                <Checkbox
                                                    value={batesSettings.destination.type === 'custom'}
                                                    onChange={() => setBatesSettings(prev => ({
                                                        ...prev,
                                                        destination: { type: 'custom', path: '' }
                                                    }))}
                                                    label="Choose different folder"
                                                />
                                                
                                                {batesSettings.destination.type === 'custom' && (
                                                    <div className='move-folder-actions'>
                                                        <SearchSelect
                                                            value={batesSettings.destination.path}
                                                            onChange={(path) => setBatesSettings(prev => ({
                                                                ...prev,
                                                                destination: { ...prev.destination, path }
                                                            }))}
                                                            options={Object.fromEntries(availableFolders.map(folder => [folder.path, formatSearchSelectPath(folder.path || folder.displayPath)]))}
                                                            placeholder="Select destination folder..."
                                                            minMatchScore={0.5}
                                                        />
                                                        <button
                                                            className="action tertiary"
                                                            style={{ backgroundColor: 'var(--background-color)' }}
                                                            onClick={() => setShowBatesFolderPicker(true)}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.borderColor = 'var(--fill)';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.borderColor = 'var(--border-color)';
                                                            }}
                                                        >
                                                            Browse...
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="bates-output-behavior">
                                            <div>
                                                <strong>Output handling</strong>
                                                <span className="small-text" style={{ color: 'var(--subtext-color)' }}>These settings apply to the generated PDFs. Your selected source files remain the inputs to the job.</span>
                                            </div>
                                            <div className="bates-output-behavior-controls">
                                                <label>
                                                    <span className="subtext">When an output already exists</span>
                                                    <select
                                                        className="form-control"
                                                        value={batesSettings.conflictPolicy}
                                                        onChange={(event) => setBatesSettings(prev => ({
                                                            ...prev,
                                                            conflictPolicy: event.target.value
                                                        }))}
                                                    >
                                                        <option value="dedupe">Save a numbered copy (1), (2), … (recommended)</option>
                                                        <option value="skip">Skip that output</option>
                                                    </select>
                                                    <small className="config-help">
                                                        {batesSettings.conflictPolicy === 'dedupe'
                                                            ? 'Creates the next available protected name, such as Document (1).pdf, without overwriting another file.'
                                                            : 'Leaves the existing file unchanged, creates no replacement for that item, and reports the skipped output.'}
                                                    </small>
                                                    <small className="config-help">Direct overwrite is unavailable until versioned rollback and target auditing are installed.</small>
                                                </label>

                                                {isFolderBatesFlow && batesMergeMode !== 'all' && (
                                                    <div className="bates-folder-layout-control">
                                                        <span className="subtext">Folder layout</span>
                                                        <Checkbox
                                                            value={!batesSettings.preserveStructure}
                                                            onChange={() => setBatesSettings(prev => ({
                                                                ...prev,
                                                                preserveStructure: !prev.preserveStructure
                                                            }))}
                                                            label="Flatten source folder hierarchy"
                                                        />
                                                        <small className="config-help">
                                                            {batesSettings.preserveStructure
                                                                ? 'Default: recreates the selected source folders and subfolders under the destination.'
                                                                : 'Places every generated PDF directly in the destination; duplicate names follow the conflict choice above.'}
                                                        </small>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {batesStep === batesConfigStep && (
                                    <div className="bates-step">
                                        <h4 style={{ margin: 0 }}>Configure {isExhibitStampMode ? 'Exhibit' : 'Bates'} Stamping</h4>
                                        <p className="step-description small-text" style={{ color: 'var(--subtext-color)' }}>
                                            {isExhibitStampMode
                                                ? 'Choose the stamp appearance and how CaseDB should find a clear location on the first page.'
                                                : 'Set up your Bates stamping preferences.'}
                                        </p>

                                        <div className="bates-output-plan-summary">
                                            <strong>Output plan</strong>
                                            <dl>
                                                <div>
                                                    <dt>Destination</dt>
                                                    <dd>{displayDocumentPath(batesSettings.destination.path || folderName || '{{Name}}', caseName)}</dd>
                                                </div>
                                                <div>
                                                    <dt>Folder layout</dt>
                                                    <dd>{batesMergeMode === 'all'
                                                        ? 'One merged PDF'
                                                        : (batesSettings.preserveStructure ? 'Preserve source hierarchy' : 'Flatten into destination')}</dd>
                                                </div>
                                                <div>
                                                    <dt>Output naming</dt>
                                                    <dd>{batesMergeMode === 'all'
                                                        ? `${batesMergedName || 'Bates_Merged'}.pdf`
                                                        : batesOutputNamingLabel}</dd>
                                                </div>
                                                <div>
                                                    <dt>Existing outputs</dt>
                                                    <dd>{batesSettings.conflictPolicy === 'dedupe'
                                                        ? 'Save numbered copies'
                                                        : (batesSettings.conflictPolicy === 'skip' ? 'Skip and report' : 'Overwrite exact paths')}</dd>
                                                </div>
                                            </dl>
                                        </div>
                                        
                                        <div className="bates-config">
                                            {!isExhibitStampMode && (
                                                <>
                                            <div className="config-group">
                                                <label className='subtext'>Starting Number</label>
                                                <input 
                                                    type="number"
                                                    className="form-control"
                                                    value={batesSettings.startNumber}
                                                    onChange={(e) => setBatesSettings(prev => ({
                                                        ...prev,
                                                        startNumber: parseInt(e.target.value) || 1
                                                    }))}
                                                    min="1"
                                                />
                                            </div>
                                            
                                            <div className="config-group">
                                                <label className='subtext'>Prefix (optional)</label>
                                                <input 
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="e.g., CASE001-"
                                                    value={batesSettings.prefix}
                                                    onChange={(e) => setBatesSettings(prev => ({
                                                        ...prev,
                                                        prefix: e.target.value
                                                    }))}
                                                />
                                            </div>
                                            
                                            <div className="config-group">
                                                <label className='subtext'>Suffix (optional)</label>
                                                <input 
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="e.g., -CONF"
                                                    value={batesSettings.suffix}
                                                    onChange={(e) => setBatesSettings(prev => ({
                                                        ...prev,
                                                        suffix: e.target.value
                                                    }))}
                                                />
                                            </div>
                                            
                                            <div className="config-group">
                                                <label className='subtext'>Number Padding</label>
                                                <select 
                                                    className="form-control"
                                                    value={batesSettings.padding}
                                                    onChange={(e) => setBatesSettings(prev => ({
                                                        ...prev,
                                                        padding: parseInt(e.target.value)
                                                    }))}
                                                >
                                                    <option value="3">3 digits (001, 002, 003)</option>
                                                    <option value="4">4 digits (0001, 0002, 0003)</option>
                                                    <option value="5">5 digits (00001, 00002, 00003)</option>
                                                    <option value="6">6 digits (000001, 000002, 000003)</option>
                                                </select>
                                            </div>
                                                </>
                                            )}
                                            
                                            <div className="config-group">
                                                <label className='subtext'>Position</label>
                                                <select 
                                                    className="form-control"
                                                    value={batesSettings.position}
                                                    onChange={(e) => setBatesSettings(prev => ({
                                                        ...prev,
                                                        position: e.target.value,
                                                        exhibitAutoPlacementPolicy: e.target.value === 'auto'
                                                            ? prev.exhibitAutoPlacementPolicy
                                                            : 'strict'
                                                    }))}
                                                >
                                                    {isExhibitStampMode && <option value="auto">Automatic — bottom right first</option>}
                                                    <option value="bottom-right">Bottom Right</option>
                                                    <option value="bottom-left">Bottom Left</option>
                                                    <option value="bottom-center">Bottom Center</option>
                                                    <option value="top-right">Top Right</option>
                                                    <option value="top-left">Top Left</option>
                                                    <option value="top-center">Top Center</option>
                                                </select>
                                                {isExhibitStampMode && (
                                                    <small className="config-help">
                                                        {batesSettings.position === 'auto'
                                                            ? (batesSettings.exhibitAutoPlacementPolicy === 'least_obstructed'
                                                                ? 'Automatic placement prefers verified clear space. The enabled fallback allows the least-obstructed location only when no clear location exists.'
                                                                : 'Automatic placement prefers the bottom-right, then moves the stamp only when another clear area is safer. If no clear area can be verified, that file is skipped rather than covered.')
                                                            : 'A fixed position is applied exactly where selected and may cover existing page content.'}
                                                    </small>
                                                )}
                                                {isExhibitStampMode && batesSettings.position === 'auto' && (
                                                    <div className="config-checkbox">
                                                        <Checkbox
                                                            label="Finish the batch using the least-obstructed area"
                                                            value={batesSettings.exhibitAutoPlacementPolicy === 'least_obstructed'}
                                                            onChange={() => setBatesSettings(prev => ({
                                                                ...prev,
                                                                exhibitAutoPlacementPolicy: prev.exhibitAutoPlacementPolicy === 'least_obstructed'
                                                                    ? 'strict'
                                                                    : 'least_obstructed'
                                                            }))}
                                                        />
                                                        <span className="small-text" style={{ color: 'var(--subtext-color)' }}>
                                                            This is an explicit fallback for full-page scans and photos. CaseDB still checks every possible area for clear space first, then allows only a long, thin scan line in a raised right-edge position. If every area contains page content, it uses the least-obstructed location and may cover content. The completion notice identifies these exhibits, and the exhibit manifest records the exact placement and obstruction measurements for every output.
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            {!isExhibitStampMode ? (
                                                <div className="config-group">
                                                    <label className='subtext'>Page Label Style</label>
                                                    <select
                                                        className="form-control"
                                                        value={batesSettings.pageLabelMode}
                                                        onChange={(e) => updateBatesPageLabelMode(e.target.value)}
                                                    >
                                                        <option value="bates">Bates numbering (per page)</option>
                                                        <option value="document">Document label + Page X of Y</option>
                                                    </select>
                                                    {batesSettings.pageLabelMode === 'document' && (
                                                        <select
                                                            className="form-control"
                                                            value={batesSettings.docLabelSource}
                                                            onChange={(e) => setBatesSettings(prev => ({
                                                                ...prev,
                                                                docLabelSource: e.target.value
                                                            }))}
                                                            style={{ marginTop: '6px' }}
                                                        >
                                                            <option value="filename">Use file name as document label</option>
                                                            <option value="bates">Use Bates prefix/number/suffix</option>
                                                        </select>
                                                    )}
                                                    {batesSettings.pageLabelMode === 'document' && batesSettings.docLabelSource === 'filename' && (
                                                        <small className="config-help">Prefix, suffix, and starting number are ignored when using file names.</small>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="config-group">
                                                    <label className='subtext'>Exhibit Stamp</label>
                                                    <select
                                                        className="form-control"
                                                        value={batesSettings.exhibitStampStyle}
                                                        onChange={(e) => setBatesSettings(prev => ({
                                                            ...prev,
                                                            exhibitStampStyle: e.target.value
                                                        }))}
                                                    >
                                                        <option value="auto">Choose from file name (PTX / DTX)</option>
                                                        <option value="plaintiff">Plaintiff's Trial Exhibit</option>
                                                        <option value="defendant">Defendant's Trial Exhibit</option>
                                                        <option value="neutral">Exhibit</option>
                                                    </select>
                                                    <small className="config-help">
                                                        Auto uses the plaintiff stamp for PTX, defendant stamp for DTX, and the neutral stamp for other valid identifiers.
                                                    </small>
                                                </div>
                                            )}

                                            {isExhibitStampMode && (
                                                <div className="config-group">
                                                    <label className="subtext">Number shown in stamp</label>
                                                    <select
                                                        className="form-control"
                                                        value={batesSettings.exhibitDisplayIdentifierMode}
                                                        onChange={(e) => setBatesSettings(prev => ({
                                                            ...prev,
                                                            exhibitDisplayIdentifierMode: e.target.value
                                                        }))}
                                                    >
                                                        <option value="identifier">Full identifier (PTX0001)</option>
                                                        <option value="number">Number only (1)</option>
                                                    </select>
                                                    <span className="small-text" style={{ color: 'var(--subtext-color)' }}>
                                                        This changes only the number printed inside the exhibit stamp. Output file names still follow the separate naming choice below.
                                                    </span>
                                                </div>
                                            )}

                                            {isExhibitStampMode && (
                                                <div className="config-group exhibit-resolution-config">
                                                    <div className="exhibit-resolution-heading">
                                                        <div>
                                                            <label>Exhibit identifiers</label>
                                                            <small className="config-help">
                                                                {exhibitStampResolution.processable.length} convertible file{exhibitStampResolution.processable.length === 1 ? '' : 's'} analyzed
                                                                {exhibitStampResolution.unsupported.length > 0
                                                                    ? `; ${exhibitStampResolution.unsupported.length} file${exhibitStampResolution.unsupported.length === 1 ? '' : 's'} will be skipped`
                                                                    : ''}.
                                                            </small>
                                                        </div>
                                                        {batesAnalyzeLoading && (
                                                            <span className="bates-loading small-text"><Loader2 size={14} className="spin" /> Analyzing files...</span>
                                                        )}
                                                    </div>

                                                    {exhibitStampResolution.unsupported.length > 0 && (
                                                        <div className="exhibit-unsupported-files">
                                                            <strong>Skipped files</strong>
                                                            <span className="small-text" style={{ color: 'var(--subtext-color)' }}>
                                                                {isFolderBatesFlow
                                                                    ? 'Unsupported or unavailable files will be skipped and reported. To exclude them from the batch, change the selected folder before reopening this tool.'
                                                                    : 'Unsupported or unavailable files remain in the selection so the completed job can report them as skipped. Remove them from Review Selection if you do not want them included.'}
                                                            </span>
                                                            {exhibitStampResolution.unsupported.map(entry => (
                                                                <span key={entry.sourceKey}>
                                                                    {entry.file.name}: {entry.file.parsed?.errorCode === 'source_file_not_found'
                                                                        ? (entry.file.parsed.reason || 'Source file unavailable')
                                                                        : `Unsupported ${entry.file.extension || 'unknown'} file`}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {exhibitStampResolution.processable.length > 0 && (
                                                            <div className="exhibit-sequence-panel">
                                                                <div className="exhibit-sequence-heading">
                                                                    <strong>Apply an exhibit sequence</strong>
                                                                    <span className="small-text" style={{ color: 'var(--subtext-color)' }}>
                                                                        {batesExhibitSequence.scope === 'all'
                                                                            ? 'Every convertible file will receive a new identifier in the analyzed folder and file order.'
                                                                            : 'Existing unique identifiers are kept. Generated numbers skip collisions.'}
                                                                    </span>
                                                                </div>
                                                                <div className="exhibit-sequence-controls">
                                                                    <label>
                                                                        <span className="subtext">Apply sequence to</span>
                                                                        <select
                                                                            className="form-control"
                                                                            value={batesExhibitSequence.scope}
                                                                            onChange={(e) => setBatesExhibitSequence(prev => ({ ...prev, scope: e.target.value }))}
                                                                        >
                                                                            <option value="unresolved">Unresolved files only</option>
                                                                            <option value="all">All convertible files (replace detected IDs)</option>
                                                                        </select>
                                                                    </label>
                                                                    <label>
                                                                        <span className="subtext">Identifier format</span>
                                                                        <select
                                                                            className="form-control"
                                                                            value={batesExhibitSequence.format}
                                                                            onChange={(e) => setBatesExhibitSequence(prev => ({ ...prev, format: e.target.value }))}
                                                                        >
                                                                            <option value="PTX">PTX</option>
                                                                            <option value="DTX">DTX</option>
                                                                            <option value="JTX">JTX</option>
                                                                            <option value="number">Number only</option>
                                                                            <option value="custom">Custom</option>
                                                                        </select>
                                                                    </label>
                                                                    {batesExhibitSequence.format === 'custom' && (
                                                                        <label>
                                                                            <span className="subtext">Custom format</span>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                value={batesExhibitSequence.customFormat}
                                                                                onChange={(e) => setBatesExhibitSequence(prev => ({ ...prev, customFormat: e.target.value }))}
                                                                                placeholder="A{n}"
                                                                            />
                                                                        </label>
                                                                    )}
                                                                    <label>
                                                                        <span className="subtext">Starting number</span>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control"
                                                                            min="0"
                                                                            value={batesExhibitSequence.startNumber}
                                                                            onChange={(e) => setBatesExhibitSequence(prev => ({ ...prev, startNumber: Math.max(0, parseInt(e.target.value) || 0) }))}
                                                                        />
                                                                    </label>
                                                                    <label>
                                                                        <span className="subtext">Padding</span>
                                                                        <select
                                                                            className="form-control"
                                                                            value={batesExhibitSequence.padding}
                                                                            onChange={(e) => setBatesExhibitSequence(prev => ({ ...prev, padding: parseInt(e.target.value) }))}
                                                                        >
                                                                            <option value="1">None</option>
                                                                            <option value="2">2 digits</option>
                                                                            <option value="3">3 digits</option>
                                                                            <option value="4">4 digits</option>
                                                                            <option value="5">5 digits</option>
                                                                            <option value="6">6 digits</option>
                                                                        </select>
                                                                    </label>
                                                                    <label>
                                                                        <span className="subtext">Stamp</span>
                                                                        <select
                                                                            className="form-control"
                                                                            value={batesExhibitSequence.stampStyle}
                                                                            onChange={(e) => setBatesExhibitSequence(prev => ({ ...prev, stampStyle: e.target.value }))}
                                                                        >
                                                                            <option value="auto">Match identifier</option>
                                                                            <option value="plaintiff">Plaintiff's</option>
                                                                            <option value="defendant">Defendant's</option>
                                                                            <option value="neutral">Exhibit</option>
                                                                        </select>
                                                                    </label>
                                                                    <button
                                                                        type="button"
                                                                        className="action alt"
                                                                        disabled={batesExhibitSequenceTargetCount === 0
                                                                            || (batesExhibitSequence.format === 'custom' && !batesExhibitSequence.customFormat.trim())}
                                                                        onClick={() => setBatesExhibitOverrides(prev => applyExhibitSequence(
                                                                            exhibitStampResolution,
                                                                            prev,
                                                                            batesExhibitSequence
                                                                        ))}
                                                                    >
                                                                        Apply to {batesExhibitSequenceTargetCount} {batesExhibitSequence.scope === 'all' ? 'convertible' : 'unresolved'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                    )}

                                                    {editableExhibitStampFiles.length > 0 && (
                                                        <details className="exhibit-resolution-details">
                                                            <summary>
                                                                <span>Review or edit individual identifiers</span>
                                                                <span className="exhibit-resolution-detail-count">{editableExhibitStampFiles.length}</span>
                                                            </summary>
                                                            <div className="exhibit-resolution-list">
                                                                {editableExhibitStampFiles.map(entry => (
                                                                    <div className={`exhibit-resolution-row ${entry.resolved ? 'is-resolved' : ''}`} key={entry.sourceKey}>
                                                                        <div className="exhibit-resolution-file">
                                                                            <strong>{entry.file.name}</strong>
                                                                            <span className="small-text">{entry.resolved ? 'Ready — manual identifier' : entry.reason}</span>
                                                                        </div>
                                                                        <label>
                                                                            <span className="subtext">Exhibit identifier</span>
                                                                            <input
                                                                                type="text"
                                                                                className={entry.identifierValid && !entry.duplicate ? 'form-control' : 'form-control is-invalid'}
                                                                                value={entry.identifier}
                                                                                placeholder="PTX001"
                                                                                onChange={(e) => setBatesExhibitOverrides(prev => ({
                                                                                    ...prev,
                                                                                    [entry.sourceKey]: {
                                                                                        ...(prev[entry.sourceKey] || {}),
                                                                                        identifier: e.target.value,
                                                                                        stamp_style: prev[entry.sourceKey]?.stamp_style || 'auto'
                                                                                    }
                                                                                }))}
                                                                            />
                                                                        </label>
                                                                        <label>
                                                                            <span className="subtext">Stamp</span>
                                                                            <select
                                                                                className="form-control"
                                                                                value={batesExhibitOverrides[entry.sourceKey]?.stamp_style || 'auto'}
                                                                                onChange={(e) => setBatesExhibitOverrides(prev => ({
                                                                                    ...prev,
                                                                                    [entry.sourceKey]: {
                                                                                        ...(prev[entry.sourceKey] || {}),
                                                                                        identifier: entry.identifier,
                                                                                        stamp_style: e.target.value
                                                                                    }
                                                                                }))}
                                                                            >
                                                                                <option value="auto">Match identifier</option>
                                                                                <option value="plaintiff">Plaintiff's</option>
                                                                                <option value="defendant">Defendant's</option>
                                                                                <option value="neutral">Exhibit</option>
                                                                            </select>
                                                                        </label>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </details>
                                                    )}

                                                    {exhibitStampResolution.processable.length > 0 && exhibitStampResolution.unresolved.length === 0 && (
                                                        <div className="exhibit-resolution-ready">
                                                            <CloudCheck size={16} />
                                                            All convertible files have unique exhibit identifiers.
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {isExhibitStampMode && (
                                                <div className="config-group exhibit-page-label-config">
                                                    <Checkbox
                                                        label="Add a Bates-style label to every page"
                                                        value={batesExhibitPageLabels.enabled}
                                                        onChange={() => setBatesExhibitPageLabels(prev => ({
                                                            ...prev,
                                                            enabled: !prev.enabled
                                                        }))}
                                                    />
                                                    <small className="config-help">
                                                        Each label uses that document&apos;s resolved exhibit identifier. Page numbering restarts at 1 for every exhibit; the exhibit stamp remains on page one.
                                                    </small>

                                                    {batesExhibitPageLabels.enabled && (
                                                        <>
                                                            <div className="exhibit-page-label-controls">
                                                                <label>
                                                                    <span className="subtext">Digit padding</span>
                                                                    <select
                                                                        className="form-control"
                                                                        value={batesExhibitPageLabels.padding}
                                                                        onChange={(e) => setBatesExhibitPageLabels(prev => ({
                                                                            ...prev,
                                                                            padding: parseInt(e.target.value)
                                                                        }))}
                                                                    >
                                                                        {Array.from({ length: 12 }, (_, index) => index + 1).map(value => (
                                                                            <option value={value} key={value}>{value} digit{value === 1 ? '' : 's'}</option>
                                                                        ))}
                                                                    </select>
                                                                </label>
                                                                <label>
                                                                    <span className="subtext">Separator</span>
                                                                    <select
                                                                        className="form-control"
                                                                        value={batesExhibitPageLabels.separator}
                                                                        onChange={(e) => setBatesExhibitPageLabels(prev => ({
                                                                            ...prev,
                                                                            separator: e.target.value
                                                                        }))}
                                                                    >
                                                                        <option value="-">Dash (-)</option>
                                                                        <option value=".">Period (.)</option>
                                                                    </select>
                                                                </label>
                                                                <label>
                                                                    <span className="subtext">Page-label position</span>
                                                                    <select
                                                                        className="form-control"
                                                                        value={batesExhibitPageLabels.position}
                                                                        onChange={(e) => setBatesExhibitPageLabels(prev => ({
                                                                            ...prev,
                                                                            position: e.target.value
                                                                        }))}
                                                                    >
                                                                        {BATES_FIXED_POSITION_OPTIONS.map(([value, label]) => (
                                                                            <option value={value} key={value}>{label}</option>
                                                                        ))}
                                                                    </select>
                                                                </label>
                                                            </div>

                                                            <div className="exhibit-page-label-preview">
                                                                <div>
                                                                    <strong>Page-label preview</strong>
                                                                    {exhibitPageLabelPlan.firstEntry && (
                                                                        <span className="small-text">{exhibitPageLabelPlan.firstEntry.file.name}</span>
                                                                    )}
                                                                </div>
                                                                {exhibitPageLabelPlan.firstLabel ? (
                                                                    <div className="exhibit-page-label-examples">
                                                                        <code>{exhibitPageLabelPlan.firstLabel}</code>
                                                                        <span>then</span>
                                                                        <code>{exhibitPageLabelPlan.secondLabel}</code>
                                                                        <span>…</span>
                                                                    </div>
                                                                ) : (
                                                                    <span className="small-text" style={{ color: 'var(--subtext-color)' }}>Resolve an exhibit identifier to preview its page labels.</span>
                                                                )}
                                                                <span className="small-text" style={{ color: 'var(--subtext-color)' }}>
                                                                    {exhibitPageLabelPlan.documentCount} document{exhibitPageLabelPlan.documentCount === 1 ? '' : 's'} will receive page labels. Exact page counts and ranges are recorded after conversion and stamping.
                                                                </span>
                                                            </div>

                                                            {batesSettings.position === 'auto' && (
                                                                <small className="config-help">
                                                                    Automatic exhibit-stamp placement inspects the labeled first page and avoids the page-label area.
                                                                </small>
                                                            )}
                                                            {exhibitPageLabelPlan.errors.length > 0 && (
                                                                <div className="exhibit-page-label-errors" role="alert">
                                                                    {exhibitPageLabelPlan.errors.map(error => <span key={error}>{error}</span>)}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                            
                                            {isExhibitStampMode ? (
                                                <div className="config-group exhibit-output-naming">
                                                    <label className="subtext">Output file names</label>
                                                    <select
                                                        className="form-control"
                                                        value={batesSettings.exhibitOutputNameMode}
                                                        onChange={(e) => setBatesSettings(prev => ({
                                                            ...prev,
                                                            exhibitOutputNameMode: e.target.value
                                                        }))}
                                                    >
                                                        <option value="identifier">Exhibit identifier (PTX0001.pdf)</option>
                                                        <option value="source">Keep source file names</option>
                                                    </select>
                                                    <span className="small-text" style={{ color: 'var(--subtext-color)' }}>
                                                        Identifier naming uses each resolved exhibit number for the stamped output. It does not rename the source file. Converted non-PDF sources are saved as PDFs.
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="config-group config-checkbox">
                                                    <Checkbox
                                                        label="Keep original output file names"
                                                        value={batesSettings.replaceOriginals}
                                                        onChange={() => setBatesSettings(prev => ({
                                                            ...prev,
                                                            replaceOriginals: !prev.replaceOriginals
                                                        }))}
                                                    />
                                                    <small className="config-help">
                                                        When enabled, individual stamped outputs keep the source file name instead of using the Bates number. Existing targets follow the output-conflict choice.
                                                    </small>
                                                    <small className="config-help">
                                                        Non-PDF sources still output as PDFs, so a file like "Letter.docx" becomes "Letter.pdf".
                                                    </small>
                                                </div>
                                            )}

                                            {!isExhibitStampMode && <div className="config-group config-checkbox">
                                                <Checkbox
                                                    label="Stamp with red text"
                                                    value={batesSettings.stampRedText}
                                                    onChange={() => setBatesSettings(prev => ({
                                                        ...prev,
                                                        stampRedText: !prev.stampRedText
                                                    }))}
                                                />
                                                <small className="config-help">
                                                    Uses red Bates text on each stamped page.
                                                </small>
                                            </div>}

                                            {!isExhibitStampMode && <div className="config-group config-checkbox">
                                                <Checkbox
                                                    label="Shrink pages to create a clean stamp margin"
                                                    value={batesSettings.shrinkToMargin}
                                                    onChange={() => setBatesSettings(prev => ({
                                                        ...prev,
                                                        shrinkToMargin: !prev.shrinkToMargin
                                                    }))}
                                                />
                                                <small className="config-help">
                                                    Scales each page inward slightly before stamping so existing footer or header marks are less likely to overlap the Bates label.
                                                </small>
                                            </div>}
                                            
                                            <div className="bates-preview">
                                                <strong>Preview</strong>
                                                {isExhibitStampMode ? (
                                                    <>
                                                        <div className={`exhibit-stamp-preview exhibit-stamp-${resolvedExhibitStampStyle}`}>
                                                            {resolvedExhibitStampStyle === 'neutral' ? (
                                                                <span className="exhibit-stamp-heading exhibit-stamp-heading-neutral">EXHIBIT</span>
                                                            ) : (
                                                                <>
                                                                    <span className="exhibit-stamp-heading">{exhibitStampStyleLabel(resolvedExhibitStampStyle)}</span>
                                                                    <span className="exhibit-stamp-subheading">TRIAL EXHIBIT</span>
                                                                </>
                                                            )}
                                                            <span className="exhibit-stamp-number">
                                                                {firstExhibitStampPreview?.valid ? firstExhibitStampPreview.value : 'NO NUMBER'}
                                                            </span>
                                                        </div>
                                                        <div className={`exhibit-filename-status ${firstExhibitStampPreview?.valid ? 'is-valid' : 'is-invalid'}`}>
                                                            {firstExhibitStampPreview?.valid
                                                                ? `${firstExhibitStampEntry?.file?.name} will be stamped ${firstExhibitStampPreview.value}.`
                                                                : (firstExhibitStampPreview?.reason || 'Select a file with an exhibit identifier in its name.')}
                                                        </div>
                                                        {invalidExhibitStampFiles.length > 0 && (
                                                            <div className="exhibit-invalid-files">
                                                                <strong>{invalidExhibitStampFiles.length} file{invalidExhibitStampFiles.length === 1 ? '' : 's'} need a clearer exhibit number:</strong>
                                                                {invalidExhibitStampFiles.map(({ file, reason, sourceKey }) => (
                                                                    <span key={sourceKey}>{file.name}: {reason}</span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <span className="preview-text">
                                                        {batesSettings.pageLabelMode === 'document'
                                                            ? (
                                                                `${batesSettings.docLabelSource === 'bates'
                                                                    ? `${batesSettings.prefix}${String(batesSettings.startNumber).padStart(batesSettings.padding, '0')}${batesSettings.suffix}`
                                                                    : 'Document'} Page 1 of 10`
                                                            )
                                                            : (
                                                                `${batesSettings.prefix}${String(batesSettings.startNumber).padStart(batesSettings.padding, '0')}${batesSettings.suffix}`
                                                            )
                                                        }
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                        </div>
                    </Modal>
                )}

                <PdfConversionOptionsModal
                    file={pdfConversionRequest?.file || null}
                    initialOcr={pdfConversionRequest?.initialOcr || false}
                    onClose={() => setPdfConversionRequest(null)}
                    onConfirm={executePdfConversion}
                />

                {/* File Editor Component */}
                <FileEditor
                    file={fileToEdit}
                    isOpen={showFileEditor}
                    onClose={() => {
                        setShowFileEditor(false);
                        setFileToEdit(null);
                    }}
                    onSave={handleFileSave}
                    case_id={case_id}
                    user_id={user_id}
                />
                
                {/* Download Options Modal */}
                {showDownloadModal && (
                    <Modal
                        title="Download Options"
                        instructions="Choose how you'd like to download the selected files."
                        onClose={() => setShowDownloadModal(false)}
                        footer={
                            <div className='modal-footer-actions'>
                                <button onClick={executeDownload} className="action">Download</button>
                                <button onClick={() => setShowDownloadModal(false)} className="action alt">Cancel</button>
                            </div>
                        }
                    >
                        <div className='modal-content-wrapper'>
                            <div className='form-group full'>
                                <label className="subtext">Download Method</label>
                                <div className='radio-group'>
                                    <div className='radio-wrapper'>
                                        <div className='radio-option'>
                                            <input
                                                type="radio"
                                                value="zip"
                                                checked={downloadType === 'zip'}
                                                onChange={(e) => setDownloadType(e.target.value)}
                                            />
                                            <span onClick={() => setDownloadType('zip')}>Zip Selected Files</span>
                                        </div>
                                        <small className="subtext">Download all selected files in a single ZIP archive</small>
                                    </div>
                                    <div className='radio-wrapper'>
                                        <div className='radio-option'>
                                            <input
                                                type="radio"
                                                value="individual"
                                                checked={downloadType === 'individual'}
                                                onChange={(e) => setDownloadType(e.target.value)}
                                            />
                                            <span onClick={() => setDownloadType('individual')}>Download Files Individually</span>
                                        </div>
                                        <small className="subtext">Download each file separately (browser will prompt for each)</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Modal>
                )}

                {showMoveFolderPicker && (
                    <MoveFolderPicker
                        isOpen={showMoveFolderPicker}
                        onClose={() => setShowMoveFolderPicker(false)}
                        onSelect={(path) => {
                            ensureFolderOption(path);
                            setMoveTargetFolder(path);
                            setShowMoveFolderPicker(false);
                        }}
                        fetchFolderContents={fetchFolderContents}
                        caseName={caseName}
                        currentFolderPath={getCurrentFolderPathForPicker()}
                        initialPath={movePickerInitialPath || getCurrentFolderPathForPicker()}
                        initialSelection={movePickerInitialPath || getCurrentFolderPathForPicker()}
                    />
                )}
            </div>
        </div>
    );
};
